import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  List,
  ListItemText,
  ListItemButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tab,
  Tabs,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
  Checkbox,
  ImageList,
  ImageListItem,
  Link,
  Popper,
  Fade,
  Collapse,
} from '@mui/material';
import { prepareFileForSubmission } from '../utils/fileHandler';
import {
  Add as AddIcon,
  Save as SaveIcon,
  PlayArrow as PlayIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
  FolderOpen as FolderOpenIcon,
  LibraryBooks as TemplateIcon,
  MenuBook as MenuBookIcon,
  Code as CodeIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  TouchApp as TouchIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ShowChart as ChartIcon,
} from '@mui/icons-material';
import ReactFlow, {
  Node,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  ConnectionMode,
  Panel,
  BackgroundVariant,
  NodeProps,
  Handle,
  Position,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useViewMode } from '../contexts/ViewModeContext';
import { hasVideoModel, describeRunModel } from '../config/modelConfig';
import { ModelSelector } from '../components/ModelSelector';
import { SavedNextSteps } from '../components/common/SavedNextSteps';
import { TemperatureSlider } from '../components/TemperatureSlider';
import { PluginUsageInfo, isLlmBatchPlugin } from '../components/PluginUsageInfo';
import { BatchJobSettings, parseInputTemplate } from '../components/BatchJobSettings';
import { StepInputFields } from '../components/StepInputFields';
import { SchemaView } from '../components/schema/SchemaView';
import { ObjectSchemaView } from '../components/schema/ObjectSchemaView';
import { objectToJsonSchema } from '../utils/jsonSchema';
import { GroundingMetadataPanel } from '../components/GroundingMetadataPanel';
import { BatchStatusPanel, isBatchStatusOutput } from '../components/BatchStatusPanel';
import { friendlyDownloadLabel, friendlyDownloadPath, friendlySaveAsName, urlBasename } from '../components/batchDownloadNames';
import { RuntimeUploadField, normalizeRuntimeUploadFields, acceptIncludesImages, isTextUploadFile } from '../components/RuntimeUploadField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import TuneIcon from '@mui/icons-material/Tune';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import SettingsIcon from '@mui/icons-material/Settings';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import WebIcon from '@mui/icons-material/Web';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import JSZip from 'jszip';
import { ChainWizard } from '../components/ChainWizard';
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import { ChainVersionHistory } from '../components/ChainVersionHistory';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { ReliabilityDialog } from '../components/reliability/ReliabilityDialog';
import { StepReliability } from '../components/reliability/StepReliability';
import { ReliabilityDescription, reliabilityApi } from '../services/reliabilityApi';
import { ChainListRow, performanceApi } from '../services/performanceApi';
import { WarningChips } from '../components/performance/common';
import { LearningAttentionBadge, SelfImprovedChip, useLearningAttention, useLearningStatus } from '../components/learning/common';
import { learningPath } from '../services/learningApi';
import { useTestsAccess } from '../services/testsApi';
import {
  firstMatchingNodeId,
  formatMatchWhere,
  isRunSearchActive,
  nodeMatchesQuery,
  splitHighlight,
  summarizeInput,
  valueContains,
  type RunMatchPreview,
} from '../utils/chainRunSearch';

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  if (ms < 3_600_000) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(ms / 3_600_000);
  const rm = Math.round((ms % 3_600_000) / 60_000);
  return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
};

type MediaKind = 'image' | 'video' | 'audio' | 'pdf' | 'link';
type MediaItem = {
  kind: MediaKind;
  url?: string;
  gcsUrl?: string;
  label?: string;
  stepName?: string;
};

const isHttpUrl = (v: unknown): v is string => typeof v === 'string' && /^https?:\/\//i.test(v);
const isGcsUrl = (v: unknown): v is string => typeof v === 'string' && /^gs:\/\//i.test(v);

const gcsToHttp = (gcsUrl: string): string | null => {
  const m = gcsUrl.match(/^gs:\/\/([^/]+)\/(.+)$/);
  return m ? `https://storage.googleapis.com/${m[1]}/${m[2]}` : null;
};

const classifyUrl = (url: string): MediaKind => {
  const lower = url.toLowerCase();
  if (lower.endsWith('.pdf') || lower.includes('application/pdf')) return 'pdf';
  if (lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.includes('/generated_videos/')) return 'video';
  if (lower.endsWith('.mp3') || lower.endsWith('.wav') || lower.endsWith('.m4a') || lower.includes('/audio')) return 'audio';
  if (lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.webp') || lower.includes('/image')) return 'image';
  return 'link';
};

const extractMediaItems = (output: any): MediaItem[] => {
  const items: MediaItem[] = [];
  const seen = new Set<string>();

  const normalize = (url: string) => url.replace(/[?#].*$/, '').replace(/\/+$/, '');

  const push = (item: MediaItem) => {
    const key = normalize(item.url || item.gcsUrl || '');
    if (!key || seen.has(key)) return;
    seen.add(key);
    if (item.gcsUrl) seen.add(item.gcsUrl);
    items.push(item);
  };

  const walk = (value: any, path: string) => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach((v, idx) => walk(v, `${path}[${idx}]`));
      return;
    }
    if (typeof value !== 'object') return;

    const obj = value as Record<string, any>;

    const httpUrl = obj.url || obj.image_url || obj.video_url || obj.html_url || obj.public_url;
    const gcsUrl = obj.gcs_url;
    const label = (suffix: string) => path ? `${path}.${suffix}` : suffix;
    const givenName = typeof obj.filename === 'string' ? obj.filename.trim() : '';
    const namedUrl = isHttpUrl(obj.download_url) ? obj.download_url : isHttpUrl(httpUrl) ? httpUrl : null;

    if (givenName && namedUrl) {
      push({ kind: classifyUrl(namedUrl), url: namedUrl, label: givenName });
    } else if (isHttpUrl(httpUrl)) {
      push({ kind: classifyUrl(httpUrl), url: httpUrl, gcsUrl: isGcsUrl(gcsUrl) ? gcsUrl : undefined, label: label('url') });
    } else if (isGcsUrl(gcsUrl)) {
      const derived = gcsToHttp(gcsUrl);
      if (derived) {
        push({ kind: classifyUrl(derived), url: derived, gcsUrl, label: label('gcs_url') });
      } else {
        push({ kind: 'link', gcsUrl, label: label('gcs_url') });
      }
    }

    for (const [k, v] of Object.entries(obj)) {
      const key = String(k);
      if (['url', 'image_url', 'video_url', 'html_url', 'public_url', 'gcs_url', 'download_url'].includes(key)) continue;
      if ((key.endsWith('_url') || key.endsWith('_public_url')) && isHttpUrl(v)) {
        push({ kind: classifyUrl(v), url: v, label: givenName || label(key) });
      }
    }

    for (const [k, v] of Object.entries(obj)) {
      walk(v, path ? `${path}.${String(k)}` : String(k));
    }
  };

  walk(output, '');
  return items;
};

type TokenGetter = () => Promise<string | undefined>;

const fetchForDownload = async (url: string, getToken?: TokenGetter): Promise<Response> => {
  const isSameOrigin = url.startsWith(window.location.origin) || url.startsWith('/');
  if (!isSameOrigin && getToken) {
    const token = await getToken();
    if (token) {
      const proxyUrl = `/api/v2/chains/download-proxy?url=${encodeURIComponent(url)}`;
      return fetch(proxyUrl, { headers: { Authorization: `Bearer ${token}` } });
    }
  }
  return fetch(url);
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(a.href);
  document.body.removeChild(a);
};

const downloadFileFromUrl = async (url: string, filename: string, getToken?: TokenGetter) => {
  try {
    const res = await fetchForDownload(url, getToken);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    triggerBlobDownload(blob, filename);
  } catch {
    window.open(url, '_blank');
  }
};

const _sanitizeFilename = (s: string) => s.replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '').slice(0, 80);

const _looksLikeGeneratedBlob = (name: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(name) || /^[0-9a-f]{32,}\./i.test(name);

const _uniquifyZipPath = (name: string, used: Set<string>): string => {
  if (!used.has(name)) {
    used.add(name);
    return name;
  }
  const slash = name.lastIndexOf('/');
  const dot = name.lastIndexOf('.');
  const extStart = dot > slash ? dot : name.length;
  const base = name.slice(0, extStart);
  const ext = name.slice(extStart);
  let n = 2;
  let candidate = `${base}_${n}${ext}`;
  while (used.has(candidate)) {
    n += 1;
    candidate = `${base}_${n}${ext}`;
  }
  used.add(candidate);
  return candidate;
};

const mediaItemDownloadPath = (item: MediaItem, fallbackIndex: number, stepCounters: Record<string, number>): string => {
  const fromLabel = item.label ? friendlyDownloadPath(item.label) : null;
  const fromUrl = item.url ? friendlyDownloadPath(item.url) : null;
  if (fromLabel || fromUrl) return (fromLabel || fromUrl)!;

  const urlFilename = item.url ? urlBasename(item.url) : '';
  const urlExt = urlFilename.includes('.') ? urlFilename.split('.').pop()! : '';
  const ext = urlExt || (item.kind === 'image' ? 'png' : item.kind === 'video' ? 'mp4' : item.kind === 'audio' ? 'mp3' : item.kind === 'pdf' ? 'pdf' : 'bin');

  const stepPrefix = item.stepName ? _sanitizeFilename(item.stepName) : '';
  const stepKey = stepPrefix || '_global';
  stepCounters[stepKey] = (stepCounters[stepKey] || 0) + 1;
  const num = stepCounters[stepKey];

  if (urlFilename && urlFilename.includes('.') && !_looksLikeGeneratedBlob(urlFilename)) {
    return stepPrefix ? `${stepPrefix}/${urlFilename}` : urlFilename;
  }

  const labelPart = item.label ? _sanitizeFilename(item.label) : '';
  if (stepPrefix && labelPart) return `${stepPrefix}_${labelPart}_${num}.${ext}`;
  if (stepPrefix) return `${stepPrefix}_${item.kind}_${num}.${ext}`;
  if (labelPart) return `${labelPart}_${num}.${ext}`;
  return `${item.kind}_${fallbackIndex}.${ext}`;
};

const zipDownloadName = (zipName: string): string => {
  let s = _sanitizeFilename(zipName) || 'chain';
  if (!/_outputs$/i.test(s) && !/_results$/i.test(s)) s = `${s}_outputs`;
  return `${s}.zip`;
};

const _zipSortKey = (item: MediaItem): string => {
  const mapped = friendlyDownloadPath(item.label || '') || friendlyDownloadPath(item.url || '') || '';
  if (mapped.startsWith('00_')) return `0-${mapped}`;
  if (mapped.startsWith('01_')) return `1-${mapped}`;
  if (mapped.startsWith('debug/')) return `3-${mapped}`;
  return `2-${item.stepName || ''}-${item.url || ''}`;
};

const downloadAllMediaAsZip = async (items: MediaItem[], zipName: string, getToken?: TokenGetter) => {
  const zip = new JSZip();
  const downloadable = items.filter(i => i.url).slice().sort((a, b) => _zipSortKey(a).localeCompare(_zipSortKey(b)));
  const stepCounters: Record<string, number> = {};
  const usedNames = new Set<string>();
  let idx = 0;
  for (const item of downloadable) {
    try {
      const res = await fetchForDownload(item.url!, getToken);
      if (!res.ok) continue;
      const blob = await res.blob();
      const filename = _uniquifyZipPath(mediaItemDownloadPath(item, idx + 1, stepCounters), usedNames);
      zip.file(filename, blob);
      idx++;
    } catch { /* skip failed downloads */ }
  }
  if (idx === 0) return;
  const content = await zip.generateAsync({ type: 'blob' });
  triggerBlobDownload(content, zipDownloadName(zipName));
};

const MediaGallery: React.FC<{ items: MediaItem[]; showDownloadAll?: boolean; zipName?: string; getToken?: TokenGetter }> = ({ items, showDownloadAll = true, zipName = 'chain', getToken }) => {
  const images = items.filter(i => i.kind === 'image' && i.url);
  const videos = items.filter(i => i.kind === 'video' && i.url);
  const audios = items.filter(i => i.kind === 'audio' && i.url);
  const pdfs = items.filter(i => i.kind === 'pdf' && i.url);
  const links = items.filter(i => i.kind === 'link' && (i.url || i.gcsUrl));
  const downloadable = items.filter(i => i.url);
  const [zipping, setZipping] = React.useState(false);
  const [busy, setBusy] = React.useState<Set<string>>(new Set());

  const dl = async (url: string, filename: string) => {
    if (busy.has(url)) return;
    setBusy(prev => new Set(prev).add(url));
    try { await downloadFileFromUrl(url, filename, getToken); }
    finally { setBusy(prev => { const n = new Set(prev); n.delete(url); return n; }); }
  };
  const isBusy = (url: string) => busy.has(url);
  const anyBusy = zipping || busy.size > 0;

  return (
    <Box sx={{ mb: 2 }}>
      {showDownloadAll && downloadable.length > 1 && (
        <Box sx={{ mb: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button variant="outlined" size="small" startIcon={zipping ? <CircularProgress size={14} /> : <DownloadIcon />}
            disabled={anyBusy}
            onClick={async () => { setZipping(true); await downloadAllMediaAsZip(items, zipName, getToken).finally(() => setZipping(false)); }}>
            {zipping ? 'Zipping...' : `Download All (${downloadable.length} files)`}
          </Button>
          <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
            Files are stored temporarily &mdash; download to keep them
          </Typography>
        </Box>
      )}

      {(images.length > 0) && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Images</Typography>
          <ImageList cols={3} gap={8} sx={{ m: 0 }}>
            {images.map((img, idx) => (
              <ImageListItem key={`${img.url}-${idx}`} sx={{ position: 'relative' }}>
                <Box
                  component="img"
                  src={img.url!}
                  alt={img.label || 'image'}
                  sx={{ width: '100%', borderRadius: 1, cursor: 'pointer' }}
                  onClick={() => window.open(img.url!, '_blank')}
                />
                <IconButton size="small" disabled={isBusy(img.url!) || zipping}
                  sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.85)', boxShadow: 1, '&:hover': { bgcolor: 'white' } }}
                  onClick={() => {
                    const ext = img.url!.split('.').pop()?.split('?')[0] || 'png';
                    const prefix = img.stepName ? _sanitizeFilename(img.stepName) : '';
                    const mapped = friendlySaveAsName(img.label || img.url || '');
                    const name = mapped !== 'download' && mapped.includes('.')
                      ? mapped
                      : (prefix ? `${prefix}_image_${idx + 1}.${ext}` : `image_${idx + 1}.${ext}`);
                    dl(img.url!, name);
                  }}>
                  {isBusy(img.url!) ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
                </IconButton>
              </ImageListItem>
            ))}
          </ImageList>
        </Box>
      )}

      {(videos.length > 0) && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Videos</Typography>
          {videos.map((v, idx) => (
            <Box key={`${v.url}-${idx}`} sx={{ mb: 2 }}>
              <Box sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: 'black' }}>
                <video controls style={{ width: '100%', maxHeight: 420 }} src={v.url!} />
              </Box>
              <Button variant="outlined" size="small" disabled={isBusy(v.url!) || zipping}
                startIcon={isBusy(v.url!) ? <CircularProgress size={14} /> : <DownloadIcon />} sx={{ mt: 0.5 }}
                onClick={() => {
                  const prefix = v.stepName ? _sanitizeFilename(v.stepName) : '';
                  dl(v.url!, prefix ? `${prefix}_video_${idx + 1}.mp4` : `video_${idx + 1}.mp4`);
                }}>
                {isBusy(v.url!) ? 'Downloading...' : 'Download'}
              </Button>
            </Box>
          ))}
        </Box>
      )}

      {(audios.length > 0) && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Audio</Typography>
          {audios.map((a, idx) => (
            <Box key={`${a.url}-${idx}`} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <audio controls style={{ flexGrow: 1 }} src={a.url!} />
              <IconButton size="small" disabled={isBusy(a.url!) || zipping}
                onClick={() => {
                  const prefix = a.stepName ? _sanitizeFilename(a.stepName) : '';
                  dl(a.url!, prefix ? `${prefix}_audio_${idx + 1}.mp3` : `audio_${idx + 1}.mp3`);
                }}>
                {isBusy(a.url!) ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {(pdfs.length > 0) && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>PDF</Typography>
          {pdfs.map((p, idx) => (
            <Box key={`${p.url}-${idx}`} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button variant="contained" size="small" href={p.url!} target="_blank" rel="noopener noreferrer">
                  Open PDF
                </Button>
                <Button variant="outlined" size="small" disabled={isBusy(p.url!) || zipping}
                  startIcon={isBusy(p.url!) ? <CircularProgress size={14} /> : <DownloadIcon />}
                  onClick={() => {
                    const prefix = p.stepName ? _sanitizeFilename(p.stepName) : '';
                    dl(p.url!, prefix ? `${prefix}_document_${idx + 1}.pdf` : `document_${idx + 1}.pdf`);
                  }}>
                  {isBusy(p.url!) ? 'Downloading...' : 'Download'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(p.url!);
                  }}
                >
                  Copy URL
                </Button>
              </Box>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                <iframe title={`pdf-${idx}`} src={p.url!} style={{ width: '100%', height: 420, border: 0 }} />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {(links.length > 0) && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2" gutterBottom>Links</Typography>
          {links.map((l, idx) => (
            <Box key={`${l.url || l.gcsUrl}-${idx}`} sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              {l.url && (
                <>
                  <Link href={l.url} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ flexGrow: 1 }}>
                    {friendlyDownloadLabel(l.label || l.url) || l.label || l.url}
                  </Link>
                  <IconButton size="small" disabled={isBusy(l.url!) || zipping}
                    onClick={() => dl(l.url!, friendlySaveAsName(l.label || l.url || `file_${idx + 1}`))}>
                    {isBusy(l.url!) ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
                  </IconButton>
                </>
              )}
              {!l.url && l.gcsUrl && (
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {l.label || 'gcs_url'}: {l.gcsUrl}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

interface AIFunction {
  id: string;
  name: string;
  description: string;
  input_types: string[];
  response_format: any;
  customer_id: string;
  is_public: boolean;
  allowed_models?: string[];
}

interface Chain {
  id: string;
  name: string;
  description: string;
  graph_data: {
    nodes: any[];
    edges: any[];
  };
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ChainExecution {
  id: string;
  chain_id: string;
  status: string;
  node_results?: any;
  error?: string;
  started_at?: string;
  completed_at?: string;
  execution_time_ms?: number;
  total_tokens?: number;
  total_cost?: number;
  total_ai_credits_used?: number;
  input_data?: {
    reference_images?: string[];
    [key: string]: any;
  };
  input_preview?: string;
  source_execution_id?: string;
  resume_from_node?: string;
  adjustment_notes?: string;
  pause_before_nodes?: string[];
  paused_at_node?: string;
  match_previews?: RunMatchPreview[];
}

const RUN_HISTORY_PAGE_SIZE = 100;

const HighlightedText: React.FC<{ text: string; query?: string }> = ({ text, query }) => {
  if (!isRunSearchActive(query)) return <>{text}</>;
  return (
    <>
      {splitHighlight(text, query || '').map((part, i) =>
        part.match ? (
          <Box
            component="mark"
            key={`${i}-${part.text.slice(0, 12)}`}
            sx={{ bgcolor: 'warning.light', color: 'inherit', px: 0.15, borderRadius: 0.5 }}
          >
            {part.text}
          </Box>
        ) : (
          <React.Fragment key={`${i}-${part.text.slice(0, 12)}`}>{part.text}</React.Fragment>
        )
      )}
    </>
  );
};

// ChainTemplate is now just a Chain loaded from the database (public chains)
// This allows templates to be managed in the database rather than hardcoded
type ChainTemplate = Chain;

// Canvas floating toolbar (rendered inside ReactFlow)
const CanvasToolbar: React.FC<{ selectedNodeId: string | null; onDeleteNode: () => void; onAutoLayout: () => void }> = ({ selectedNodeId, onDeleteNode, onAutoLayout }) => {
  const { fitView, zoomIn, zoomOut } = useReactFlow();
  return (
    <Panel position="top-right">
      <Paper elevation={2} sx={{ display: 'flex', gap: 0.25, p: 0.5, borderRadius: 2, bgcolor: 'background.paper' }}>
        <Tooltip title="Fit view" placement="bottom">
          <IconButton size="small" onClick={() => fitView({ padding: 0.15, duration: 300 })}><FitScreenIcon sx={{ fontSize: 18 }} /></IconButton>
        </Tooltip>
        <Tooltip title="Zoom in" placement="bottom">
          <IconButton size="small" onClick={() => zoomIn({ duration: 200 })}><ZoomInIcon sx={{ fontSize: 18 }} /></IconButton>
        </Tooltip>
        <Tooltip title="Zoom out" placement="bottom">
          <IconButton size="small" onClick={() => zoomOut({ duration: 200 })}><ZoomOutIcon sx={{ fontSize: 18 }} /></IconButton>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
        <Tooltip title="Auto-layout nodes" placement="bottom">
          <IconButton size="small" onClick={onAutoLayout}><AutoFixHighIcon sx={{ fontSize: 18 }} /></IconButton>
        </Tooltip>
        <Tooltip title={selectedNodeId ? 'Delete selected node' : 'Select a node first'} placement="bottom">
          <span>
            <IconButton size="small" onClick={onDeleteNode} disabled={!selectedNodeId} color="error"><DeleteIcon sx={{ fontSize: 18 }} /></IconButton>
          </span>
        </Tooltip>
      </Paper>
    </Panel>
  );
};

// Knowledge Hub category names for the canvas nodes (they render outside the page component).
const KnowledgeCategoriesContext = React.createContext<Array<{ id: string; name: string }>>([]);

// Custom node component for function nodes
const FunctionNode: React.FC<NodeProps> = ({ data, selected }) => {
  const knowledgeCategoryList = React.useContext(KnowledgeCategoriesContext);
  const knowledgeIds: string[] = Array.isArray(data.config?.knowledgeCategories) ? data.config.knowledgeCategories : [];
  const inputTypes = data.inputTypes || [];
  const hasArrayMapping = data.config?.mapOverArray || false;
  const isPlugin = data.isPlugin || data.functionId?.startsWith('plugin:');
  const webSearch = data.config?.enableGoogleSearch || false;
  const modelOverride = data.config?.modelOverride;
  const allowedModels: string[] = data.allowed_models || [];
  const tplModel = parseInputTemplate(data.config?.inputTemplate ?? data.inputTemplate).model;
  const displayModel = modelOverride || tplModel || allowedModels[0] || '';
  const usageInfo: any = data.usageInfo || null;

  const accentColor = isPlugin ? '#7b1fa2' : '#1976d2';

  const shortModel = (m: string) => {
    if (!m) return '';
    return m
      .replace(/-preview$/, '')
      .replace(/-latest$/, '')
      .replace(/@latest$/, '')
      .replace(/^models\//, '');
  };

  // For plugin nodes, build a one-line "needs / returns" hint to surface
  // the contract directly on the canvas card, not just in the side panel.
  const requiredInputNames: string[] = isPlugin && usageInfo?.inputs
    ? usageInfo.inputs.filter((i: any) => i.required).map((i: any) => i.name)
    : [];
  const optionalInputCount: number = isPlugin && usageInfo?.inputs
    ? usageInfo.inputs.filter((i: any) => !i.required).length
    : 0;
  const returnsLabel: string | null = isPlugin && usageInfo?.output
    ? (usageInfo.output.object_name || usageInfo.output.type || 'json')
    : null;

  // Tooltip content shows the full contract on hover (without needing to click)
  const tooltipContent = isPlugin && usageInfo ? (
    <Box sx={{ p: 0.5, maxWidth: 360 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, fontSize: '0.75rem' }}>
        {data.functionName}
      </Typography>
      {data.functionDescription && (
        <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontSize: '0.7rem', lineHeight: 1.4, color: 'rgba(255,255,255,0.85)' }}>
          {data.functionDescription}
        </Typography>
      )}
      {usageInfo.summary && (
        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: '0.65rem', fontStyle: 'italic', color: 'rgba(255,255,255,0.7)' }}>
          {usageInfo.summary}
        </Typography>
      )}
      {(usageInfo.inputs || []).length > 0 && (
        <Box sx={{ mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: '0.65rem', color: '#90caf9' }}>
            INPUTS
          </Typography>
          {(usageInfo.inputs || []).slice(0, 8).map((f: any) => (
            <Typography
              key={f.name}
              variant="caption"
              sx={{ display: 'block', fontSize: '0.65rem', fontFamily: 'monospace', lineHeight: 1.5 }}
            >
              <span style={{ color: '#fff', fontWeight: 600 }}>{f.name}</span>
              <span style={{ color: '#aaa' }}> ({f.type})</span>
              {f.required ? <span style={{ color: '#ef9a9a', fontWeight: 700 }}> *required</span> : <span style={{ color: '#aaa' }}> (optional)</span>}
            </Typography>
          ))}
          {(usageInfo.inputs || []).length > 8 && (
            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#bbb' }}>
              +{(usageInfo.inputs || []).length - 8} more
            </Typography>
          )}
        </Box>
      )}
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: '0.65rem', color: '#ce93d8' }}>
        RETURNS
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', fontFamily: 'monospace' }}>
        {returnsLabel}
        {usageInfo.output?.fields && usageInfo.output.fields.length > 0 && (
          <span style={{ color: '#aaa' }}> {`{ ${usageInfo.output.fields.slice(0, 6).join(', ')}${usageInfo.output.fields.length > 6 ? ', …' : ''} }`}</span>
        )}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mt: 0.75, fontSize: '0.6rem', color: 'rgba(255,255,255,0.6)', fontStyle: 'italic' }}>
        Click node for full details and example
      </Typography>
    </Box>
  ) : null;

  const cardContent = (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        background: 'white',
        border: selected ? `2px solid ${accentColor}` : '1px solid #e0e0e0',
        borderTop: `3px solid ${accentColor}`,
        minWidth: 280,
        maxWidth: 320,
        boxShadow: selected ? '0 4px 20px rgba(0,0,0,0.12)' : '0 2px 8px rgba(0,0,0,0.06)',
        position: 'relative',
        transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
      }}
    >
      <Handle type="target" position={Position.Left} id="input" style={{ width: 10, height: 10, background: '#888', border: '2px solid white' }} />
      <Handle type="source" position={Position.Right} id="output" style={{ width: 10, height: 10, background: accentColor, border: '2px solid white' }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem', flexGrow: 1, minWidth: 0 }} noWrap>
          {data.functionName}
        </Typography>
        <Chip
          label={isPlugin ? 'Tool' : 'AI'}
          size="small"
          sx={{
            fontSize: '0.6rem', height: 18, fontWeight: 700, flexShrink: 0,
            bgcolor: isPlugin ? '#f3e5f5' : '#e3f2fd',
            color: isPlugin ? '#7b1fa2' : '#1565c0',
          }}
        />
      </Box>

      {data.functionDescription && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, lineHeight: 1.3, fontSize: '0.7rem' }}>
          {data.functionDescription.substring(0, 70)}
          {data.functionDescription.length > 70 ? '...' : ''}
        </Typography>
      )}

      {/* Plugin contract hint (visible on the card itself, not just in tooltip) */}
      {isPlugin && (requiredInputNames.length > 0 || returnsLabel) && (
        <Box sx={{ mb: 0.75, py: 0.5, px: 0.75, borderRadius: 0.5, bgcolor: '#faf5ff', border: '1px solid #e1bee7' }}>
          {requiredInputNames.length > 0 && (
            <Typography
              variant="caption"
              display="block"
              sx={{ fontSize: '0.62rem', lineHeight: 1.4, color: '#4a148c' }}
            >
              <span style={{ fontWeight: 700 }}>Needs:</span>{' '}
              <span style={{ fontFamily: 'monospace' }}>
                {requiredInputNames.slice(0, 3).join(', ')}
                {requiredInputNames.length > 3 && ` +${requiredInputNames.length - 3}`}
              </span>
              {optionalInputCount > 0 && (
                <span style={{ color: '#7b1fa2', fontStyle: 'italic' }}>
                  {' '}(+{optionalInputCount} optional)
                </span>
              )}
            </Typography>
          )}
          {returnsLabel && (
            <Typography
              variant="caption"
              display="block"
              sx={{ fontSize: '0.62rem', lineHeight: 1.4, color: '#4a148c' }}
            >
              <span style={{ fontWeight: 700 }}>Returns:</span>{' '}
              <span style={{ fontFamily: 'monospace' }}>{returnsLabel}</span>
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {displayModel && (
          <Chip
            label={shortModel(displayModel)}
            size="small" variant="outlined"
            sx={{
              fontSize: '0.58rem', height: 18, maxWidth: 140,
              borderColor: modelOverride ? '#ed6c02' : '#bbb',
              color: modelOverride ? '#ed6c02' : '#666',
              fontWeight: modelOverride ? 700 : 400,
            }}
          />
        )}
        {!isPlugin && knowledgeIds.length > 0 && (
          <Tooltip
            arrow
            placement="bottom"
            title={
              <Box sx={{ p: 0.25, maxWidth: 300 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: '0.7rem', mb: 0.25 }}>
                  Knowledge (RAG)
                </Typography>
                {knowledgeIds.map((id) => (
                  <Typography key={id} variant="caption" sx={{ display: 'block', fontSize: '0.68rem' }}>
                    • {knowledgeCategoryList.find((c) => c.id === id)?.name || id}
                  </Typography>
                ))}
                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.68rem', mt: 0.5 }}>
                  Snippets: {data.config?.knowledgeChunks ?? 3}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.68rem', fontFamily: data.config?.knowledgeQuery ? 'monospace' : undefined, wordBreak: 'break-word' }}>
                  Query: {data.config?.knowledgeQuery || "this step's input"}
                </Typography>
              </Box>
            }
          >
            <Chip
              icon={<MenuBookIcon sx={{ fontSize: '0.75rem !important' }} />}
              label={knowledgeIds.length > 1 ? `Knowledge · ${knowledgeIds.length}` : 'Knowledge'}
              size="small"
              sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#e0f2f1', color: '#00695c', fontWeight: 700, '& .MuiChip-icon': { color: '#00695c', ml: 0.5 } }}
            />
          </Tooltip>
        )}
        {webSearch && <Chip label="Web" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }} />}
        {hasArrayMapping && <Chip label="Map" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#fff3e0', color: '#e65100', fontWeight: 700 }} />}
        {inputTypes.slice(0, 2).map((type: string) => (
          <Chip key={type} label={type} size="small" variant="outlined" sx={{ fontSize: '0.55rem', height: 16, borderColor: '#ddd', color: '#888' }} />
        ))}
        {inputTypes.length > 2 && <Chip label={`+${inputTypes.length - 2}`} size="small" variant="outlined" sx={{ fontSize: '0.55rem', height: 16 }} />}
      </Box>
    </Box>
  );

  if (tooltipContent) {
    return (
      <Tooltip
        title={tooltipContent}
        placement="right"
        arrow
        enterDelay={400}
        enterNextDelay={400}
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: 'rgba(33, 33, 33, 0.96)',
              maxWidth: 380,
              p: 1.25,
            },
          },
          arrow: { sx: { color: 'rgba(33, 33, 33, 0.96)' } },
        }}
      >
        {cardContent}
      </Tooltip>
    );
  }

  return cardContent;
};

// Knowledge (RAG) controls shared by the sidebar node panel and the Full Config dialog.
const NodeKnowledgeSettings: React.FC<{
  config: any;
  categories: Array<{ id: string; name: string }>;
  onChange: (patch: Record<string, any>) => void;
  dense?: boolean;
}> = ({ config, categories, onChange, dense }) => {
  const selected: string[] = Array.isArray(config?.knowledgeCategories) ? config.knowledgeCategories : [];
  const hasCategories = selected.length > 0;
  const fieldSx = dense ? { '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' } } : {};
  return (
    <>
      <FormControl fullWidth size="small" sx={{ mb: dense ? 1 : 2, ...fieldSx }}>
        <InputLabel>Knowledge Categories</InputLabel>
        <Select
          multiple
          value={selected}
          label="Knowledge Categories"
          MenuProps={{
            variant: 'menu',
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            PaperProps: { sx: { maxHeight: 320 } },
          }}
          onChange={(e) => {
            const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
            const patch: Record<string, any> = { knowledgeCategories: value };
            if (value.length > 0 && !hasCategories && config?.knowledgeChunks == null) patch.knowledgeChunks = 3;
            onChange(patch);
          }}
          renderValue={(vals) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(vals as string[]).map((value) => (
                <Chip
                  key={value}
                  label={categories.find(c => c.id === value)?.name || value}
                  size="small"
                  sx={dense ? { height: 20, fontSize: '0.7rem' } : undefined}
                />
              ))}
            </Box>
          )}
        >
          {categories.length === 0 ? (
            <MenuItem disabled>
              <em>No categories available - upload materials first</em>
            </MenuItem>
          ) : (
            categories.map((category) => (
              <MenuItem key={category.id} value={category.id} dense={dense}>
                <Checkbox size={dense ? 'small' : 'medium'} checked={selected.includes(category.id)} />
                <ListItemText primary={category.name} primaryTypographyProps={dense ? { fontSize: '0.8rem' } : undefined} />
              </MenuItem>
            ))
          )}
        </Select>
        {!dense && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Select knowledge categories to search for context (RAG)
          </Typography>
        )}
      </FormControl>
      {hasCategories && (
        <>
          <TextField
            label={dense ? 'Snippets' : 'Knowledge Chunks'}
            type="number"
            value={config?.knowledgeChunks ?? 3}
            onChange={(e) => {
              const n = parseInt(e.target.value);
              onChange({ knowledgeChunks: Number.isFinite(n) ? Math.min(10, Math.max(1, n)) : undefined });
            }}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 10 }}
            helperText={dense ? undefined : 'Number of relevant chunks to retrieve (1-10)'}
            sx={{ mb: dense ? 1 : 2, ...fieldSx }}
          />
          <TextField
            label={dense ? 'Search query (optional)' : 'Knowledge search query (optional)'}
            value={config?.knowledgeQuery || ''}
            onChange={(e) => onChange({ knowledgeQuery: e.target.value || undefined })}
            size="small"
            fullWidth
            multiline={dense}
            maxRows={dense ? 3 : undefined}
            placeholder="e.g. substitutes for {{item.name}}"
            helperText={dense ? "Blank = this step's input" : "What to search the knowledge base for. Supports {{input.field}}, {{node-1.field}} and {{item.field}}; defaults to this step's input."}
            sx={{ mb: dense ? 0.5 : 2, ...fieldSx }}
          />
        </>
      )}
    </>
  );
};

const nodeTypes = {
  functionNode: FunctionNode,
};

const CopyableCodeBlock: React.FC<{ content: unknown; maxHeight?: number; highlight?: string }> = ({ content, maxHeight = 400, highlight }) => {
  const [copied, setCopied] = React.useState(false);
  const text = typeof content === 'string' ? content : JSON.stringify(content, null, 2);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
        <IconButton
          onClick={handleCopy}
          size="small"
          sx={{
            position: 'absolute',
            right: 4,
            top: 4,
            zIndex: 1,
            bgcolor: 'background.paper',
            boxShadow: 1,
            '&:hover': { bgcolor: 'grey.200' },
          }}
        >
          {copied ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1, maxHeight, overflow: 'auto' }}>
        <Typography
          variant="caption"
          component="pre"
          sx={{ fontSize: '0.7rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5, m: 0 }}
        >
          <HighlightedText text={text} query={highlight} />
        </Typography>
      </Box>
    </Box>
  );
};

export const FunctionChaining: React.FC = () => {
  const { user, isPlatformAdmin, isImpersonating, getIdToken } = useAuth();
  const navigate = useNavigate();
  const { enabled: testsEnabled } = useTestsAccess(getIdToken, null);
  const { setViewMode } = useViewMode();
  const getToken: TokenGetter = useCallback(async () => user?.getIdToken(), [user]);
  const [functions, setFunctions] = useState<AIFunction[]>([]);
  const [chains, setChains] = useState<Chain[]>([]);
  const [chainsLoading, setChainsLoading] = useState(true);
  const [selectedChain, setSelectedChain] = useState<Chain | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [savedNotice, setSavedNotice] = useState<{ isNew: boolean; id?: string; name: string } | null>(null);

  useEffect(() => {
    if (!savedNotice || savedNotice.isNew) return;
    const t = setTimeout(() => setSavedNotice(null), 4000);
    return () => clearTimeout(t);
  }, [savedNotice]);
  const [chainName, setChainName] = useState('');
  const [chainDescription, setChainDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [executeDialogOpen, setExecuteDialogOpen] = useState(false);
  const [executionInput, setExecutionInput] = useState('{}');
  const [executionInputs, setExecutionInputs] = useState<Record<string, any>>({});
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, File | File[]>>({});
  const [inputMethods, setInputMethods] = useState<Record<string, 'url' | 'file'>>({});
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number; fileName: string } | null>(null);
  const [showAttachImages, setShowAttachImages] = useState(false);
  const [singleInput, setSingleInput] = useState(true);
  const [videoUploadStatus, setVideoUploadStatus] = useState<{ uploading: boolean; fileName?: string; fileSize?: string; error?: string }>({ uploading: false });
  const [videoInputMethod, setVideoInputMethod] = useState<'upload' | 'url'>('upload');
  
  // Video generation settings
  const [videoDuration, setVideoDuration] = useState(6); // 4-8 seconds for video models (default 6 for better quality)
  const [videoAspectRatio, setVideoAspectRatio] = useState('16:9');
  const [execution, setExecution] = useState<ChainExecution | null>(null);
  const [executionPolling, setExecutionPolling] = useState(false);
  const [dialogZipping, setDialogZipping] = useState(false);
  const [expandedOutputNodes, setExpandedOutputNodes] = useState<Set<string>>(new Set());
  const [, setLastCompletedNode] = useState<string | null>(null);
  const nodeOutputRefs = useRef<Record<string, HTMLElement | null>>({});
  const progressScrollRef = useRef<HTMLDivElement | null>(null);
  const stepRowRefs = useRef<Record<string, HTMLElement | null>>({});
  const [selectedResultNode, setSelectedResultNode] = useState<string | null>(null);
  const [rerunDialogOpen, setRerunDialogOpen] = useState(false);
  const [rerunNodeId, setRerunNodeId] = useState<string | null>(null);
  const [rerunNotes, setRerunNotes] = useState('');
  const [rerunIncludeOutput, setRerunIncludeOutput] = useState(false);
  const [rerunModelOverride, setRerunModelOverride] = useState<string>('');
  const [continueNotes, setContinueNotes] = useState('');
  const [leftPanelWidth, setLeftPanelWidth] = useState(300);
  const isDraggingRef = useRef(false);
  const [tabValue, setTabValue] = useState(0);
  const [codeTabValue, setCodeTabValue] = useState(0);
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);
  const [chainWizardOpen, setChainWizardOpen] = useState(false);
  const [wizardEditPrompt, setWizardEditPrompt] = useState<string | undefined>(undefined);
  const [wizardEditChainId, setWizardEditChainId] = useState<string | undefined>(undefined);
  const [wizardEditChainName, setWizardEditChainName] = useState<string | undefined>(undefined);
  const [wizardEditChainDesc, setWizardEditChainDesc] = useState<string | undefined>(undefined);
  const [wizardEditPlan, setWizardEditPlan] = useState<import('../components/ChainWizard').ChainPlan | undefined>(undefined);
  const [wizardEditTemplateSlug, setWizardEditTemplateSlug] = useState<string | undefined>(undefined);
  const [wizardSeedPrompt, setWizardSeedPrompt] = useState<string | undefined>(undefined);
  const [showAIBuilderCTA, setShowAIBuilderCTA] = useState(false);
  const aiBuilderBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const [templates, setTemplates] = useState<ChainTemplate[]>([]);
  const [hideChainFunctions, setHideChainFunctions] = useState(true);
  
  // Template tutorial tooltip state
  const [showTemplateTip, setShowTemplateTip] = useState(false);
  const [templateTipAnchor, setTemplateTipAnchor] = useState<HTMLElement | null>(null);
  const [deleteChainDialog, setDeleteChainDialog] = useState<{ open: boolean; chain: Chain | null }>({ open: false, chain: null });
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [nodeConfigOpen, setNodeConfigOpen] = useState(false);
  const [customObjects, setCustomObjects] = useState<any[]>([]);
  const [loadingNodeSchema, setLoadingNodeSchema] = useState(false);
  const [nodeInstructions, setNodeInstructions] = useState<string | null>(null);
  const [loadingNodeInstructions, setLoadingNodeInstructions] = useState(false);
  const [executionHistory, setExecutionHistory] = useState<ChainExecution[]>([]);
  const [runSearch, setRunSearch] = useState('');
  const [runSearchDebounced, setRunSearchDebounced] = useState('');
  const [runStatusFilter, setRunStatusFilter] = useState<'all' | 'completed' | 'failed' | 'running'>('all');
  const [runHistoryLoading, setRunHistoryLoading] = useState(false);
  const [runHistoryPage, setRunHistoryPage] = useState(1);
  const [runHistoryTotal, setRunHistoryTotal] = useState(0);
  const [openingRunId, setOpeningRunId] = useState<string | null>(null);
  const [inRunSearch, setInRunSearch] = useState('');
  const [showMatchingStepsOnly, setShowMatchingStepsOnly] = useState(false);
  const historyAbortRef = useRef<AbortController | null>(null);
  const [codeExamplesOpen, setCodeExamplesOpen] = useState(false);
  const [codeExampleApiKeys, setCodeExampleApiKeys] = useState<Array<{ id: number; name?: string; api_key: string; active?: boolean }>>([]);
  const [selectedCodeApiKeyId, setSelectedCodeApiKeyId] = useState<number | null>(null);
  const [codeExampleApiKeysLoading, setCodeExampleApiKeysLoading] = useState(false);
  const [codeExampleApiKeysError, setCodeExampleApiKeysError] = useState<string | null>(null);
  const [revealCodeApiKey, setRevealCodeApiKey] = useState(false);
  const [codeExampleInputs, setCodeExampleInputs] = useState<Record<string, any>>({});
  const [codeExampleTrackId, setCodeExampleTrackId] = useState<string>('');
  const [codeStatusTabValue, setCodeStatusTabValue] = useState<number>(0);
  const [sidebarOpen, setSidebarOpen] = useState(() => localStorage.getItem('chainSidebarOpen') !== 'false');
  const [sidebarTab, setSidebarTab] = useState(0);
  const [sidebarWidth, setSidebarWidth] = useState(340);
  const [chainSearch, setChainSearch] = useState('');
  const sidebarDraggingRef = useRef(false);
  const [overflowAnchorEl, setOverflowAnchorEl] = useState<null | HTMLElement>(null);
  const [reliabilityOpen, setReliabilityOpen] = useState(false);
  const [chainReliability, setChainReliability] = useState<ReliabilityDescription | null>(null);
  const selectedChainId = selectedChain?.id;
  useEffect(() => {
    setChainReliability(null);
    if (!selectedChainId) return;
    let cancelled = false;
    reliabilityApi(getIdToken)
      .get('chain', selectedChainId)
      .then((d) => !cancelled && setChainReliability(d))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectedChainId, getIdToken]);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const learningStatus = useLearningStatus();
  const learningAttention = useLearningAttention();
  const [codeCopyAlert, setCodeCopyAlert] = useState(false);
  
  // Knowledge base categories for node AI settings
  const [availableCategories, setAvailableCategories] = useState<Array<{id: string, name: string}>>([]);
  // Polling guards (prevent overlapping polls / multiple loops)
  const pollTimeoutRef = useRef<number | null>(null);
  const pollInFlightRef = useRef(false);
  const pollingExecutionIdRef = useRef<string | null>(null);
  const lastPollAtRef = useRef<number>(0);
  const pollAbortRef = useRef<AbortController | null>(null);

  useEffect(() => { localStorage.setItem('chainSidebarOpen', String(sidebarOpen)); }, [sidebarOpen]);

  // Load the user's API keys when the API Integration dialog opens, and seed
  // the runtime-input form with sensible defaults from the chain's runtime_inputs.
  useEffect(() => {
    if (!codeExamplesOpen) return;
    let cancelled = false;
    (async () => {
      try {
        setCodeExampleApiKeysLoading(true);
        setCodeExampleApiKeysError(null);
        const token = await user?.getIdToken();
        const res = await fetch('/api/v2/api-keys?page=1&page_size=100&sort_by=created_at&sort_dir=desc', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Failed to load API keys (${res.status})`);
        const data = await res.json();
        const items = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        if (cancelled) return;
        setCodeExampleApiKeys(items);
        const activeKey = items.find((k: any) => k.active !== false) || items[0];
        setSelectedCodeApiKeyId(activeKey ? activeKey.id : null);
      } catch (e: any) {
        if (!cancelled) {
          setCodeExampleApiKeysError(e?.message || 'Failed to load API keys');
          setCodeExampleApiKeys([]);
          setSelectedCodeApiKeyId(null);
        }
      } finally {
        if (!cancelled) setCodeExampleApiKeysLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [codeExamplesOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seed example inputs from the selected chain's runtime_inputs whenever the
  // dialog opens or the chain changes.
  useEffect(() => {
    if (!codeExamplesOpen) return;
    const runtimeInputs: any[] = (selectedChain as any)?.config?.runtime_inputs || [];
    const seeded: Record<string, any> = {};
    if (Array.isArray(runtimeInputs)) {
      for (const f of runtimeInputs) {
        if (!f || !f.key) continue;
        if (f.default !== undefined && f.default !== null) {
          seeded[f.key] = f.default;
        } else if (f.type === 'boolean') {
          seeded[f.key] = false;
        } else if (f.type === 'number') {
          seeded[f.key] = '';
        } else if (f.type === 'select' && Array.isArray(f.options) && f.options.length > 0) {
          seeded[f.key] = f.options[0]?.value ?? '';
        } else {
          seeded[f.key] = '';
        }
      }
    }
    setCodeExampleInputs(seeded);
    setCodeExampleTrackId('');
  }, [codeExamplesOpen, selectedChain?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-load chains when Chains tab is selected
  useEffect(() => {
    if (sidebarTab === 0 && sidebarOpen) {
      loadChains();
    }
  }, [sidebarTab, sidebarOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const [chainPerf, setChainPerf] = useState<Record<string, ChainListRow>>({});
  useEffect(() => {
    if (sidebarTab !== 0 || !sidebarOpen) return;
    let cancelled = false;
    performanceApi(getIdToken)
      .chains()
      .then((d) => !cancelled && setChainPerf(Object.fromEntries(d.chains.map((c) => [c.chain_id, c]))))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [sidebarTab, sidebarOpen, getIdToken]);

  // Auto-load execution history when Runs tab is selected or search/filter changes
  useEffect(() => {
    const t = window.setTimeout(() => {
      const next = runSearch.trim();
      setRunSearchDebounced((prev) => {
        if (prev !== next) setRunHistoryPage(1);
        return next;
      });
    }, 350);
    return () => window.clearTimeout(t);
  }, [runSearch]);

  useEffect(() => {
    setRunHistoryPage(1);
  }, [selectedChain?.id]);

  useEffect(() => {
    if (sidebarTab === 3 && sidebarOpen) {
      loadExecutionHistory(selectedChain?.id);
    }
  }, [sidebarTab, sidebarOpen, selectedChain?.id, runSearchDebounced, runStatusFilter, runHistoryPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-expand the most recently completed/failed node output and scroll to it
  useEffect(() => {
    if (!execution?.node_results) return;
    const results = execution.node_results as Record<string, any>;
    const completedIds = Object.keys(results).filter(id => results[id]?.status === 'completed' || results[id]?.status === 'failed');
    
    if (completedIds.length === 0) return;
    
    // Find the newest completed node (not already tracked)
    const newlyCompleted = completedIds.filter(id => !expandedOutputNodes.has(id));
    if (newlyCompleted.length > 0) {
      setExpandedOutputNodes(prev => {
        const next = new Set(prev);
        newlyCompleted.forEach(id => next.add(id));
        return next;
      });
      const latest = newlyCompleted[newlyCompleted.length - 1];
      setLastCompletedNode(latest);
      setSelectedResultNode(latest);
      setTimeout(() => {
        // Auto-scroll the progress stepper to keep the CURRENTLY RUNNING step in view.
        // Fallback to the most recently completed step if nothing is running yet.
        const runningId = Object.keys(results).find(id => results[id]?.status === 'running');
        const focusId = runningId || latest;
        const container = progressScrollRef.current;
        const target = stepRowRefs.current[focusId];
        if (container && target) {
          const cRect = container.getBoundingClientRect();
          const tRect = target.getBoundingClientRect();
          const offset = (tRect.top - cRect.top) + container.scrollTop - 8; // small top padding
          container.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' });
        }
        // Scroll the main results pane to the newly completed output accordion
        nodeOutputRefs.current[latest]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 200);
    }
  }, [execution?.node_results]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset expanded output nodes when a new execution starts
  useEffect(() => {
    if (execution && execution.status === 'pending') {
      setExpandedOutputNodes(new Set());
      setLastCompletedNode(null);
      nodeOutputRefs.current = {};
      stepRowRefs.current = {};
    }
  }, [execution?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the currently-running step centered in the stepper as progress advances.
  // Reacts to the identity of the running node (not just node_results changes).
  const runningNodeId = useMemo(() => {
    const results = execution?.node_results as Record<string, any> | undefined;
    if (!results) return null;
    return Object.keys(results).find(id => results[id]?.status === 'running') || null;
  }, [execution?.node_results]);

  useEffect(() => {
    if (!runningNodeId) return;
    setSelectedResultNode(runningNodeId);
    const t = setTimeout(() => {
      const container = progressScrollRef.current;
      const target = stepRowRefs.current[runningNodeId];
      if (container && target) {
        const cRect = container.getBoundingClientRect();
        const tRect = target.getBoundingClientRect();
        const offset = (tRect.top - cRect.top) + container.scrollTop - 8;
        container.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' });
      }
    }, 150);
    return () => clearTimeout(t);
  }, [runningNodeId]);

  // Load functions, chains, and categories on mount
  useEffect(() => {
    loadFunctions();
    loadChains();
    loadTemplates();
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Load knowledge base categories for RAG selection
  const loadCategories = async () => {
    try {
      const token = await user?.getIdToken();
      if (!token) return;

      const res = await fetch(`/api/v2/customers/me/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Handle various API response formats:
        // - Direct array: [...]
        // - {categories: [...]}
        // - {data: {items: [...]}} (standard API response format)
        let categories: Array<{id: string, name: string}> = [];
        if (Array.isArray(data)) {
          categories = data;
        } else if (Array.isArray(data?.categories)) {
          categories = data.categories;
        } else if (Array.isArray(data?.data?.items)) {
          categories = data.data.items;
        } else if (Array.isArray(data?.items)) {
          categories = data.items;
        }
        console.log('[CATEGORIES] Loaded categories:', categories);
        setAvailableCategories(categories);
      }
    } catch (err) {
      console.warn('Failed to load categories:', err);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollTimeoutRef.current) {
        window.clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      try {
        pollAbortRef.current?.abort();
      } catch {
        // ignore
      }
      pollAbortRef.current = null;
      pollInFlightRef.current = false;
      pollingExecutionIdRef.current = null;
    };
  }, []);
  
  // Show template tip on first visit
  useEffect(() => {
    const hasShownTip = localStorage.getItem('hasShownChainTemplateTip') === 'true';
    if (!hasShownTip && templates.length > 0) {
      const timer = setTimeout(() => {
        const menuBtn = document.querySelector('[aria-label="Manage Chains"]') as HTMLElement;
        if (menuBtn) {
          setTemplateTipAnchor(menuBtn);
          setShowTemplateTip(true);
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [templates]);

  // Show AI Chain Builder CTA on first visit
  useEffect(() => {
    if (localStorage.getItem('hasSeenChainBuilderCTA') !== 'true') {
      const timer = setTimeout(() => setShowAIBuilderCTA(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);
  
  const fetchObjectDetails = async (objectId: string) => {
    try {
      const token = await user?.getIdToken();
      const response = await fetch(`/api/v2/custom-objects/${objectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) return null;
      
      const data = await response.json();
      const obj = data?.data || data?.results || data?.item || data;
      
      // If fields not loaded, try to fetch them explicitly
      if (obj && (!obj.fields || obj.fields.length === 0)) {
        const fieldsResponse = await fetch(`/api/v2/custom-objects/${objectId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (fieldsResponse.ok) {
          const fieldsData = await fieldsResponse.json();
          const fullObj = fieldsData?.data || fieldsData;
          if (fullObj?.fields && fullObj.fields.length > 0) {
            return fullObj;
          }
        }
      }
      
      return obj;
    } catch (err) {
      console.error('Error fetching object details:', err);
      return null;
    }
  };
  
  // Fetch object by name (for plugins)
  const fetchObjectByName = async (objectName: string): Promise<any> => {
    try {
      const token = await user?.getIdToken();
      // Search for public objects by name
      const response = await fetch(`/api/v2/custom-objects?name=${encodeURIComponent(objectName)}&include_public=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) return null;
      
      const data = await response.json();
      const objects = data?.data || data?.results || data?.items || data || [];
      
      // Find the matching object
      const obj = objects.find((o: any) => o.name === objectName || o.display_name === objectName);
      if (!obj) return null;
      
      // Get full details with fields
      return await fetchObjectDetails(obj.id);
    } catch (err) {
      console.error('Error fetching object by name:', err);
      return null;
    }
  };

  // Load a specific object with its fields and references
  const loadObjectWithReferences = async (objectId: string, loadedIds: Set<string> = new Set()): Promise<any> => {
    // Prevent circular loading
    if (loadedIds.has(objectId)) return null;
    loadedIds.add(objectId);
    
    // Check if already loaded
    const existing = customObjects.find(o => o.id === objectId && o.fields && o.fields.length > 0);
    if (existing) return existing;
    
    // Fetch the object
    const obj = await fetchObjectDetails(objectId);
    if (!obj || !obj.fields) return obj;
    
    // Add to state
    setCustomObjects(prev => {
      const exists = prev.some(o => o.id === objectId);
      if (exists) {
        return prev.map(o => o.id === objectId ? obj : o);
      }
      return [...prev, obj];
    });
    
    // Load any referenced objects
    for (const field of obj.fields) {
      if ((field.field_type === 'reference' || 
           (field.field_type === 'array' && field.array_item_type === 'reference')) &&
          field.reference_object_id) {
        await loadObjectWithReferences(field.reference_object_id, loadedIds);
      }
    }
    
    return obj;
  };
  
  const loadTemplates = async () => {
    // Load public chain templates from the database
    // This allows templates to be managed via migrations rather than hardcoded
    try {
      const token = await user?.getIdToken();
      const response = await fetch('/api/v2/chains/templates', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      } else {
        console.error('Failed to load templates:', response.status);
        setTemplates([]);
      }
    } catch (err) {
      console.error('Error loading templates:', err);
      setTemplates([]);
    }
  };
  
  useEffect(() => {
    if (!selectedNodeId || !nodeConfigOpen) { setNodeInstructions(null); return; }
    const node = nodes.find(n => n.id === selectedNodeId);
    const funcId = node?.data?.functionId || '';
    if (!funcId || funcId.startsWith('plugin:')) { setNodeInstructions(null); return; }
    let cancelled = false;
    (async () => {
      setLoadingNodeInstructions(true);
      try {
        const token = await user?.getIdToken();
        const res = await fetch(`/api/v2/ai-functions/${funcId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok && !cancelled) {
          const fn = await res.json();
          setNodeInstructions((fn.data || fn).instructions || null);
        }
      } catch { /* ignore */ }
      if (!cancelled) setLoadingNodeInstructions(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId, nodeConfigOpen]);

  // Load output schema when node is selected (on-demand)
  useEffect(() => {
    const loadNodeOutputSchema = async () => {
      if (!selectedNodeId || !nodeConfigOpen) return;
      
      const node = nodes.find(n => n.id === selectedNodeId);

      // Plugins don't use custom objects — skip schema loading entirely
      const funcId = node?.data?.functionId || '';
      if (typeof funcId === 'string' && funcId.startsWith('plugin:')) {
        setLoadingNodeSchema(false);
        return;
      }

      const responseFormat = node?.data?.responseFormat;
      if (!responseFormat) return;
      
      const objectId = responseFormat.object_id;
      const objectName = responseFormat.object_name;
      
      if (!objectId && !objectName) return;
      
      // Check if already loaded in current state
      const existingById = objectId ? customObjects.find(o => o.id === objectId && o.fields && o.fields.length > 0) : null;
      const existingByName = objectName ? customObjects.find(o => o.name === objectName && o.fields && o.fields.length > 0) : null;
      
      if (existingById || existingByName) {
        setLoadingNodeSchema(false);
        return;
      }
      
      setLoadingNodeSchema(true);
      
      try {
        if (objectId) {
          // Load by ID (AI functions)
          await loadObjectWithReferences(objectId);
        } else if (objectName) {
          // Load by name (plugins)
          const obj = await fetchObjectByName(objectName);
          if (obj && obj.id) {
            // Load with references
            await loadObjectWithReferences(obj.id);
          }
        }
      } catch (err) {
        console.error('Error loading node schema:', err);
      }
      
      // Small delay to allow state update to propagate before hiding loading
      await new Promise(resolve => setTimeout(resolve, 100));
      setLoadingNodeSchema(false);
    };
    
    loadNodeOutputSchema();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId, nodeConfigOpen, customObjects.length]);

  const loadFunctions = async () => {
    try {
      const token = await user?.getIdToken();
      
      // Load AI functions
      const functionsResponse = await fetch('/api/v2/chains/functions/schemas', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (functionsResponse.ok) {
        const aiFunctions = await functionsResponse.json();
        
        // Load plugin functions
        try {
          const pluginsResponse = await fetch('/api/v2/plugins', {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (pluginsResponse.ok) {
            const pluginsData = await pluginsResponse.json();
            console.log('[PLUGINS] Received plugin data:', pluginsData);
            
            const pluginFunctions = (pluginsData.plugins || []).map((plugin: any) => ({
              ...plugin,
              id: `plugin:${plugin.name}`,  // Prefix with 'plugin:' to distinguish
              name: plugin.name,
              display_name: plugin.display_name || plugin.name,
              description: plugin.description,
              input_types: plugin.input_types,
              allowed_models: [],  // Plugins don't use models
              category: plugin.category,
              tags: [...(plugin.tags || []), 'plugin', 'code'],
              execution_time_estimate: plugin.execution_time_estimate,
              is_plugin: true,
              is_public: false  // Mark as not public so they don't appear in user functions tab
            }));
            
            console.log('[PLUGINS] Mapped plugin functions:', pluginFunctions);
            
            // Combine AI functions and plugin functions
            const allFunctions = [...aiFunctions, ...pluginFunctions];
            setFunctions(allFunctions);
            console.log('[PLUGINS] Total functions after combining:', allFunctions.length);
            
          } else {
            // Plugins failed to load, just use AI functions
            setFunctions(aiFunctions);
          }
        } catch (pluginError) {
          // If plugins fail, still use AI functions
          console.warn('Failed to load plugins:', pluginError);
          setFunctions(aiFunctions);
        }
        
        if (aiFunctions.length === 0) {
          setError('No functions available. Please create some AI functions first, or run database migrations to add public functions.');
        }
      } else {
        const errorData = await functionsResponse.json();
        setError(`Failed to load functions: ${errorData.detail || functionsResponse.statusText}`);
      }
    } catch (err) {
      console.error('Error loading functions:', err);
      setError('Failed to load functions. Please check your connection and try again.');
    }
  };

  const loadChains = async () => {
    try {
      setChainsLoading(true);
      const token = await user?.getIdToken();
      const response = await fetch('/api/v2/chains/', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setChains(data);
      }
    } catch (err) {
      console.error('Error loading chains:', err);
    } finally {
      setChainsLoading(false);
    }
  };
  
  const loadExecutionHistory = async (chainId?: string) => {
    historyAbortRef.current?.abort();
    const controller = new AbortController();
    historyAbortRef.current = controller;
    try {
      setRunHistoryLoading(true);
      const token = await user?.getIdToken();
      const params = new URLSearchParams();
      const searching = isRunSearchActive(runSearchDebounced);
      params.set('page', String(runHistoryPage));
      params.set('page_size', String(RUN_HISTORY_PAGE_SIZE));
      if (searching) params.set('q', runSearchDebounced);
      if (runStatusFilter !== 'all') params.set('status', runStatusFilter);
      const qs = params.toString();
      const url = chainId
        ? `/api/v2/chains/${chainId}/executions?${qs}`
        : `/api/v2/chains/executions?${qs}`;
        
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });

      if (response.ok) {
        const data = await response.json();
        if (controller.signal.aborted) return;
        if (Array.isArray(data)) {
          setExecutionHistory(data);
          setRunHistoryTotal(data.length);
        } else {
          setExecutionHistory(data.results || []);
          setRunHistoryTotal(data.pagination?.total_items ?? 0);
        }
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      console.error('Error loading execution history:', err);
    } finally {
      if (!controller.signal.aborted) setRunHistoryLoading(false);
    }
  };

  const openHistoryRun = async (exec: ChainExecution) => {
    if (openingRunId) return;
    setOpeningRunId(exec.id);
    try {
      const token = await user?.getIdToken();
      const response = await fetch(`/api/v2/chains/executions/${exec.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      const full: ChainExecution = await response.json();
      const merged = { ...full, match_previews: exec.match_previews || full.match_previews };
      const results = merged.node_results || {};
      const completedIds = Object.keys(results).filter(
        (id) => results[id]?.status === 'completed' || results[id]?.status === 'failed'
      );
      setExpandedOutputNodes(new Set(completedIds));
      const matchNode = firstMatchingNodeId(
        merged.match_previews,
        merged.node_results,
        runSearchDebounced
      );
      setSelectedResultNode(matchNode || completedIds[completedIds.length - 1] || null);
      setInRunSearch(isRunSearchActive(runSearch) ? runSearch.trim() : '');
      setShowMatchingStepsOnly(false);
      setExecution(merged);
      if (merged.status === 'running' || merged.status === 'pending' || merged.status === 'paused') {
        pollExecution(merged.id);
      }
    } catch (err) {
      console.error('Error loading run details:', err);
    } finally {
      setOpeningRunId(null);
    }
  };

  const loadChain = (chain: Chain, freshFunctions?: any[]) => {
    setSelectedChain(chain);
    setChainName(chain.name);
    setChainDescription(chain.description);
    
    const funcList = freshFunctions || functions;

    const gd = typeof chain.graph_data === 'string'
      ? JSON.parse(chain.graph_data) : chain.graph_data;

    const flowNodes = (gd?.nodes || []).map((node: any) => {
      const func = funcList.find((f: any) => f.id === node.functionId);
      return {
        id: node.id,
        type: 'functionNode',
        position: node.position,
        data: {
          functionId: node.functionId,
          functionName: func?.name || 'Unknown',
          functionDescription: func?.description || '',
          isPublic: func?.is_public || false,
          inputTypes: func?.input_types || [],
          responseFormat: func?.response_format,
          allowed_models: func?.allowed_models || [],
          config: node.config || { mapOverArray: false },
          inputTemplate: node.inputTemplate || node.config?.inputTemplate || null,
          isPlugin: (func as any)?.is_plugin || node.functionId?.startsWith('plugin:') || false,
          usageInfo: (func as any)?.usage_info || null,
        },
      };
    });

    setNodes(flowNodes);
    setEdges(gd?.edges || []);
  };

  /** Re-fetch the saved chain (e.g. after a version restore) and load it into the editor. */
  const reloadChainFromServer = async (chainId: string) => {
    const token = await user?.getIdToken();
    const authHeaders = { Authorization: `Bearer ${token}` };
    const res = await fetch(`/api/v2/chains/${chainId}`, { headers: authHeaders });
    if (!res.ok) throw new Error('Failed to reload chain');
    const chain = await res.json();
    const gd = typeof chain.graph_data === 'string' ? JSON.parse(chain.graph_data) : chain.graph_data;
    const known = new Set(functions.map((f: any) => f.id));
    const missing = Array.from(new Set<string>(
      (gd?.nodes || []).map((n: any) => n.functionId).filter((id: string) => id && !id.startsWith('plugin:') && !known.has(id)),
    ));
    const resolved = (await Promise.all(missing.map(async (fid) => {
      try {
        const r = await fetch(`/api/v2/ai-functions/${fid}`, { headers: authHeaders });
        if (!r.ok) return null;
        const d = await r.json();
        const fn = d.data || d;
        return { id: String(fn.id), name: fn.name, display_name: fn.display_name || fn.name,
          description: fn.description, input_types: fn.input_types, response_format: fn.response_format,
          allowed_models: fn.allowed_models || [], customer_id: fn.customer_id,
          is_public: fn.customer_id === 'public', version: fn.version };
      } catch {
        return null;
      }
    }))).filter(Boolean);
    loadChain(chain, resolved.length ? [...functions, ...resolved] : undefined);
    setChains((prev) => prev.map((c) => (c.id === chain.id ? chain : c)));
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const chainLink = searchParams.get('chain');
  useEffect(() => {
    if (!chainLink || functions.length === 0) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('chain');
      return next;
    }, { replace: true });
    reloadChainFromServer(chainLink).catch(() => setError('Could not open that chain.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainLink, functions.length]);

  const promptLink = searchParams.get('prompt');
  useEffect(() => {
    if (!promptLink || chainLink) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('prompt');
      return next;
    }, { replace: true });
    setWizardEditPrompt(undefined); setWizardEditTemplateSlug(undefined); setWizardEditChainId(undefined); setWizardEditChainName(undefined); setWizardEditChainDesc(undefined); setWizardEditPlan(undefined);
    setWizardSeedPrompt(promptLink.slice(0, 4000));
    setChainWizardOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptLink, chainLink]);
  
  const loadTemplate = (template: ChainTemplate) => {
    // Templates are now loaded from the database with full graph_data
    // This works the same way as loading a saved chain, but doesn't set selectedChain
    // so the user can save it as a new chain with their own name
    
    if (!template.graph_data || !template.graph_data.nodes) {
      setError('Invalid template: missing graph data');
      setTemplatesDialogOpen(false);
      return;
    }
    
    // Validate that all required functions/plugins exist
    const missingFunctions: string[] = [];
    for (const node of template.graph_data.nodes) {
      const funcId = node.functionId;
      
      // Check if it's a plugin
      if (funcId.startsWith('plugin:')) {
        const pluginFunc = functions.find(f => f.id === funcId);
        if (!pluginFunc) {
          missingFunctions.push(funcId.replace('plugin:', '') + ' (plugin)');
        }
      } else {
        // It's an AI function
        const func = functions.find(f => f.id === funcId);
        if (!func) {
          missingFunctions.push(funcId);
        }
      }
    }
    
    if (missingFunctions.length > 0) {
      setError(`Missing required functions: ${missingFunctions.join(', ')}. Please run database migrations: alembic upgrade head`);
      setTemplatesDialogOpen(false);
      return;
    }
    
    // Convert stored nodes to React Flow nodes with full function data
    const flowNodes = template.graph_data.nodes.map((node: any) => {
      const funcId = node.functionId;
      const func = functions.find(f => f.id === funcId);
      
      return {
        id: node.id,
        type: 'functionNode',
        position: node.position,
        data: {
          functionId: funcId,
          functionName: func?.name || node.functionId,
          functionDescription: func?.description || '',
          isPublic: func?.is_public || funcId.startsWith('plugin:'),
          inputTypes: func?.input_types || [],
          responseFormat: func?.response_format,
          allowed_models: func?.allowed_models || [],
          config: node.config || { mapOverArray: false },
          inputTemplate: node.inputTemplate || node.config?.inputTemplate || null,
          isPlugin: (func as any)?.is_plugin || funcId.startsWith('plugin:') || false,
          usageInfo: (func as any)?.usage_info || null,
        }
      };
    });

    setNodes(flowNodes);
    setEdges(template.graph_data.edges);
    
    // Set name with " (Copy)" suffix so user knows to rename it
    setChainName(template.name + ' (Copy)');
    setChainDescription(template.description);
    
    // Don't set selectedChain - this ensures the user saves it as a NEW chain
    // rather than overwriting the public template
    setSelectedChain(null);
    setTemplatesDialogOpen(false);
  };

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const chainFunctionIds = useMemo(() => {
    const ids = new Set<string>();
    chains.forEach(chain => {
      const gd = typeof chain.graph_data === 'string' ? JSON.parse(chain.graph_data) : chain.graph_data;
      (gd?.nodes || []).forEach((n: any) => { if (n.functionId && !n.functionId.startsWith('plugin:')) ids.add(n.functionId); });
    });
    return ids;
  }, [chains]);

  const filteredFunctions = useMemo(() => {
    return functions.filter((func) => {
      const matchesSearch = func.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        func.description?.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;
      if (hideChainFunctions && chainFunctionIds.has(func.id)) return false;
      return true;
    });
  }, [functions, searchTerm, hideChainFunctions, chainFunctionIds]);

  const onDragStart = (event: React.DragEvent, func: AIFunction) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify(func));
    event.dataTransfer.effectAllowed = 'move';
  };

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      try {
        const reactFlowBounds = event.currentTarget.getBoundingClientRect();
        const dataString = event.dataTransfer.getData('application/reactflow');
        
        if (!dataString) {
          console.error('No data transferred');
          return;
        }
        
        const funcData = JSON.parse(dataString);

        const position = {
          x: event.clientX - reactFlowBounds.left - 125,
          y: event.clientY - reactFlowBounds.top - 75,
        };

        // Check if this is a video generation function
        const isVideoFunc = funcData.allowed_models && hasVideoModel(funcData.allowed_models);
        
        const newNode: Node = {
          id: `node-${Date.now()}`,
          type: 'functionNode',
          position,
          data: {
            functionId: funcData.id,
            functionName: funcData.name || funcData.display_name,
            functionDescription: funcData.description,
            isPublic: funcData.is_public !== false,
            inputTypes: funcData.input_types || [],
            responseFormat: funcData.response_format,
            allowed_models: funcData.allowed_models || [],
            config: { 
              mapOverArray: false,
              // Set default video settings for video generation nodes
              ...(isVideoFunc && {
                videoDuration: 6,
                videoAspectRatio: '16:9'
              })
            },
            isPlugin: funcData.is_plugin || false,
            usageInfo: (funcData as any).usage_info || null,
          },
        };

        setNodes((nds) => nds.concat(newNode));
      } catch (error) {
        console.error('Error in onDrop:', error);
        setError('Failed to add node. Check console for details.');
      }
    },
    [setNodes, setError]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const openExecuteDialog = () => {
    const rtiCfg = (selectedChain as any)?.config?.runtime_inputs;
    const defaults: Record<string, any> = {};
    if (Array.isArray(rtiCfg)) {
      for (const f of rtiCfg) {
        if (f.default != null && f.key) {
          defaults[f.key] = f.default;
        }
      }
    }
    setExecutionInputs(defaults);
    setUploadedFiles({}); setInputMethods({}); setExecutionInput('{}'); setVideoUploadStatus({ uploading: false }); setVideoInputMethod('upload'); setExecuteDialogOpen(true);
  };

  const saveChain = async () => {
    if (!chainName.trim()) {
      setError('Please enter a chain name');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await user?.getIdToken();

      // Convert React Flow nodes to storage format
      const graphData = {
        nodes: nodes.map((node) => {
          // Parse inputTemplate if it's a string
          let inputTemplate = node.data.inputTemplate;
          if (typeof inputTemplate === 'string' && inputTemplate.trim()) {
            try {
              inputTemplate = JSON.parse(inputTemplate);
            } catch (err) {
              console.warn(`Node ${node.id} has invalid JSON in inputTemplate, saving as-is`);
            }
          }
          
          const nodeToSave = {
            id: node.id,
            functionId: node.data.functionId,
            position: node.position,
            config: node.data.config || {},
            inputTemplate: inputTemplate || null,  // Save input template
          };
          
          
          return nodeToSave;
        }),
        edges: edges.map((edge) => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle,
          targetHandle: edge.targetHandle,
        })),
      };

      const body = {
        name: chainName,
        description: chainDescription,
        graph_data: graphData,
        config: (selectedChain as any)?.config ?? {},
      };
      

      let response;
      if (selectedChain) {
        // Update existing chain
        response = await fetch(`/api/v2/chains/${selectedChain.id}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });
      } else {
        // Create new chain
        response = await fetch('/api/v2/chains/', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });
      }

      if (response.ok) {
        const data = await response.json();
        setSavedNotice({ isNew: !selectedChain, id: data?.id, name: data?.name || chainName });
        setSelectedChain(data);
        setSaveDialogOpen(false);
        loadChains();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save chain');
      }
    } catch (err) {
      console.error('Error saving chain:', err);
      setError('Failed to save chain');
    } finally {
      setLoading(false);
    }
  };

  const executeChain = async () => {
    if (!selectedChain) {
      setError('Please save the chain first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await user?.getIdToken();

      // Validate the Additional Data box before uploading anything.
      let extraJson: Record<string, any> = {};
      const rawExtra = executionInput.trim();
      if (rawExtra && rawExtra !== '{}') {
        let parsed: any;
        try { parsed = JSON.parse(rawExtra); } catch { parsed = undefined; }
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          setError('Additional Data must be a JSON object, e.g. {"question": "...", "foods": [...]}. Put plain text in the fields above instead.');
          return;
        }
        extraJson = parsed;
      }
      
      // Merge typed inputs with JSON input
      let inputData = { ...executionInputs };
      
      // Handle file uploads - use unified file handler
      // For reference_images, ALWAYS upload to GCS to avoid sending large base64 to LLM
      for (const [inputType, fileOrFiles] of Object.entries(uploadedFiles)) {
        if (fileOrFiles) {
          try {
            const asList = (Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles]).filter(Boolean) as File[];
            // Text files (csv/json/txt) already filled a paste field — don't also
            // upload them as URLs.
            const toUpload = asList.filter((f) => !isTextUploadFile(f));
            if (toUpload.length === 0) continue;

            // Check if this is an array of files (for reference_images)
            if (toUpload.length > 1 || Array.isArray(fileOrFiles)) {
              const totalFiles = toUpload.length;
              setUploadProgress({ current: 0, total: totalFiles, fileName: 'Starting parallel uploads...' });
              
              // Upload all files in PARALLEL using Promise.all for much faster uploads
              const uploadPromises = toUpload.map(async (file) => {
                // For reference images, FORCE upload to GCS by using the temp upload API directly
                // This ensures we always send URLs, not base64, to avoid oversized payloads
                try {
                  const formData = new FormData();
                  formData.append('file', file);
                  
                  const uploadResponse = await fetch('/api/v2/temp/upload', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: formData,
                  });
                  
                  if (uploadResponse.ok) {
                    const uploadResult = await uploadResponse.json();
                    const fileUrl = uploadResult.public_url || uploadResult.gcs_url || uploadResult.url;
                    if (fileUrl) {
                      return fileUrl;
                    }
                  }
                } catch (uploadErr) {
                  console.warn(`GCS upload failed for ${file.name}, falling back to prepareFileForSubmission`);
                }
                
                // Fallback to prepareFileForSubmission
                const prepared = await prepareFileForSubmission(file, undefined, token);
                const fileUrl = (prepared.metadata as any).gcs_url 
                  || (prepared.metadata as any).public_url 
                  || prepared.input;
                return fileUrl;
              });
              
              // Wait for all uploads to complete in parallel
              const urls = await Promise.all(uploadPromises);
              setUploadProgress({ current: totalFiles, total: totalFiles, fileName: 'All uploads complete!' });
              
              setUploadProgress(null);
              // Use the input type name directly (e.g., 'reference_images')
              inputData[inputType] = urls;
              if (inputType === 'attached_images') {
                inputData['attached_image_urls'] = urls;
              }
            } else {
              const singleFile = toUpload[0];
              setUploadProgress({ current: 1, total: 1, fileName: singleFile.name });

              // Runtime fields named *_url already are the destination key
              // (e.g. source_file_url). Media types like pdf/image become pdf_url.
              const destKey = inputType.endsWith('_url') ? inputType : `${inputType}_url`;
              let fileUrl: string | undefined;

              // Always send a hosted URL: inline base64 can't be seen by vision
              // nodes and gets billed as prompt text by downstream nodes.
              // Vision and file extraction need https; video/audio paths expect gs://.
              const wantsPublicUrl = inputType.endsWith('_url') || inputType.includes('file')
                || singleFile.type.startsWith('image/') || singleFile.type === 'application/pdf';
              try {
                const formData = new FormData();
                formData.append('file', singleFile);
                const uploadResponse = await fetch('/api/v2/temp/upload', {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}` },
                  body: formData,
                });
                if (uploadResponse.ok) {
                  const uploadResult = await uploadResponse.json();
                  fileUrl = wantsPublicUrl
                    ? (uploadResult.public_url || uploadResult.gcs_url || uploadResult.url)
                    : (uploadResult.gcs_url || uploadResult.public_url || uploadResult.url);
                }
              } catch (uploadErr) {
                console.warn(`GCS upload failed for ${inputType}, falling back to prepareFileForSubmission`, uploadErr);
              }

              if (!fileUrl) {
                const prepared = await prepareFileForSubmission(singleFile, undefined, token);
                const meta = prepared.metadata as any;
                fileUrl = (wantsPublicUrl ? (meta.public_url || meta.gcs_url) : (meta.gcs_url || meta.public_url))
                  || prepared.input;
              }

              setUploadProgress(null);
              inputData[destKey] = fileUrl;
              if (inputType === 'shared' && fileUrl && singleFile.type.startsWith('image/')) {
                const existing = Array.isArray(inputData['attached_image_urls']) ? inputData['attached_image_urls'] : [];
                if (!existing.includes(fileUrl)) {
                  inputData['attached_image_urls'] = [...existing, fileUrl];
                }
              }
            }
          } catch (error) {
            setUploadProgress(null);
            setError(`Failed to upload ${inputType} file: ${error instanceof Error ? error.message : 'Unknown error'}`);
            setLoading(false);
            return;
          }
        }
      }
      
      inputData = { ...inputData, ...extraJson };

      // Check if any node uses a video generation model
      const hasVideoNode = nodes.some(node => 
        node.data?.allowed_models && hasVideoModel(node.data.allowed_models)
      );
      
      const response = await fetch(`/api/v2/chains/${selectedChain.id}/execute`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          input_data: inputData,
          metadata: hasVideoNode ? {
            duration: videoDuration,
            aspect_ratio: videoAspectRatio
          } : {},
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setInRunSearch('');
        setShowMatchingStepsOnly(false);
        setExecution(data);
        setExecuteDialogOpen(false);
        
        // Start polling for results
        pollExecution(data.id);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to execute chain');
      }
    } catch (err) {
      console.error('Error executing chain:', err);
      setError('Failed to execute chain');
    } finally {
      setLoading(false);
    }
  };

  const pollExecution = async (executionId: string) => {
    if (!executionId) return;

    // If we're already polling this execution, don't start another loop
    if (executionPolling && pollingExecutionIdRef.current === executionId) {
      return;
    }

    // Stop any existing poll loop
    if (pollTimeoutRef.current) {
      window.clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    try {
      pollAbortRef.current?.abort();
    } catch {
      // ignore
    }
    pollAbortRef.current = null;
    pollInFlightRef.current = false;

    pollingExecutionIdRef.current = executionId;
    setExecutionPolling(true);

    const token = await user?.getIdToken();

    const scheduleNext = (delayMs: number) => {
      if (pollingExecutionIdRef.current !== executionId) return; // stale loop
      if (pollTimeoutRef.current) window.clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = window.setTimeout(() => {
        poll();
      }, delayMs);
    };

    const poll = async () => {
      if (pollingExecutionIdRef.current !== executionId) return; // stale loop

      // Ensure at most one in-flight request and at most 2 req/sec
      const now = Date.now();
      const sinceLast = now - (lastPollAtRef.current || 0);
      if (sinceLast < 500) {
        scheduleNext(500 - sinceLast);
        return;
      }
      if (pollInFlightRef.current) {
        scheduleNext(500);
        return;
      }

      pollInFlightRef.current = true;
      lastPollAtRef.current = now;

      const controller = new AbortController();
      pollAbortRef.current = controller;

      try {
        const response = await fetch(`/api/v2/chains/executions/${executionId}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });

        if (!response.ok) {
          // Back off a bit on transient errors
          scheduleNext(1000);
          return;
        }

        const data = await response.json();
        setExecution(data);

        if (data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled') {
          setExecutionPolling(false);
          pollingExecutionIdRef.current = null;
          if (pollTimeoutRef.current) {
            window.clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
          }
          // Refresh the Runs tab so the completed run appears immediately
          if (sidebarTab === 3) loadExecutionHistory(selectedChain?.id);
        } else if (data.status === 'paused') {
          scheduleNext(2000);
        } else {
          scheduleNext(500);
        }
      } catch (err) {
        if ((err as any)?.name === 'AbortError') return;
        console.error('Error polling execution:', err);
        // Back off on errors instead of hammering
        scheduleNext(1000);
      } finally {
        pollInFlightRef.current = false;
      }
    };

    poll();
  };

  const newChain = () => {
    setSelectedChain(null);
    setChainName('');
    setChainDescription('');
    setNodes([]);
    setEdges([]);
  };

  const deleteChain = async (chainToDelete?: Chain) => {
    const chain = chainToDelete || selectedChain;
    if (!chain) {
      return;
    }

    try {
      const token = await user?.getIdToken();
      const response = await fetch(`/api/v2/chains/${chain.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        // If we deleted the currently selected chain, clear the canvas
        if (selectedChain?.id === chain.id) {
        newChain();
        }
        loadChains();
        setDeleteChainDialog({ open: false, chain: null });
      }
    } catch (err) {
      console.error('Error deleting chain:', err);
      setError('Failed to delete chain');
    }
  };

  const confirmDeleteChain = (chain: Chain, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent loading the chain
    setDeleteChainDialog({ open: true, chain });
  };
  
  const handleCloseTemplateTip = () => {
    setShowTemplateTip(false);
    localStorage.setItem('hasShownChainTemplateTip', 'true');
  };
  
  const downloadPDF = (nodeResults: any) => {
    // Look for PDF output in node results
    try {
      
      for (const result of Object.values(nodeResults)) {
        const output = (result as any)?.output;
        
        
        // Check multiple possible locations for PDF data
        let pdfBase64 = null;
        let filename = 'document.pdf';
        
        if (output) {
          // Try direct properties
          pdfBase64 = output.pdf_base64 || output.pdf_data || output.pdfBase64 || output.pdfData;
          filename = output.filename || output.fileName || 'document.pdf';
          
          // If not found directly, check if output itself might contain the base64
          if (!pdfBase64 && typeof output === 'string' && output.length > 100) {
            // Might be base64 PDF data directly
            try {
              // Test if it's valid base64
              atob(output.substring(0, 100));
              pdfBase64 = output;
            } catch {
              // Not base64
            }
          }
          
          // Check nested structures
          if (!pdfBase64 && typeof output === 'object') {
            // Try nested data property
            if (output.data) {
              pdfBase64 = output.data.pdf_base64 || output.data.pdf_data;
              filename = output.data.filename || filename;
            }
          }
        }
        
        if (pdfBase64) {
          
          // Convert base64 to blob
          try {
            const binaryString = atob(pdfBase64);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: 'application/pdf' });
            
            
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            return true;
          } catch (decodeError) {
            console.error('Error decoding base64:', decodeError);
            setError(`Failed to decode PDF data: ${decodeError instanceof Error ? decodeError.message : 'Unknown error'}`);
            return false;
          }
        }
      }
      
      console.error('No PDF found in any node results');
      setError('No PDF found in execution results. Make sure your PDF generation function ran successfully.');
      return false;
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError(`Failed to download PDF: ${err instanceof Error ? err.message : 'Unknown error'}`);
      return false;
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
      {/* Template Tutorial Tooltip */}
      <Popper
        open={showTemplateTip}
        anchorEl={templateTipAnchor}
        placement="bottom-start"
        transition
        style={{ zIndex: 1500 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper
              elevation={8}
              sx={{
                mt: 1,
                p: 2.5,
                maxWidth: 360,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: '3px solid white',
                borderRadius: 2,
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%, 100%': {
                    boxShadow: '0 0 0 0 rgba(102, 126, 234, 0.7)',
                  },
                  '50%': {
                    boxShadow: '0 0 0 10px rgba(102, 126, 234, 0)',
                  },
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <TouchIcon sx={{ 
                  fontSize: 32, 
                  mt: 0.5,
                  animation: 'bounce 1s infinite',
                  '@keyframes bounce': {
                    '0%, 100%': { transform: 'translateY(0)' },
                    '50%': { transform: 'translateY(-8px)' },
                  }
                }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    ✨ Pro Tip: Ready-Made Templates
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    Start faster with <strong>pre-built chain templates</strong>! Click the <strong>Menu</strong> button and select <strong>"Use Template"</strong> to browse ready-to-use chains for common workflows.
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    💡 Templates include video generation, document processing, and more!
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={handleCloseTemplateTip}
                  sx={{ 
                    color: 'white',
                    mt: -0.5,
                    mr: -0.5,
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          </Fade>
        )}
      </Popper>

      {/* AI Chain Builder CTA */}
      <Popper
        open={showAIBuilderCTA && Boolean(aiBuilderBtnRef.current)}
        anchorEl={aiBuilderBtnRef.current}
        placement="bottom-start"
        transition
        style={{ zIndex: 1500 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper
              elevation={8}
              sx={{
                mt: 1, p: 2.5, maxWidth: 380,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: '3px solid white',
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <AutoAwesomeIcon sx={{ fontSize: 28, mt: 0.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.75 }}>
                    Create Chains with AI
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Describe what you want in plain English and AI will design an automated multi-step workflow for you.
                  </Typography>
                  <Typography variant="caption" sx={{
                    display: 'block', mb: 1.5, p: 1, bgcolor: 'rgba(255,255,255,0.15)',
                    borderRadius: 1, fontStyle: 'italic', lineHeight: 1.4,
                  }}>
                    "Generate dessert recipes with images and email them to my team"
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => { setShowAIBuilderCTA(false); localStorage.setItem('hasSeenChainBuilderCTA', 'true'); }}
                    sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.15)' } }}
                  >
                    Got it
                  </Button>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => { setShowAIBuilderCTA(false); localStorage.setItem('hasSeenChainBuilderCTA', 'true'); }}
                  sx={{ color: 'white', mt: -0.5, mr: -0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          </Fade>
        )}
      </Popper>
      
      {/* Header */}
      <Paper elevation={0} sx={{ px: 2, py: 1.25, mb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Left: Chain identity */}
          <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }} noWrap>
              {selectedChain ? selectedChain.name : 'Chain Builder'}
            </Typography>
            {selectedChain && (
              <Chip label="Saved" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
            )}
            {!selectedChain && nodes.length > 0 && (
              <Chip label="Unsaved" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
            )}
          </Box>

          {/* Right: Actions */}
          <Button
            ref={aiBuilderBtnRef}
            variant="contained"
            size="small"
            startIcon={<AutoAwesomeIcon sx={{ fontSize: 18 }} />}
            onClick={() => { setWizardEditPrompt(undefined); setWizardEditTemplateSlug(undefined); setWizardEditChainId(undefined); setWizardEditChainName(undefined); setWizardEditChainDesc(undefined); setWizardSeedPrompt(undefined); setChainWizardOpen(true); setShowAIBuilderCTA(false); localStorage.setItem('hasSeenChainBuilderCTA', 'true'); }}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4393 100%)' },
              textTransform: 'none', fontWeight: 600,
            }}
          >
            AI Builder
          </Button>

          {selectedChain && (selectedChain as any).config?.generation_prompt && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<TuneIcon sx={{ fontSize: 18 }} />}
              onClick={async () => {
                if (!selectedChain) return;
                try {
                  const token = await user?.getIdToken();
                  const headers = { Authorization: `Bearer ${token}` };
                  const graphData = typeof selectedChain.graph_data === 'string' ? JSON.parse(selectedChain.graph_data) : selectedChain.graph_data;
                  const chainNodes: any[] = graphData?.nodes || [];
                  const chainEdges: any[] = graphData?.edges || [];

                  const nodeToStepRefs = (tpl: Record<string, any> | null): Record<string, any> | null => {
                    if (!tpl) return null;
                    try {
                      const json = JSON.stringify(tpl);
                      return JSON.parse(json.replace(/\{\{node-(\d+)/g, '{{step-$1'));
                    } catch { return tpl; }
                  };

                  const steps: import('../components/ChainWizard').ChainStep[] = [];
                  for (let i = 0; i < chainNodes.length; i++) {
                    const cn = chainNodes[i];
                    const funcId = cn.functionId;
                    const cfg = cn.config || {};
                    const stepNum = parseInt(cn.id?.replace('node-', '') || `${i + 1}`, 10);

                    let instructions = '';
                    let description = '';
                    let inputTypes: string[] = ['text'];
                    let allowedModels: string[] = [];
                    let responseType = 'text';
                    let returnObject = null;
                    let stepType: 'ai_function' | 'plugin' | 'existing_function' = 'ai_function';
                    let existingId: string | null = null;
                    let displayName = cn.data?.functionName || `Step ${stepNum}`;
                    let name = displayName.replace(/\s+/g, '_').toLowerCase();

                    if (funcId?.startsWith('plugin:')) {
                      stepType = 'plugin';
                      existingId = funcId;
                      name = funcId.replace('plugin:', '');
                      displayName = name.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
                    } else if (funcId) {
                      try {
                        const fnRes = await fetch(`/api/v2/ai-functions/${funcId}`, { headers });
                        if (fnRes.ok) {
                          const fn = await fnRes.json();
                          const fnData = fn.data || fn;
                          instructions = fnData.instructions || '';
                          description = fnData.description || '';
                          inputTypes = fnData.input_types || ['text'];
                          allowedModels = fnData.allowed_models || [];
                          responseType = fnData.response_format?.type || 'text';
                          displayName = fnData.name || displayName;
                          name = (fnData.name || name).replace(/\s+/g, '_').toLowerCase();

                          const objId = fnData.response_format?.object_id;
                          if (objId && (responseType === 'object' || responseType === 'list')) {
                            try {
                              const objRes = await fetch(`/api/v2/custom-objects/${objId}`, { headers });
                              if (objRes.ok) {
                                const obj = await objRes.json();
                                const objData = obj.data || obj;
                                returnObject = {
                                  name: objData.name || 'output',
                                  fields: (objData.fields || []).map((f: any) => ({
                                    name: f.name, type: f.field_type || f.type || 'string',
                                    description: f.description || '', required: f.required !== false,
                                  })),
                                };
                              }
                            } catch { /* use defaults */ }
                          }
                        }
                      } catch { /* use defaults */ }
                    }

                    steps.push({
                      step_number: stepNum,
                      name,
                      display_name: displayName,
                      description,
                      step_type: stepType,
                      existing_id: existingId,
                      instructions,
                      input_types: inputTypes,
                      allowed_models: allowedModels,
                      response_type: responseType,
                      return_object: returnObject,
                      map_over_array: cfg.mapOverArray || false,
                      array_field: cfg.arrayPath ? cfg.arrayPath.replace(/^node-\d+\./, '') : null,
                      skip_item_when_empty: cfg.skipItemWhenEmpty || null,
                      input_template: nodeToStepRefs(cfg.inputTemplate),
                      enable_google_search: cfg.enableGoogleSearch || false,
                      knowledge_categories: Array.isArray(cfg.knowledgeCategories) ? cfg.knowledgeCategories : [],
                      knowledge_chunks: cfg.knowledgeChunks ?? null,
                      knowledge_query: cfg.knowledgeQuery || null,
                    });
                  }

                  const now = new Date();
                  const dateStr = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' + now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

                  const plan: import('../components/ChainWizard').ChainPlan = {
                    chain_name: `${selectedChain.name} (tuned ${dateStr})`,
                    chain_description: selectedChain.description || '',
                    steps,
                    edges: chainEdges.map((e: any) => ({ source: e.source, target: e.target })),
                    runtime_inputs: (selectedChain as any)?.config?.runtime_inputs || undefined,
                  };

                  setWizardEditPlan(plan);
                  setWizardEditPrompt((selectedChain as any).config?.generation_prompt || undefined);
                  setWizardEditTemplateSlug((selectedChain as any).config?.template_slug || undefined);
                  setWizardEditChainId(undefined);
                  setWizardEditChainName(plan.chain_name);
                  setWizardEditChainDesc(selectedChain.description);
                  setChainWizardOpen(true);
                } catch (err) {
                  console.error('Failed to build tuning plan:', err);
                }
              }}
              sx={{
                textTransform: 'none', fontWeight: 500, fontSize: '0.8rem',
                borderColor: 'secondary.main', color: 'secondary.main',
                '&:hover': { borderColor: 'secondary.dark', bgcolor: 'secondary.50' },
              }}
            >
              Customize Steps
            </Button>
          )}

          <Tooltip title="API Code">
            <span>
              <IconButton size="small" onClick={() => setCodeExamplesOpen(true)} disabled={!selectedChain}>
                <CodeIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={selectedChain ? 'Version history' : 'Save the chain to start keeping versions'}>
            <span>
              <IconButton size="small" onClick={() => setVersionHistoryOpen(true)} disabled={!selectedChain}>
                <ManageHistoryIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              !selectedChain
                ? 'Save the chain first'
                : chainReliability && !chainReliability.is_standard
                  ? 'Reliability (custom): time limit, retries and model fallback for this chain and its steps'
                  : 'Reliability: time limit, retries and model fallback for this chain and its steps'
            }
          >
            <span>
              <IconButton
                size="small"
                onClick={() => setReliabilityOpen(true)}
                disabled={!selectedChain}
                color={chainReliability && !chainReliability.is_standard ? 'primary' : 'default'}
              >
                <ShieldOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {learningStatus?.enabled && (
            <Tooltip title={selectedChain ? 'Self-improvement: learn from this chain’s runs' : 'Save the chain first'}>
              <span>
                <IconButton
                  size="small"
                  disabled={!selectedChain}
                  onClick={() => selectedChain && navigate(learningPath('chain', selectedChain.id))}
                >
                  <TipsAndUpdatesIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {/* Overflow menu for less common actions */}
          <Tooltip title="More actions">
            <IconButton size="small" onClick={(e) => setOverflowAnchorEl(e.currentTarget)}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={overflowAnchorEl} open={Boolean(overflowAnchorEl)} onClose={() => setOverflowAnchorEl(null)}>
            <MenuItem onClick={() => { newChain(); setOverflowAnchorEl(null); }}>
              <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="New Chain" />
            </MenuItem>
            <MenuItem onClick={() => { setTemplatesDialogOpen(true); setOverflowAnchorEl(null); handleCloseTemplateTip(); }} disabled={functions.length === 0}>
              <ListItemIcon><TemplateIcon fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText primary="Use Template" secondary={`${templates.length} available`} />
            </MenuItem>
            {selectedChain && (selectedChain as any).config?.generation_prompt && (
              <MenuItem onClick={() => {
                const prompt = (selectedChain as any).config.generation_prompt;
                const slug = (selectedChain as any).config?.template_slug;
                const now = new Date();
                const dateStr = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' + now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                setWizardEditPrompt(prompt);
                setWizardEditTemplateSlug(slug || undefined);
                setWizardEditChainId(undefined);
                setWizardEditChainName(`${selectedChain.name} (edited ${dateStr})`);
                setWizardEditChainDesc(selectedChain.description);
                setChainWizardOpen(true);
                setOverflowAnchorEl(null);
              }}>
                <ListItemIcon><AutoAwesomeIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText primary="Edit with AI" />
              </MenuItem>
            )}
            {selectedChain && learningStatus?.enabled && (
              <MenuItem onClick={() => { navigate(learningPath('chain', selectedChain.id)); setOverflowAnchorEl(null); }}>
                <ListItemIcon><TipsAndUpdatesIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText primary="Self-improvement" secondary="Learn from this chain's runs" />
              </MenuItem>
            )}
            {selectedChain && learningStatus && !learningStatus.enabled && isPlatformAdmin && !isImpersonating && (
              <MenuItem onClick={() => { navigate('/dashboard/admin/learning'); setOverflowAnchorEl(null); }}>
                <ListItemIcon><TipsAndUpdatesIcon fontSize="small" color="disabled" /></ListItemIcon>
                <ListItemText primary="Self-improvement" secondary="Off for this account: enable in Admin" />
              </MenuItem>
            )}
            {selectedChain && (
                  <MenuItem
                    onClick={() => { if (selectedChain) setDeleteChainDialog({ open: true, chain: selectedChain }); setOverflowAnchorEl(null); }}
                    sx={{ color: 'error.main' }}
                  >
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText primary="Delete Chain" />
                  </MenuItem>
            )}
          </Menu>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Tooltip title="Save chain">
            <span>
              <Button
                variant="outlined" size="small"
                startIcon={<SaveIcon sx={{ fontSize: 18 }} />}
                onClick={() => setSaveDialogOpen(true)}
                disabled={nodes.length === 0}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Save
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Execute chain">
            <span>
              <Button
                variant="contained" size="small" color="success"
                startIcon={<PlayIcon sx={{ fontSize: 18 }} />}
                onClick={openExecuteDialog}
                disabled={!selectedChain}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Execute
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={selectedChain ? 'Open App Builder with this chain attached and a starter prompt' : 'Load or save a chain first, then wrap it in a web app'}>
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={<WebIcon sx={{ fontSize: 18 }} />}
                disabled={!selectedChain}
                onClick={() => {
                  if (!selectedChain) return;
                  setViewMode('developer');
                  navigate(
                    `/dashboard/playground?mode=app_builder&new=1&chain=${encodeURIComponent(selectedChain.id)}&name=${encodeURIComponent(selectedChain.name || '')}`,
                  );
                }}
                sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                Make an app
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Paper>

      {savedNotice && selectedChain && savedNotice.id === selectedChain.id && (
        <SavedNextSteps
          sx={{ mb: 2 }}
          title={savedNotice.isNew ? `${savedNotice.name} is saved` : 'Changes saved'}
          message={
            savedNotice.isNew
              ? 'Next, run it with a real input. When it works, call it from your app with the API code, or wrap it in a web app.'
              : undefined
          }
          actions={
            savedNotice.isNew
              ? [
                  { label: 'Run it', icon: <PlayIcon />, primary: true, onClick: () => { setSavedNotice(null); openExecuteDialog(); } },
                  { label: 'API code', icon: <CodeIcon />, onClick: () => { setSavedNotice(null); setCodeExamplesOpen(true); } },
                ]
              : []
          }
          onClose={() => setSavedNotice(null)}
        />
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Main area: Sidebar + Canvas */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        {/* ── Persistent Side Panel ── */}
        <Paper
          elevation={0}
          sx={{
            width: sidebarOpen ? sidebarWidth : 0,
            minWidth: sidebarOpen ? sidebarWidth : 0,
            transition: sidebarDraggingRef.current ? 'none' : 'width 0.2s ease, min-width 0.2s ease',
            overflow: 'hidden',
            borderRight: sidebarOpen ? '1px solid' : 'none',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {sidebarOpen && (
            <>
              <Tabs
                value={sidebarTab}
                onChange={(_, v) => setSidebarTab(v)}
                variant="fullWidth"
                sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0, fontSize: '0.72rem', minWidth: 0 } }}
              >
                <Tab icon={<FolderOpenIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Chains" sx={{ minHeight: 40, fontWeight: 700, letterSpacing: 0.3 }} />
                <Tab icon={<NoteAddIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Nodes" sx={{ minHeight: 40 }} />
                <Tab icon={<SettingsIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Details" sx={{ minHeight: 40 }} />
                <Tab icon={<HistoryIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Runs" sx={{ minHeight: 40 }} />
              </Tabs>

              {/* Tab 1: Node browser (from drawer) */}
              {sidebarTab === 1 && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 1.5 }}>
                  <TextField
                    fullWidth size="small"
                    placeholder="Search functions & tools..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.5, color: 'text.secondary', fontSize: 18 }} /> }}
                    sx={{ mb: 1 }}
                  />
                  {chainFunctionIds.size > 0 && (
                    <FormControlLabel
                      control={<Switch size="small" checked={hideChainFunctions} onChange={(e) => setHideChainFunctions(e.target.checked)} />}
                      label={<Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Hide chain-only functions ({chainFunctionIds.size})</Typography>}
                      sx={{ ml: 0, mb: 0.5 }}
                    />
                  )}
                  <Tabs
                    value={tabValue}
                    onChange={(_, v) => setTabValue(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ minHeight: 32, mb: 1, '& .MuiTab-root': { minHeight: 32, py: 0, fontSize: '0.7rem', textTransform: 'none' } }}
                  >
                    <Tab label={`Mine (${filteredFunctions.filter(f => !f.is_public && !(f as any).is_plugin).length})`} />
                    <Tab label={`Public (${filteredFunctions.filter(f => f.is_public === true && !(f as any).is_plugin).length})`} />
                    <Tab label={`Tools (${filteredFunctions.filter(f => (f as any).is_plugin === true).length})`} />
                  </Tabs>
                  <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {(() => {
                      const userFunctions = filteredFunctions.filter(f => !f.is_public && !(f as any).is_plugin);
                      const publicFunctions = filteredFunctions.filter(f => f.is_public === true && !(f as any).is_plugin);
                      const pluginTools = filteredFunctions.filter(f => (f as any).is_plugin === true);
                      let displayFunctions: typeof filteredFunctions;
                      let emptyMessage: string;
                      if (tabValue === 0) { displayFunctions = userFunctions; emptyMessage = 'No custom functions. Create them in AI Functions.'; }
                      else if (tabValue === 1) { displayFunctions = publicFunctions; emptyMessage = 'No public functions available.'; }
                      else { displayFunctions = pluginTools; emptyMessage = 'No plugin tools available.'; }

                      const addFunctionToCanvas = (func: AIFunction) => {
                        const newNode: Node = {
                          id: `node-${Date.now()}`,
                          type: 'functionNode',
                          position: { x: 250 + nodes.length * 50, y: 150 + nodes.length * 30 },
                          data: {
                            functionId: func.id,
                            functionName: func.name || (func as any).display_name,
                            functionDescription: func.description,
                            isPublic: func.is_public !== false,
                            inputTypes: func.input_types || [],
                            responseFormat: func.response_format,
                            config: { mapOverArray: false },
                            isPlugin: (func as any).is_plugin || false,
                            usageInfo: (func as any).usage_info || null,
                          },
                        };
                        setNodes((nds) => nds.concat(newNode));
                      };

                      if (displayFunctions.length === 0) {
                        return (
                          <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">{searchTerm ? 'No matches.' : emptyMessage}</Typography>
                          </Box>
                        );
                      }
                      return displayFunctions.map((func) => (
                        <Tooltip key={func.id} title={func.description || ''} arrow placement="right" enterDelay={400}>
                          <Paper
                            elevation={0}
                            draggable
                            onDragStart={(e) => onDragStart(e as any, func)}
                            onClick={() => addFunctionToCanvas(func)}
                            sx={{
                              p: 1, mb: 0.75, cursor: 'grab', border: '1px solid', borderColor: 'divider',
                              borderRadius: 1.5, transition: 'all 0.12s ease',
                              '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50', transform: 'translateX(2px)' },
                              '&:active': { cursor: 'grabbing' },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', flexGrow: 1, minWidth: 0 }} noWrap>
                                {func.name}
                              </Typography>
                              {(func as any).is_plugin && <Chip label="Tool" size="small" color="secondary" sx={{ height: 18, fontSize: '0.6rem' }} />}
                              <AddIcon sx={{ fontSize: 16, color: 'action.active', flexShrink: 0 }} />
                            </Box>
                            {func.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontSize: '0.68rem', lineHeight: 1.3 }} noWrap>
                                {func.description.substring(0, 80)}{(func.description.length || 0) > 80 ? '...' : ''}
                              </Typography>
                            )}
                          </Paper>
                        </Tooltip>
                      ));
                    })()}
                  </Box>
                </Box>
              )}

              {/* Tab 0: My Chains (search, list, load, delete) */}
              {sidebarTab === 0 && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 1.5 }}>
                  <TextField
                    fullWidth size="small"
                    placeholder="Search chains..."
                    value={chainSearch}
                    onChange={(e) => setChainSearch(e.target.value)}
                    InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.5, color: 'text.secondary', fontSize: 18 }} /> }}
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {chainsLoading && chains.length === 0 ? (
                      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={28} />
                      </Box>
                    ) : chains.length === 0 ? (
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">No saved chains yet. Use the AI Builder to create one.</Typography>
                      </Box>
                    ) : (() => {
                      const filtered = chains.filter(c =>
                        c.name.toLowerCase().includes(chainSearch.toLowerCase()) ||
                        (c.description || '').toLowerCase().includes(chainSearch.toLowerCase())
                      );
                      if (filtered.length === 0) {
                        return (
                          <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">No chains match "{chainSearch}"</Typography>
                          </Box>
                        );
                      }
                      return filtered.map((chain) => {
                        const isSelected = selectedChain?.id === chain.id;
                        return (
                          <Tooltip
                            key={chain.id}
                            arrow
                            placement="right"
                            enterDelay={400}
                            title={
                              <Box sx={{ p: 0.5, maxWidth: 280 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{chain.name}</Typography>
                                {chain.description && (
                                  <Typography variant="caption" sx={{ lineHeight: 1.4, display: 'block' }}>{chain.description}</Typography>
                                )}
                                {(chain as any).config?.generation_prompt && (
                                  <Typography variant="caption" sx={{ lineHeight: 1.4, mt: 0.5, display: 'block', fontStyle: 'italic', color: 'grey.400' }}>
                                    AI prompt: {(chain as any).config.generation_prompt.substring(0, 120)}{(chain as any).config.generation_prompt.length > 120 ? '...' : ''}
                                  </Typography>
                                )}
                              </Box>
                            }
                          >
                            <Paper
                              variant="outlined"
                              onClick={() => loadChain(chain)}
                              sx={{
                                p: 1.25, mb: 0.75, cursor: 'pointer', borderRadius: 1.5,
                                borderColor: isSelected ? 'primary.main' : 'divider',
                                bgcolor: isSelected ? 'primary.50' : 'transparent',
                                borderWidth: isSelected ? 2 : 1,
                                transition: 'all 0.15s',
                                '&:hover': { bgcolor: isSelected ? 'primary.50' : 'action.hover', borderColor: 'primary.main' },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', flex: 1, minWidth: 0 }} noWrap>{chain.name}</Typography>
                                    <LearningAttentionBadge item={learningAttention[`chain:${chain.id}`]} />
                                  </Box>
                                  {chain.description && (
                                    <Typography variant="caption" color="text.secondary" sx={{
                                      fontSize: '0.68rem', lineHeight: 1.3, display: '-webkit-box',
                                      WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                    }}>
                                      {chain.description}
                                    </Typography>
                                  )}
                                  <SelfImprovedChip item={learningAttention[`chain:${chain.id}`]} />
                                  {chainPerf[chain.id]?.warnings?.length ? (
                                    <Box sx={{ mt: 0.5 }} onClick={(e) => e.stopPropagation()}>
                                      <WarningChips warnings={chainPerf[chain.id].warnings} max={2} />
                                    </Box>
                                  ) : null}
                                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', display: 'block', mt: 0.25 }}>
                                    {chain.updated_at
                                      ? `Updated ${new Date(chain.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${new Date(chain.updated_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
                                      : chain.created_at
                                        ? `Created ${new Date(chain.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${new Date(chain.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
                                        : ''}
                                  </Typography>
                                </Box>
                                <Tooltip title="Performance: run times, steps, time limit, cost">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/dashboard/chain-performance/${chain.id}`);
                                    }}
                                    sx={{ opacity: 0.5, mt: -0.25, '&:hover': { opacity: 1 } }}
                                  >
                                    <ChartIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(e) => confirmDeleteChain(chain, e)}
                                  sx={{ opacity: 0.4, mt: -0.25, '&:hover': { opacity: 1, bgcolor: 'error.light', color: 'white' } }}
                                >
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                            </Paper>
                          </Tooltip>
                        );
                      });
                    })()}
                  </Box>
                </Box>
              )}

              {/* Tab 2: Selected node details (inline config) */}
              {sidebarTab === 2 && (
                <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
                  {!selectedNodeId || !nodes.find(n => n.id === selectedNodeId) ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Click a node on the canvas or in the Chain tab to view its details.</Typography>
                    </Box>
                  ) : (() => {
                    const node = nodes.find(n => n.id === selectedNodeId)!;
                    const func = functions.find(f => String(f.id) === String(node.data.functionId));
                    const isPlugin = node.data.isPlugin || (func as any)?.is_plugin;
                    const config = node.data.config || {};
                    const updateConfig = (key: string, value: any) => {
                      setNodes((nds) => nds.map((n) =>
                        n.id === selectedNodeId
                          ? { ...n, data: { ...n.data, config: { ...n.data.config, [key]: value } } }
                          : n
                      ));
                    };
                    return (
                      <>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{node.data.functionName}</Typography>
                          {node.data.functionDescription && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                              {node.data.functionDescription}
                            </Typography>
                          )}
                          {isPlugin && <Chip label="Plugin / Tool" size="small" color="secondary" sx={{ mt: 0.5, height: 20, fontSize: '0.65rem' }} />}
                        </Box>

                        {isPlugin && func && (
                          <>
                            <Divider sx={{ mb: 1.5 }} />
                            <PluginUsageInfo plugin={func as any} variant="compact" />
                          </>
                        )}

                        <Divider sx={{ mb: 1.5 }} />
                        <StepInputFields
                          value={config.inputTemplate ?? node.data.inputTemplate}
                          fields={
                            isPlugin && func
                              ? ((func as any).usage_info?.inputs
                                || [
                                  ...(((func as any).required_params || []).map((n: string) => ({ name: n, required: true }))),
                                  ...(((func as any).optional_params || []).map((n: string) => ({ name: n, required: false }))),
                                ])
                              : undefined
                          }
                          hideKeys={isLlmBatchPlugin(node.data.functionId) ? ['model', 'max_tokens', 'reasoning_effort'] : []}
                          onChange={(next) => {
                            const tpl = Object.keys(next).length ? next : null;
                            setNodes((nds) => nds.map((n) =>
                              n.id === selectedNodeId
                                ? {
                                    ...n,
                                    data: {
                                      ...n.data,
                                      inputTemplate: tpl,
                                      config: { ...n.data.config, inputTemplate: tpl },
                                    },
                                  }
                                : n
                            ));
                          }}
                        />

                        <Divider sx={{ mb: 1.5 }} />

                        <Typography variant="overline" sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>Execution Options</Typography>

                        <FormControlLabel
                          control={<Switch size="small" checked={!!config.mapOverArray} onChange={(_, v) => updateConfig('mapOverArray', v)} />}
                          label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Map over array</Typography>}
                          sx={{ ml: 0, mb: 0.5 }}
                        />
                        {config.mapOverArray && (
                          <Box sx={{ pl: 2, mb: 1 }}>
                            <TextField
                              label="Max items" size="small" type="number" fullWidth
                              value={config.maxMappedItems || ''}
                              onChange={(e) => updateConfig('maxMappedItems', e.target.value ? parseInt(e.target.value) : undefined)}
                              sx={{ mb: 1, '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
                            />
                            <TextField
                              label="Skip when empty (field name)" size="small" fullWidth
                              value={config.skipItemWhenEmpty || ''}
                              onChange={(e) => updateConfig('skipItemWhenEmpty', e.target.value || undefined)}
                              sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
                            />
                          </Box>
                        )}

                        {func && func.allowed_models && func.allowed_models.some((m: string) => m.includes('gemini')) && (
                          <FormControlLabel
                            control={<Switch size="small" checked={!!config.enableGoogleSearch} onChange={(_, v) => updateConfig('enableGoogleSearch', v)} />}
                            label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Enable Google search</Typography>}
                            sx={{ ml: 0, mb: 0.5 }}
                          />
                        )}

                        {isLlmBatchPlugin(node.data.functionId) ? (
                          <>
                            <Divider sx={{ my: 1.5 }} />
                            {(() => {
                              const tpl = parseInputTemplate(config.inputTemplate ?? node.data.inputTemplate);
                              return (
                            <BatchJobSettings
                              model={config.modelOverride || tpl.model || 'gpt-5.4-mini'}
                              maxTokens={tpl.max_tokens ?? 8192}
                              reasoningEffort={tpl.reasoning_effort ?? 'low'}
                              onChange={(next) => {
                                const nextTpl = { ...tpl };
                                nextTpl.model = next.model;
                                nextTpl.max_tokens = next.maxTokens;
                                if (next.reasoningEffort) nextTpl.reasoning_effort = next.reasoningEffort;
                                else delete nextTpl.reasoning_effort;
                                setNodes((nds) => nds.map((n) =>
                                  n.id === selectedNodeId
                                    ? {
                                        ...n,
                                        data: {
                                          ...n.data,
                                          inputTemplate: nextTpl,
                                          config: { ...n.data.config, modelOverride: next.model, inputTemplate: nextTpl },
                                        },
                                      }
                                    : n
                                ));
                              }}
                            />
                              );
                            })()}
                          </>
                        ) : !isPlugin ? (
                          <>
                            <Divider sx={{ my: 1.5 }} />
                            <Typography variant="overline" sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>
                              AI Model
                            </Typography>
                            <Box sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}>
                              <ModelSelector
                                value={config.modelOverride || func?.allowed_models?.[0] || 'gpt-4o'}
                                onChange={(v) => updateConfig('modelOverride', v || undefined)}
                                label="Model"
                                size="small"
                              />
                            </Box>
                            <Box sx={{ mt: 1.5 }}>
                              <TemperatureSlider
                                value={config.temperature}
                                model={config.modelOverride || func?.allowed_models?.[0]}
                                onChange={(v) => updateConfig('temperature', v)}
                              />
                            </Box>
                          </>
                        ) : null}

                        {!isPlugin && (
                          <>
                            <Divider sx={{ my: 1.5 }} />
                            <Typography variant="overline" sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>
                              Knowledge (RAG)
                            </Typography>
                            <NodeKnowledgeSettings
                              dense
                              config={config}
                              categories={availableCategories}
                              onChange={(patch) => setNodes((nds) => nds.map((n) =>
                                n.id === selectedNodeId
                                  ? { ...n, data: { ...n.data, config: { ...n.data.config, ...patch } } }
                                  : n
                              ))}
                            />
                          </>
                        )}

                        <Divider sx={{ my: 1.5 }} />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small" variant="outlined" fullWidth
                            onClick={() => { setSelectedNodeId(node.id); setNodeConfigOpen(true); }}
                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                          >
                            Full Config
                          </Button>
                          <Button
                            size="small" variant="outlined" color="error" fullWidth
                            onClick={() => {
                              setNodes((nds) => nds.filter((n) => n.id !== selectedNodeId));
                              setEdges((eds) => eds.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId));
                              setSelectedNodeId(null);
                              setSidebarTab(1);
                            }}
                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                          >
                            Delete Node
                          </Button>
                        </Box>
                      </>
                    );
                  })()}
                </Box>
              )}

              {/* Tab 3: Execution history / Previous Runs */}
              {sidebarTab === 3 && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  {!selectedChain ? (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Select or save a chain to view its run history.</Typography>
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ px: 1, pt: 1, pb: 0.5, flexShrink: 0 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Search inputs, step I/O, errors…"
                          value={runSearch}
                          onChange={(e) => setRunSearch(e.target.value)}
                          InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 0.5, color: 'text.secondary', fontSize: 18 }} />,
                            endAdornment: runSearch ? (
                              <IconButton size="small" onClick={() => setRunSearch('')} sx={{ p: 0.25 }}>
                                <CloseIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            ) : runHistoryLoading ? (
                              <CircularProgress size={14} />
                            ) : null,
                          }}
                          sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
                        />
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
                          {([
                            { id: 'all', label: 'All' },
                            { id: 'completed', label: 'OK' },
                            { id: 'failed', label: 'Failed' },
                            { id: 'running', label: 'Running' },
                          ] as const).map((opt) => (
                            <Chip
                              key={opt.id}
                              label={opt.label}
                              size="small"
                              variant={runStatusFilter === opt.id ? 'filled' : 'outlined'}
                              color={runStatusFilter === opt.id ? 'primary' : 'default'}
                              onClick={() => {
                                setRunStatusFilter(opt.id);
                                setRunHistoryPage(1);
                              }}
                              sx={{ height: 22, fontSize: '0.65rem' }}
                            />
                          ))}
                          <Chip
                            icon={<ChartIcon sx={{ fontSize: 14 }} />}
                            label="Performance"
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(`/dashboard/chain-performance/${selectedChain.id}`)}
                            sx={{ height: 22, fontSize: '0.65rem', ml: 'auto' }}
                          />
                          {testsEnabled && (
                            <Tooltip title="Test cases for this chain: compare models, generate cases">
                              <Chip
                                icon={<FactCheckIcon sx={{ fontSize: 14 }} />}
                                label="Tests"
                                size="small"
                                variant="outlined"
                                onClick={() => navigate(`/dashboard/chain-performance/${selectedChain.id}?tab=tests`)}
                                sx={{ height: 22, fontSize: '0.65rem' }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.65rem' }}>
                          {(() => {
                            const start = runHistoryTotal === 0 ? 0 : (runHistoryPage - 1) * RUN_HISTORY_PAGE_SIZE + 1;
                            const end = Math.min(runHistoryPage * RUN_HISTORY_PAGE_SIZE, runHistoryTotal);
                            if (runHistoryLoading && executionHistory.length === 0) return 'Searching…';
                            if (runHistoryTotal === 0) return isRunSearchActive(runSearchDebounced) ? 'No matching runs' : 'No runs yet';
                            const range = `${start}–${end} of ${runHistoryTotal}`;
                            return isRunSearchActive(runSearchDebounced)
                              ? `${range} matching run${runHistoryTotal === 1 ? '' : 's'}`
                              : `${range} run${runHistoryTotal === 1 ? '' : 's'}`;
                          })()}
                        </Typography>
                      </Box>
                      {executionHistory.length === 0 ? (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            {isRunSearchActive(runSearchDebounced)
                              ? `No runs matching “${runSearchDebounced}”.`
                              : runStatusFilter !== 'all'
                                ? `No ${runStatusFilter} runs.`
                                : 'No runs yet. Execute this chain to see history here.'}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ flex: 1, overflow: 'auto', p: 1, pt: 0.5 }}>
                          {executionHistory.map((exec) => {
                            const totalCredits = Number(exec.total_ai_credits_used ?? 0);
                            const inputPreview = exec.input_preview || summarizeInput(exec.input_data);
                            const previews = exec.match_previews || [];
                            const opening = openingRunId === exec.id;
                            return (
                              <Paper
                                key={exec.id}
                                variant="outlined"
                                onClick={() => openHistoryRun(exec)}
                                sx={{
                                  p: 1.25, mb: 0.75, cursor: openingRunId ? 'wait' : 'pointer', borderRadius: 1.5,
                                  opacity: openingRunId && !opening ? 0.6 : 1,
                                  borderColor: exec.status === 'completed' ? 'success.light'
                                    : exec.status === 'failed' ? 'error.light'
                                    : exec.status === 'running' ? 'primary.light'
                                    : 'divider',
                                  bgcolor: exec.status === 'running' ? 'primary.50' : 'transparent',
                                  transition: 'all 0.15s',
                                  '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main', boxShadow: 1 },
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                  {opening ? (
                                    <CircularProgress size={12} />
                                  ) : (
                                    <Chip
                                      label={exec.status}
                                      size="small"
                                      color={exec.status === 'completed' ? 'success' : exec.status === 'failed' ? 'error' : exec.status === 'running' ? 'primary' : 'default'}
                                      sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                                    />
                                  )}
                                  {exec.execution_time_ms && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                      {formatDuration(exec.execution_time_ms)}
                                    </Typography>
                                  )}
                                  {(totalCredits > 0 || exec.total_cost) && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                      {totalCredits > 0 ? `${totalCredits.toFixed(1)} cr` : `$${exec.total_cost!.toFixed(4)}`}
                                    </Typography>
                                  )}
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                  {exec.started_at ? new Date(exec.started_at).toLocaleString() : 'Not started'}
                                </Typography>
                                {previews.length > 0 ? (
                                  previews.slice(0, 2).map((preview, idx) => (
                                    <Typography
                                      key={`${preview.where}-${preview.node_id || 'x'}-${idx}`}
                                      variant="caption"
                                      sx={{ fontSize: '0.65rem', display: 'block', mt: 0.35, color: 'text.primary', lineHeight: 1.3 }}
                                    >
                                      <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                        {formatMatchWhere(preview)}:{' '}
                                      </Box>
                                      <HighlightedText text={preview.snippet} query={runSearchDebounced} />
                                    </Typography>
                                  ))
                                ) : inputPreview ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.65rem', display: 'block', mt: 0.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  >
                                    {inputPreview}
                                  </Typography>
                                ) : null}
                                {exec.error && (
                                  <Typography variant="caption" color="error" sx={{ fontSize: '0.6rem', display: 'block', mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {exec.error.substring(0, 60)}
                                  </Typography>
                                )}
                              </Paper>
                            );
                          })}
                        </Box>
                      )}
                      {runHistoryTotal > RUN_HISTORY_PAGE_SIZE && (
                        <Box sx={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          px: 0.5, py: 0.25, flexShrink: 0, borderTop: 1, borderColor: 'divider',
                        }}>
                          <IconButton
                            size="small"
                            disabled={runHistoryPage <= 1 || runHistoryLoading}
                            onClick={() => setRunHistoryPage((p) => Math.max(1, p - 1))}
                          >
                            <ChevronLeftIcon fontSize="small" />
                          </IconButton>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            Page {runHistoryPage} of {Math.max(1, Math.ceil(runHistoryTotal / RUN_HISTORY_PAGE_SIZE))}
                          </Typography>
                          <IconButton
                            size="small"
                            disabled={runHistoryPage >= Math.ceil(runHistoryTotal / RUN_HISTORY_PAGE_SIZE) || runHistoryLoading}
                            onClick={() => setRunHistoryPage((p) => p + 1)}
                          >
                            <ChevronRightIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}
            </>
          )}
        </Paper>

        {/* Sidebar drag handle (resizable) + toggle */}
        <Box
          sx={{
            position: 'relative',
            width: 8, flexShrink: 0,
            cursor: sidebarOpen ? 'col-resize' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10,
            '&:hover .drag-indicator': { opacity: 1, bgcolor: 'primary.main' },
          }}
          onMouseDown={(e) => {
            if (!sidebarOpen) { setSidebarOpen(true); return; }
            e.preventDefault();
            sidebarDraggingRef.current = true;
            const startX = e.clientX;
            const startW = sidebarWidth;
            const onMove = (ev: MouseEvent) => {
              if (!sidebarDraggingRef.current) return;
              const newW = startW + ev.clientX - startX;
              if (newW < 100) {
                setSidebarOpen(false);
                sidebarDraggingRef.current = false;
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                return;
              }
              setSidebarWidth(Math.max(200, Math.min(550, newW)));
            };
            const onUp = () => {
              sidebarDraggingRef.current = false;
              document.removeEventListener('mousemove', onMove);
              document.removeEventListener('mouseup', onUp);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
          }}
          onDoubleClick={() => setSidebarOpen(v => !v)}
        >
          <Box
            className="drag-indicator"
            sx={{
              width: 4, height: 48, borderRadius: 2,
              bgcolor: sidebarOpen ? 'divider' : 'primary.main',
              opacity: sidebarOpen ? 0.5 : 1,
              transition: 'opacity 0.15s, background-color 0.15s',
            }}
          />
        </Box>

        {/* ── Canvas ── */}
        <Paper sx={{ flex: 1, position: 'relative' }}>
          <KnowledgeCategoriesContext.Provider value={availableCategories}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={(event, node) => {
              setSelectedNodeId(node.id);
              setSidebarTab(2);
            }}
            nodeTypes={nodeTypes}
            connectionMode={ConnectionMode.Loose}
            fitView
          >
            <Background variant={BackgroundVariant.Dots} />
            <MiniMap style={{ height: 80, width: 120 }} />
            <CanvasToolbar
              selectedNodeId={selectedNodeId}
              onDeleteNode={() => {
                if (selectedNodeId) {
                  setNodes((nds) => nds.filter((n) => n.id !== selectedNodeId));
                  setEdges((eds) => eds.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId));
                  setSelectedNodeId(null);
                }
              }}
              onAutoLayout={() => {
                setNodes((nds) => {
                  if (nds.length === 0) return nds;
                  const indeg: Record<string, number> = {};
                  const adj: Record<string, string[]> = {};
                  nds.forEach((n) => { indeg[n.id] = 0; adj[n.id] = []; });
                  edges.forEach((e) => { if (adj[e.source]) { adj[e.source].push(e.target); indeg[e.target] = (indeg[e.target] || 0) + 1; } });
                  const q = nds.map(n => n.id).filter(id => (indeg[id] || 0) === 0);
                  const order: string[] = [];
                  while (q.length) { const id = q.shift()!; order.push(id); for (const nxt of (adj[id] || [])) { indeg[nxt] -= 1; if (indeg[nxt] === 0) q.push(nxt); } }
                  nds.forEach(n => { if (!order.includes(n.id)) order.push(n.id); });
                  const colWidth = 350;
                  const rowHeight = 150;
                  const cols: Record<string, number> = {};
                  order.forEach((id) => {
                    const parents = edges.filter(e => e.target === id).map(e => e.source);
                    const parentCol = parents.length > 0 ? Math.max(...parents.map(p => cols[p] ?? 0)) + 1 : 0;
                    cols[id] = parentCol;
                  });
                  const colCounts: Record<number, number> = {};
                  return nds.map((n) => {
                    const col = cols[n.id] ?? 0;
                    const row = colCounts[col] ?? 0;
                    colCounts[col] = row + 1;
                    return { ...n, position: { x: 50 + col * colWidth, y: 50 + row * rowHeight } };
                  });
                });
              }}
            />
          </ReactFlow>
          </KnowledgeCategoriesContext.Provider>
        </Paper>
      </Box>

      {/* Delete Chain Confirmation Dialog */}
      <Dialog
        open={deleteChainDialog.open}
        onClose={() => setDeleteChainDialog({ open: false, chain: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          ⚠️ Delete Chain
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete <strong>"{deleteChainDialog.chain?.name}"</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 1 }}>
            This action cannot be undone. All execution history for this chain will also be deleted.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setDeleteChainDialog({ open: false, chain: null })}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={() => deleteChainDialog.chain && deleteChain(deleteChainDialog.chain)}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete Chain
          </Button>
        </DialogActions>
      </Dialog>

      {/* Function Selector Drawer is replaced by the persistent sidebar above */}

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save Chain</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Chain Name"
            value={chainName}
            onChange={(e) => setChainName(e.target.value)}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={chainDescription}
            onChange={(e) => setChainDescription(e.target.value)}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveChain} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Execute Dialog */}
      <Dialog 
        open={executeDialogOpen} 
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return; // Prevent closing on backdrop click during upload
          }
          setExecuteDialogOpen(false);
        }}
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PlayIcon color="success" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Execute Chain</Typography>
              {selectedChain && (
                <Typography variant="caption" color="text.secondary">{selectedChain.name}</Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {(() => {
            // Find entry nodes (nodes with no parents)
            const entryNodes = nodes.filter(node => {
              return !edges.some(edge => edge.target === node.id);
            });
            
            if (entryNodes.length === 0) {
              return (
                <Alert severity="warning">
                  No entry nodes found. Add nodes to your chain first.
                </Alert>
              );
            }
            
            // Collect unique input types from entry nodes
            const inputTypes = new Set<string>();
            entryNodes.forEach(node => {
              (node.data.inputTypes || []).forEach((type: string) => inputTypes.add(type));
            });
            
            // Check if this chain needs reference images (video chains)
            const needsReferenceImages =
              (selectedChain as any)?.config?.ui_type === 'video';

            const runtimeInputsCfg = (selectedChain as any)?.config?.runtime_inputs;
            const isVideoProcessingChain = !needsReferenceImages &&
              Array.isArray(runtimeInputsCfg) &&
              runtimeInputsCfg.some((f: any) => f.key === 'video_url');
            const hideAttachImages = Array.isArray(runtimeInputsCfg) &&
              normalizeRuntimeUploadFields(runtimeInputsCfg).some(
                (f: any) => f.type === 'upload' && acceptIncludesImages(f.accept)
              );

            // Categorize input types
            const mediaTypes = ['video', 'mp4', 'image', 'audio', 'mp3', 'pdf'];
            const textTypes = ['text', 'markdown', 'html', 'xml', 'csv'];
            const hasJSON = inputTypes.has('json');
            
            const mediaInputs = Array.from(inputTypes).filter(type => mediaTypes.includes(type.toLowerCase()));
            const textInputs = Array.from(inputTypes).filter(type => textTypes.includes(type.toLowerCase()));
            // Image-only chains use the Attach Images section; a second generic uploader just competes with it.
            const skipMediaUploader = !hideAttachImages && mediaInputs.length > 0 &&
              mediaInputs.every(type => type.toLowerCase() === 'image');
            
            // Helper to render file input for a given type
            const renderFileInput = (inputType: string) => {
              const method = inputMethods[inputType] || 'file';
              const fileOrFiles = uploadedFiles[inputType];
              
              // Map input types to user-friendly labels and accept attributes
              const typeConfig: Record<string, { label: string; accept: string; icon: string; multiple?: boolean }> = {
                'video': { label: 'Video', accept: 'video/*', icon: '🎥' },
                'mp4': { label: 'Video', accept: 'video/*', icon: '🎥' },
                'image': { label: 'Image', accept: 'image/*', icon: '🖼️' },
                'audio': { label: 'Audio', accept: 'audio/*', icon: '🎵' },
                'mp3': { label: 'Audio', accept: 'audio/*', icon: '🎵' },
                'pdf': { label: 'PDF', accept: 'application/pdf', icon: '📄' },
              };
              
              const config = typeConfig[inputType.toLowerCase()] || { label: inputType, accept: '*/*', icon: '📎' };
              const isMultiple = inputType === 'image' || inputType.toLowerCase().includes('images');
              const files = Array.isArray(fileOrFiles) ? fileOrFiles : (fileOrFiles ? [fileOrFiles] : []);
              
              return (
                <Box sx={{ mt: 2 }} key={inputType}>
                  <Typography variant="subtitle2" gutterBottom>
                    {config.icon} {config.label} Input {isMultiple && <Typography component="span" variant="caption" color="text.secondary">(up to 10)</Typography>}
                  </Typography>
                  
                  <Tabs 
                    value={method} 
                    onChange={(e, v) => setInputMethods({...inputMethods, [inputType]: v})} 
                    sx={{ mb: 2 }}
                  >
                    <Tab label={isMultiple ? "Upload Files" : "Upload File"} value="file" />
                    <Tab label={isMultiple ? "URLs" : "URL"} value="url" />
                  </Tabs>
                  
                  {method === 'url' ? (
                    isMultiple ? (
                      <Box>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          label={`${config.label} URLs (one per line)`}
                          placeholder={`https://example.com/image1.jpg\nhttps://example.com/image2.jpg`}
                          value={(executionInputs['reference_images'] || []).join('\n')}
                          onChange={(e) => {
                            const urls = e.target.value.split('\n').filter(u => u.trim());
                            setExecutionInputs({...executionInputs, reference_images: urls});
                            const newFiles = {...uploadedFiles};
                            delete newFiles[inputType];
                            setUploadedFiles(newFiles);
                          }}
                          helperText={`Enter up to 10 image URLs, one per line`}
                          sx={{ mb: 2 }}
                        />
                      </Box>
                    ) : (
                      <TextField
                        fullWidth
                        label={`${config.label} URL`}
                        placeholder={`https://example.com/file.${inputType}`}
                        value={executionInputs[`${inputType}_url`] || ''}
                        onChange={(e) => {
                          setExecutionInputs({...executionInputs, [`${inputType}_url`]: e.target.value});
                          const newFiles = {...uploadedFiles};
                          delete newFiles[inputType];
                          setUploadedFiles(newFiles);
                        }}
                        helperText={`Enter a public URL to your ${config.label.toLowerCase()} file`}
                        sx={{ mb: 2 }}
                      />
                    )
                  ) : (
                    <Box>
                      <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem' }}>
                        {isMultiple 
                          ? 'Upload up to 10 reference images (screenshots, product photos, etc.). Max 10MB each.'
                          : 'Files are uploaded to your cloud storage. Max 100MB per file.'}
                      </Alert>
                      <Box sx={{ p: 3, border: '2px dashed', borderColor: 'divider', borderRadius: 1, textAlign: 'center', bgcolor: 'grey.50' }}>
                        <input
                          accept={config.accept}
                          style={{ display: 'none' }}
                          id={`${inputType}-upload-button`}
                          type="file"
                          multiple={isMultiple}
                          onChange={(e) => {
                            const selectedFiles = e.target.files;
                            if (selectedFiles && selectedFiles.length > 0) {
                              if (isMultiple) {
                                // Handle multiple files
                                const newFiles: File[] = [...files];
                                for (let i = 0; i < selectedFiles.length && newFiles.length < 10; i++) {
                                  const file = selectedFiles[i];
                                  const sizeMB = file.size / 1024 / 1024;
                                  if (sizeMB > 10) {
                                    setError(`Image ${file.name} is too large. Maximum 10MB per image.`);
                                    continue;
                                  }
                                  newFiles.push(file);
                                }
                                setUploadedFiles({...uploadedFiles, reference_images: newFiles});
                                setExecutionInputs({...executionInputs, reference_images: []});
                              } else {
                                // Single file
                                const selectedFile = selectedFiles[0];
                                const sizeMB = selectedFile.size / 1024 / 1024;
                                if (sizeMB > 100) {
                                  setError('File too large. Maximum 100MB. Please use a URL instead.');
                                  return;
                                }
                                setUploadedFiles({...uploadedFiles, [inputType]: selectedFile});
                                setExecutionInputs({...executionInputs, [`${inputType}_url`]: ''});
                              }
                            }
                          }}
                        />
                        <label htmlFor={`${inputType}-upload-button`}>
                          <Button variant="contained" component="span" size="large">
                            {isMultiple ? `Add Images (${files.length}/10)` : `Choose ${config.label} File`}
                          </Button>
                        </label>
                        
                        {/* Show uploaded files */}
                        {files.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            {isMultiple ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                                {files.map((file, idx) => (
                                  <Box key={idx} sx={{ 
                                    position: 'relative', 
                                    width: 80, 
                                    height: 80, 
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    overflow: 'hidden'
                                  }}>
                                    <img 
                                      src={URL.createObjectURL(file)} 
                                      alt={file.name}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const newFiles = files.filter((_, i) => i !== idx);
                                        setUploadedFiles({...uploadedFiles, reference_images: newFiles});
                                      }}
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        bgcolor: 'rgba(0,0,0,0.5)',
                                        color: 'white',
                                        p: 0.25,
                                        '&:hover': { bgcolor: 'error.main' }
                                      }}
                                    >
                                      <CloseIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {files[0]?.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {((files[0]?.size || 0) / 1024 / 1024).toFixed(2)} MB
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            };
            
            // Helper to render text input for a given type
            const renderTextInput = (inputType: string) => {
              const labelMap: Record<string, string> = {
                'text': 'Text',
                'markdown': 'Markdown',
                'html': 'HTML',
                'xml': 'XML',
                'csv': 'CSV',
              };
              
              const label = labelMap[inputType.toLowerCase()] || inputType;
              const chainNameLower = (selectedChain?.name || '').toLowerCase();

              const mealPlanExample = `Create a meal plan for 7 days.\n\nDetails:\n- Meals per day: 4 (breakfast, lunch, dinner, snack)\n- Goal: fat loss\n- Height: 165 cm\n- Weight: 72 kg\n- Activity: moderately active\n- Diet style: high-protein mediterranean\n- Likes: salmon, berries, greek yogurt\n- Dislikes: mushrooms\n- Allergies: peanuts\n- Budget: medium\n- Health conditions: prediabetes\n- Time constraints: 30 min max cooking on weekdays\n\nOptional: you can also paste JSON if you prefer. Example:\n{"days":7,"meals_per_day":4,"goal":"fat loss","macro_targets":{"calories":1900,"protein_g":150,"carbs_g":180,"fat_g":60}}`;

              const placeholder = chainNameLower.includes('meal plan') && (inputType.toLowerCase() === 'text' || inputType.toLowerCase() === 'json')
                ? mealPlanExample
                : `Enter ${label.toLowerCase()} content...`;
              
              return (
                <Box sx={{ mt: 2 }} key={inputType}>
                  <Typography variant="subtitle2" gutterBottom>
                    📝 {label} Input
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    value={executionInputs[inputType] || ''}
                    onChange={(e) => setExecutionInputs({...executionInputs, [inputType]: e.target.value})}
                    placeholder={placeholder}
                    sx={{ mb: 2, fontFamily: inputType !== 'text' ? 'monospace' : 'inherit' }}
                  />
                </Box>
              );
            };
            
            return (
              <Box>
                {/* Credit Usage Warning for Video Chains */}
                {needsReferenceImages && (
                  <Alert 
                    severity="warning" 
                    sx={{ mb: 2 }}
                    icon={<span style={{ fontSize: '1.2rem' }}>⚠️</span>}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      High Credit Usage Warning
                    </Typography>
                    <Typography variant="body2">
                      Video generation chains create multiple video clips and can use <strong>5,000+ AI credits</strong> per run. 
                      Longer videos or more scenes will use more credits. Each ~8 second video clip costs approximately 750 credits.
                    </Typography>
                  </Alert>
                )}

                {isVideoProcessingChain && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      Video Processing
                    </Typography>
                    <Typography variant="body2">
                      This chain analyzes and processes an existing video. Costs depend on video length but are typically low (transcription + AI analysis).
                    </Typography>
                  </Alert>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Entry nodes: {entryNodes.map(n => n.data.functionName).join(', ')}
                  </Typography>
                  {entryNodes.length > 1 && mediaInputs.length > 0 && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={singleInput}
                          onChange={(e) => setSingleInput(e.target.checked)}
                          size="small"
                        />
                      }
                      label={<Typography variant="caption">Use same input for all nodes</Typography>}
                    />
                  )}
                </Box>
                
                {/* Render all media inputs - but NOT for Reference Video Generator which has custom inputs */}
                {skipMediaUploader ? null : !needsReferenceImages && singleInput && mediaInputs.length > 0 ? (
                  // Single input mode - show generic file uploader
                  <Box sx={{ mt: 2 }} key="shared-media">
                    <Typography variant="subtitle2" gutterBottom>
                      📎 Media Input (shared by all nodes)
                    </Typography>
                    
                    <Tabs 
                      value={inputMethods['shared'] || 'file'} 
                      onChange={(e, v) => setInputMethods({...inputMethods, 'shared': v})} 
                      sx={{ mb: 2 }}
                    >
                      <Tab label="Upload File" value="file" />
                      <Tab label="URL" value="url" />
                    </Tabs>
                    
                    {(inputMethods['shared'] || 'file') === 'url' ? (
                      <TextField
                        fullWidth
                        label="File URL"
                        placeholder="https://example.com/video.mp4"
                        value={executionInputs.video_url || ''}
                        onChange={(e) => {
                          setExecutionInputs({...executionInputs, video_url: e.target.value});
                          setUploadedFiles({});
                        }}
                        helperText="Enter URL to video, audio, or other media file"
                        sx={{ mb: 2 }}
                      />
                    ) : (
                      <Box>
                        <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem' }}>
                          Upload a media file (video, audio, etc.). Max 100MB.
                        </Alert>
                        <Box sx={{ p: 3, border: '2px dashed', borderColor: 'divider', borderRadius: 1, textAlign: 'center', bgcolor: 'grey.50' }}>
                          <input
                            accept={mediaInputs.map(t => {
                              const lower = t.toLowerCase();
                              if (lower.includes('video') || lower === 'mp4') return 'video/*';
                              if (lower.includes('audio') || lower === 'mp3') return 'audio/*';
                              if (lower === 'image') return 'image/*';
                              if (lower === 'pdf') return 'application/pdf';
                              return '*/*';
                            }).join(',')}
                            style={{ display: 'none' }}
                            id="shared-upload-button"
                            type="file"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const sizeMB = file.size / 1024 / 1024;
                                if (sizeMB > 100) {
                                  setError('File too large. Maximum 100MB.');
                                  return;
                                }
                                setUploadedFiles({ 'shared': file });
                                setExecutionInputs(prev => ({...prev, video_url: ''}));
                              }
                            }}
                          />
                          <label htmlFor="shared-upload-button">
                            <Button variant="contained" component="span" size="large">
                              Choose File
                            </Button>
                          </label>
                          {uploadedFiles['shared'] && !Array.isArray(uploadedFiles['shared']) && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" fontWeight="bold">
                                {(uploadedFiles['shared'] as File).name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {((uploadedFiles['shared'] as File).size / 1024 / 1024).toFixed(2)} MB
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                ) : (
                  // Multiple inputs mode - show all (but not for Reference Video Generator)
                  !needsReferenceImages && mediaInputs.map(type => renderFileInput(type))
                )}

                {/* Description/Concept Input - for video chains */}
                {needsReferenceImages && (() => {
                  const runtimeInputs = (selectedChain as any)?.config?.runtime_inputs;
                  const primaryField = runtimeInputs?.find((f: any) => f.required && (f.type === 'textarea' || f.type === 'text' || !f.type));
                  const primaryKey = primaryField?.key || (selectedChain?.name?.toLowerCase().includes('video story') ? 'text' : 'description');
                  const primaryLabel = primaryField?.label || (selectedChain?.name?.toLowerCase().includes('video story') ? 'Video Concept' : 'Video Description');

                  return (
                  <Box sx={{ mt: 2 }} key="description">
                    <Typography variant="subtitle2" gutterBottom>
                      📝 {primaryLabel} <Typography component="span" color="error">*</Typography>
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={executionInputs[primaryKey] || ''}
                      onChange={(e) => {
                        setExecutionInputs({...executionInputs, [primaryKey]: e.target.value});
                      }}
                      placeholder={primaryField?.placeholder || (selectedChain?.name?.toLowerCase().includes('video story') 
                        ? "Enter your video story concept. For example:\n\n'Create a 60-second sci-fi short about astronauts discovering alien life on Europa. Main character is Commander Eva, a brave explorer. Include dramatic music and a hopeful ending.'"
                        : selectedChain?.name?.toLowerCase().includes('meal plan')
                        ? "Enter meal plan request info. Example:\n\n{\n  \"days\": 7,\n  \"meals_per_day\": 4,\n  \"goal\": \"fat loss\",\n  \"age\": 34,\n  \"sex\": \"female\",\n  \"height_cm\": 165,\n  \"weight_kg\": 72,\n  \"activity_level\": \"moderately active\",\n  \"diet_style\": \"high-protein mediterranean\",\n  \"likes\": [\"salmon\", \"berries\", \"greek yogurt\"],\n  \"dislikes\": [\"mushrooms\"],\n  \"allergies\": [\"peanuts\"],\n  \"budget\": \"medium\",\n  \"health_conditions\": [\"prediabetes\"],\n  \"time_constraints\": \"30 min max cooking on weekdays\"\n}\n\nYou can also provide explicit targets:\n{\n  \"days\": 5,\n  \"macro_targets\": {\"calories\": 1900, \"protein_g\": 150, \"carbs_g\": 180, \"fat_g\": 60}\n}"
                        : "Describe what kind of video you want. For example:\n\n'Create a 30-second promotional video for my SaaS dashboard. Highlight the analytics features shown in the screenshots, the easy-to-use interface, and emphasize how it saves time for busy professionals.'"
                      )}
                      helperText={primaryField?.description || (selectedChain?.name?.toLowerCase().includes('video story')
                        ? "Describe your story, characters, and scenes. The AI will enhance this into a full video concept."
                        : "Describe your product, guide, or content. Be specific about what you want to highlight."
                      )}
                      sx={{ mb: 2 }}
                    />
                    
                    <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                      <TextField
                        select
                        label="Target Duration"
                        value={executionInputs['target_duration'] || executionInputs['target_duration_seconds'] || 45}
                        onChange={(e) => {
                          const duration = parseInt(e.target.value);
                          setExecutionInputs(prev => ({
                            ...prev, 
                            target_duration: duration,
                            target_duration_seconds: duration
                          }));
                        }}
                        sx={{ minWidth: 150 }}
                        size="small"
                      >
                        <MenuItem value={15}>15 seconds</MenuItem>
                        <MenuItem value={30}>30 seconds</MenuItem>
                        <MenuItem value={45}>45 seconds</MenuItem>
                        <MenuItem value={60}>60 seconds</MenuItem>
                        <MenuItem value={90}>90 seconds</MenuItem>
                      </TextField>
                    </Box>
                  </Box>
                  );
                })()}
                
                {/* Reference Images Input - for video chains */}
                {needsReferenceImages && (
                  <Box sx={{ mt: 3 }} key="reference-images">
                    <Typography variant="subtitle2" gutterBottom>
                      🖼️ Reference Images {selectedChain?.name?.toLowerCase().includes('video story') 
                        ? <Typography component="span" variant="caption" color="text.secondary">(optional - up to 10 images to guide the AI)</Typography>
                        : <Typography component="span" variant="caption" color="text.secondary">(up to 10 - screenshots, product photos, etc.)</Typography>
                      }
                    </Typography>
                    
                    <Tabs 
                      value={inputMethods['reference_images'] || 'file'} 
                      onChange={(e, v) => setInputMethods({...inputMethods, reference_images: v})} 
                      sx={{ mb: 2 }}
                    >
                      <Tab label="Upload Images" value="file" />
                      <Tab label="Image URLs" value="url" />
                    </Tabs>
                    
                    {inputMethods['reference_images'] === 'url' ? (
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Image URLs (one per line)"
                        placeholder={`https://example.com/screenshot1.png\nhttps://example.com/product-photo.jpg`}
                        value={(executionInputs['reference_images'] || []).join('\n')}
                        onChange={(e) => {
                          const urls = e.target.value.split('\n').filter(u => u.trim());
                          setExecutionInputs({...executionInputs, reference_images: urls});
                          const newFiles = {...uploadedFiles};
                          delete newFiles['reference_images'];
                          setUploadedFiles(newFiles);
                        }}
                        helperText="Enter up to 10 image URLs, one per line"
                        sx={{ mb: 2 }}
                      />
                    ) : (
                      <Box>
                        <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem' }}>
                          Upload screenshots, product photos, or any images you want featured in your video. These will be used as visual references.
                        </Alert>
                        <Box sx={{ p: 3, border: '2px dashed', borderColor: 'primary.main', borderRadius: 1, textAlign: 'center', bgcolor: 'rgba(25, 118, 210, 0.04)' }}>
                          <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="reference-images-upload"
                            type="file"
                            multiple
                            onChange={(e) => {
                              const selectedFiles = e.target.files;
                              if (selectedFiles && selectedFiles.length > 0) {
                                const existingFiles = Array.isArray(uploadedFiles['reference_images']) 
                                  ? uploadedFiles['reference_images'] as File[]
                                  : [];
                                const newFiles: File[] = [...existingFiles];
                                for (let i = 0; i < selectedFiles.length && newFiles.length < 10; i++) {
                                  const file = selectedFiles[i];
                                  const sizeMB = file.size / 1024 / 1024;
                                  if (sizeMB > 10) {
                                    setError(`Image ${file.name} is too large. Maximum 10MB per image.`);
                                    continue;
                                  }
                                  newFiles.push(file);
                                }
                                setUploadedFiles({...uploadedFiles, reference_images: newFiles});
                                setExecutionInputs({...executionInputs, reference_images: []});
                              }
                            }}
                          />
                          <label htmlFor="reference-images-upload">
                            <Button variant="contained" component="span" size="large" color="primary">
                              Add Images ({(Array.isArray(uploadedFiles['reference_images']) ? (uploadedFiles['reference_images'] as File[]).length : 0)}/10)
                            </Button>
                          </label>
                          
                          {/* Show uploaded images */}
                          {Array.isArray(uploadedFiles['reference_images']) && (uploadedFiles['reference_images'] as File[]).length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                                {(uploadedFiles['reference_images'] as File[]).map((file, idx) => (
                                  <Box key={idx} sx={{ 
                                    position: 'relative', 
                                    width: 100, 
                                    height: 100, 
                                    border: '2px solid',
                                    borderColor: 'primary.main',
                                    borderRadius: 1,
                                    overflow: 'hidden'
                                  }}>
                                    <img 
                                      src={URL.createObjectURL(file)} 
                                      alt={file.name}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const newFiles = (uploadedFiles['reference_images'] as File[]).filter((_, i) => i !== idx);
                                        setUploadedFiles({...uploadedFiles, reference_images: newFiles});
                                      }}
                                      sx={{
                                        position: 'absolute',
                                        top: 2,
                                        right: 2,
                                        bgcolor: 'rgba(0,0,0,0.6)',
                                        color: 'white',
                                        p: 0.25,
                                        '&:hover': { bgcolor: 'error.main' }
                                      }}
                                    >
                                      <CloseIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        position: 'absolute', 
                                        bottom: 0, 
                                        left: 0, 
                                        right: 0, 
                                        bgcolor: 'rgba(0,0,0,0.6)', 
                                        color: 'white',
                                        p: 0.25,
                                        textAlign: 'center',
                                        fontSize: '0.65rem',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                      }}
                                    >
                                      {file.name}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Runtime input fields — structured form when chain has runtime_inputs metadata */}
                {(() => {
                  const runtimeInputs = (selectedChain as any)?.config?.runtime_inputs;
                  const videoHandledKeys = new Set(['attached_image_urls', 'manual_image_urls', 'reference_images']);
                  if (needsReferenceImages) {
                    const primaryField = runtimeInputs?.find((f: any) => f.required && (f.type === 'textarea' || f.type === 'text' || !f.type));
                    if (primaryField) videoHandledKeys.add(primaryField.key);
                    videoHandledKeys.add('description');
                    videoHandledKeys.add('text');
                    videoHandledKeys.add('topic');
                    videoHandledKeys.add('topic_content');
                    videoHandledKeys.add('target_duration');
                    videoHandledKeys.add('target_duration_seconds');
                  }
                  if (runtimeInputs && Array.isArray(runtimeInputs) && runtimeInputs.length > 0) {
                    const visibleFields = runtimeInputs.filter((f: any) => !videoHandledKeys.has(f.key));
                    if (visibleFields.length === 0 && needsReferenceImages) return null;
                    const fields = normalizeRuntimeUploadFields(visibleFields);
                    return (
                      <Box sx={{ mt: 1 }}>
                        {fields.map((field: any) => {
                          if (videoHandledKeys.has(field.key)) return null;
                          const fieldType = field.type || 'text';
                          const isRequired = field.required === true;
                          const fieldLabel = `${field.label || field.key}${isRequired ? '' : ' (optional)'}`;

                          if (field.key === 'video_url' && isVideoProcessingChain) {
                            const hasVideoInput = !!(executionInputs.video_url && String(executionInputs.video_url).trim());
                            return (
                              <Box key={field.key} sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                                  🎬 Video Source <Typography component="span" color="error" sx={{ fontWeight: 700 }}>*</Typography>
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>Required</Typography>
                                </Typography>

                                {videoInputMethod === 'upload' ? (
                                  <Box>
                                    <Box
                                      component="label"
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        p: 3,
                                        border: '2px dashed',
                                        borderColor: hasVideoInput ? 'success.main' : videoUploadStatus.error ? 'error.main' : 'primary.main',
                                        borderRadius: 2,
                                        bgcolor: hasVideoInput ? 'rgba(46, 125, 50, 0.04)' : videoUploadStatus.error ? 'rgba(211, 47, 47, 0.04)' : 'rgba(25, 118, 210, 0.04)',
                                        cursor: videoUploadStatus.uploading ? 'default' : 'pointer',
                                        transition: 'all 0.2s',
                                        '&:hover': videoUploadStatus.uploading ? {} : {
                                          borderColor: hasVideoInput ? 'success.dark' : 'primary.dark',
                                          bgcolor: hasVideoInput ? 'rgba(46, 125, 50, 0.08)' : 'rgba(25, 118, 210, 0.08)',
                                        },
                                        minHeight: 120,
                                      }}
                                    >
                                      <input
                                        type="file"
                                        hidden
                                        accept="video/*"
                                        disabled={videoUploadStatus.uploading}
                                        onChange={async (e) => {
                                          const file = e.target.files?.[0];
                                          if (!file) return;
                                          const sizeMB = parseFloat((file.size / (1024 * 1024)).toFixed(1));
                                          if (sizeMB > 500) {
                                            setVideoUploadStatus({ uploading: false, error: `File is ${sizeMB}MB — maximum is 500MB. Please compress or trim the video first.` });
                                            e.target.value = '';
                                            return;
                                          }
                                          const sizeLabel = `${sizeMB} MB`;
                                          setVideoUploadStatus({ uploading: true, fileName: file.name, fileSize: sizeLabel });
                                          try {
                                            const token = await getToken();
                                            const formData = new FormData();
                                            formData.append('file', file);
                                            const resp = await fetch('/api/v2/temp/upload', {
                                              method: 'POST',
                                              headers: { 'Authorization': `Bearer ${token}` },
                                              body: formData,
                                            });
                                            if (!resp.ok) throw new Error(`Upload failed (${resp.status})`);
                                            const result = await resp.json();
                                            const fileUrl = result.public_url || result.gcs_url || result.url;
                                            if (fileUrl) {
                                              setExecutionInputs(prev => ({ ...prev, video_url: fileUrl }));
                                              setVideoUploadStatus({ uploading: false, fileName: file.name, fileSize: sizeLabel });
                                            } else {
                                              throw new Error('No URL returned from upload');
                                            }
                                          } catch (err: any) {
                                            setVideoUploadStatus({ uploading: false, error: err.message || 'Upload failed' });
                                          }
                                          e.target.value = '';
                                        }}
                                      />
                                      {videoUploadStatus.uploading ? (
                                        <>
                                          <CircularProgress size={32} sx={{ mb: 1 }} />
                                          <Typography variant="body2" color="text.secondary">
                                            Uploading {videoUploadStatus.fileName}...
                                          </Typography>
                                        </>
                                      ) : hasVideoInput && videoUploadStatus.fileName ? (
                                        <>
                                          <Typography sx={{ fontSize: '2rem', mb: 0.5 }}>✅</Typography>
                                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                                            {videoUploadStatus.fileName}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {videoUploadStatus.fileSize} — click to replace
                                          </Typography>
                                        </>
                                      ) : (
                                        <>
                                          <Typography sx={{ fontSize: '2.5rem', mb: 0.5, lineHeight: 1 }}>🎥</Typography>
                                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                            Click to upload a video file
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            MP4, MOV, AVI, WebM — max 500 MB (recommended under 200 MB)
                                          </Typography>
                                        </>
                                      )}
                                    </Box>
                                    {videoUploadStatus.error && (
                                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                        {videoUploadStatus.error}
                                      </Typography>
                                    )}

                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, mb: 0.5 }}>
                                      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                                      <Typography variant="caption" color="text.secondary" sx={{ px: 2 }}>
                                        or paste a video URL
                                      </Typography>
                                      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                                    </Box>

                                    <TextField
                                      fullWidth
                                      placeholder="https://example.com/video.mp4 or gs://bucket/path/video.mp4"
                                      value={(!videoUploadStatus.fileName && executionInputs[field.key]) ? executionInputs[field.key] : ''}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setExecutionInputs(prev => ({ ...prev, [field.key]: val }));
                                        setVideoUploadStatus({ uploading: false });
                                      }}
                                      size="small"
                                      sx={{ mt: 0.5 }}
                                      InputProps={{
                                        sx: { fontSize: '0.85rem' },
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  <Box>
                                    <TextField
                                      fullWidth
                                      label="Video URL"
                                      placeholder="https://... or gs://bucket/path/video.mp4"
                                      value={executionInputs[field.key] || ''}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setExecutionInputs(prev => ({ ...prev, [field.key]: val }));
                                        setVideoUploadStatus({ uploading: false });
                                      }}
                                      size="small"
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, mb: 0.5 }}>
                                      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                                      <Typography variant="caption" color="text.secondary" sx={{ px: 2 }}>
                                        or upload a file
                                      </Typography>
                                      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                                    </Box>
                                    <Button
                                      variant="outlined"
                                      component="label"
                                      fullWidth
                                      disabled={videoUploadStatus.uploading}
                                      startIcon={videoUploadStatus.uploading ? <CircularProgress size={16} /> : undefined}
                                      sx={{ textTransform: 'none' }}
                                    >
                                      {videoUploadStatus.uploading ? 'Uploading...' : videoUploadStatus.fileName ? `✓ ${videoUploadStatus.fileName}` : '🎥 Choose Video File'}
                                      <input
                                        type="file"
                                        hidden
                                        accept="video/*"
                                        onChange={async (e) => {
                                          const file = e.target.files?.[0];
                                          if (!file) return;
                                          const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
                                          setVideoUploadStatus({ uploading: true, fileName: file.name, fileSize: `${sizeMB} MB` });
                                          try {
                                            const token = await getToken();
                                            const formData = new FormData();
                                            formData.append('file', file);
                                            const resp = await fetch('/api/v2/temp/upload', {
                                              method: 'POST',
                                              headers: { 'Authorization': `Bearer ${token}` },
                                              body: formData,
                                            });
                                            if (!resp.ok) throw new Error(`Upload failed (${resp.status})`);
                                            const result = await resp.json();
                                            const fileUrl = result.public_url || result.gcs_url || result.url;
                                            if (fileUrl) {
                                              setExecutionInputs(prev => ({ ...prev, video_url: fileUrl }));
                                              setVideoUploadStatus({ uploading: false, fileName: file.name, fileSize: `${sizeMB} MB` });
                                            } else {
                                              throw new Error('No URL returned from upload');
                                            }
                                          } catch (err: any) {
                                            setVideoUploadStatus({ uploading: false, error: err.message || 'Upload failed' });
                                          }
                                          e.target.value = '';
                                        }}
                                      />
                                    </Button>
                                  </Box>
                                )}
                              </Box>
                            );
                          }

                          if (fieldType === 'boolean') {
                            const checked = executionInputs[field.key] != null
                              ? executionInputs[field.key] === true || executionInputs[field.key] === 'true'
                              : (field.default === true || field.default === 'true');
                            return (
                              <FormControlLabel
                                key={field.key}
                                sx={{ mt: 2, ml: 0 }}
                                control={
                                  <Switch
                                    checked={checked}
                                    onChange={(e) => { const v = e.target.checked; setExecutionInputs(prev => ({ ...prev, [field.key]: v })); }}
                                  />
                                }
                                label={field.label || field.key}
                              />
                            );
                          }
                          if (fieldType === 'select' && Array.isArray(field.options)) {
                            return (
                              <TextField
                                key={field.key}
                                select
                                fullWidth
                                label={fieldLabel}
                                value={executionInputs[field.key] ?? (field.default || field.options[0]?.value || '')}
                                onChange={(e) => { const v = e.target.value; setExecutionInputs(prev => ({ ...prev, [field.key]: v })); }}
                                sx={{ mt: 2 }}
                                size="small"
                              >
                                {field.options.map((opt: any) => (
                                  <MenuItem key={opt.value} value={opt.value}>{opt.label || opt.value}</MenuItem>
                                ))}
                              </TextField>
                            );
                          }
                          if (fieldType === 'upload') {
                            const textKey = field.text_key || field.key;
                            const fileKey = field.file_key;
                            const dest = fileKey || field.key;
                            const stored = uploadedFiles[dest];
                            const files = Array.isArray(stored) ? stored : (stored ? [stored] : []);
                            return (
                              <RuntimeUploadField
                                key={field.key}
                                label={field.label || field.key}
                                required={isRequired}
                                placeholder={field.placeholder}
                                accept={field.accept}
                                maxFiles={Number(field.max_files || 1)}
                                paste={field.paste !== false}
                                maxSizeMb={Number(field.max_size_mb || 100)}
                                textValue={executionInputs[textKey] || ''}
                                files={files}
                                onTextChange={(text) => setExecutionInputs(prev => ({ ...prev, [textKey]: text }))}
                                onFilesChange={(nextFiles) => {
                                  setUploadedFiles(prev => {
                                    const copy = { ...prev };
                                    if (nextFiles.length === 0) delete copy[dest];
                                    else if (nextFiles.length === 1) copy[dest] = nextFiles[0];
                                    else copy[dest] = nextFiles;
                                    return copy;
                                  });
                                }}
                                onError={(msg) => setError(msg)}
                              />
                            );
                          }
                          if (fieldType === 'textarea') {
                            return (
                              <TextField
                                key={field.key}
                                fullWidth
                                multiline
                                rows={3}
                                label={fieldLabel}
                                placeholder={field.placeholder || ''}
                                required={isRequired}
                                value={executionInputs[field.key] || ''}
                                onChange={(e) => { const v = e.target.value; setExecutionInputs(prev => ({ ...prev, [field.key]: v })); }}
                                sx={{ mt: 2 }}
                              />
                            );
                          }
                          if (fieldType === 'number') {
                            return (
                              <TextField
                                key={field.key}
                                fullWidth
                                type="number"
                                label={fieldLabel}
                                placeholder={field.placeholder || ''}
                                required={isRequired}
                                value={executionInputs[field.key] ?? (field.default != null ? field.default : '')}
                                onChange={(e) => { const v = e.target.value ? Number(e.target.value) : ''; setExecutionInputs(prev => ({ ...prev, [field.key]: v })); }}
                                sx={{ mt: 2 }}
                                inputProps={{ min: 1 }}
                              />
                            );
                          }
                          return (
                            <TextField
                              key={field.key}
                              fullWidth
                              label={fieldLabel}
                              placeholder={field.placeholder || ''}
                              required={isRequired}
                              type={fieldType === 'url' ? 'url' : 'text'}
                              value={executionInputs[field.key] || ''}
                              onChange={(e) => { const v = e.target.value; setExecutionInputs(prev => ({ ...prev, [field.key]: v })); }}
                              sx={{ mt: 2 }}
                              multiline={fieldType === 'text' && field.key.includes('content')}
                              rows={fieldType === 'text' && field.key.includes('content') ? 4 : undefined}
                            />
                          );
                        })}
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label="Additional Instructions (optional)"
                          placeholder="Any extra notes or instructions for this run..."
                          value={executionInputs['additional_instructions'] || ''}
                          onChange={(e) => { const v = e.target.value; setExecutionInputs(prev => ({ ...prev, additional_instructions: v })); }}
                          sx={{ mt: 2 }}
                        />
                      </Box>
                    );
                  }
                  return needsReferenceImages ? null : textInputs.map(type => renderTextInput(type));
                })()}

                {/* Attach Images — skip when a runtime upload already accepts images */}
                {!needsReferenceImages && !hideAttachImages && (
                  <Box sx={{ mt: 2 }}>
                    <Box
                      onClick={() => setShowAttachImages((v: boolean) => !v)}
                      sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none', gap: 0.5 }}
                    >
                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        🖼️ Attach Images
                        <Typography component="span" variant="caption" color="text.secondary">(optional — provide images for the chain to use)</Typography>
                      </Typography>
                      <Typography variant="caption" sx={{ ml: 'auto' }}>{showAttachImages ? '▲' : '▼'}</Typography>
                    </Box>
                    <Collapse in={showAttachImages}>
                      <Box sx={{ mt: 1 }}>
                        <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
                          Upload images or paste URLs. These are uploaded to cloud storage and their URLs are passed to the chain as <strong>attached_image_urls</strong>.
                          Chains with a content planning step can use these instead of generating new images.
                        </Alert>
                        <Tabs
                          value={inputMethods['attached_images'] || 'file'}
                          onChange={(_, v) => setInputMethods({ ...inputMethods, attached_images: v })}
                          sx={{ mb: 1.5 }}
                        >
                          <Tab label="Upload Files" value="file" />
                          <Tab label="Paste URLs" value="url" />
                        </Tabs>

                        {(inputMethods['attached_images'] || 'file') === 'url' ? (
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Image URLs (one per line)"
                            placeholder={"https://example.com/photo1.jpg\nhttps://example.com/logo.png"}
                            value={(executionInputs['attached_image_urls'] || []).join('\n')}
                            onChange={(e) => {
                              const urls = e.target.value.split('\n').filter((u: string) => u.trim());
                              setExecutionInputs({ ...executionInputs, attached_image_urls: urls });
                              const newFiles = { ...uploadedFiles };
                              delete newFiles['attached_images'];
                              setUploadedFiles(newFiles);
                            }}
                            helperText="Enter public image URLs, one per line"
                            sx={{ mb: 1 }}
                          />
                        ) : (
                          <Box sx={{ p: 2, border: '2px dashed', borderColor: 'divider', borderRadius: 1, textAlign: 'center', bgcolor: 'grey.50' }}>
                            <input
                              accept="image/*"
                              style={{ display: 'none' }}
                              id="attached-images-upload"
                              type="file"
                              multiple
                              onChange={(e) => {
                                const selectedFiles = e.target.files;
                                if (selectedFiles && selectedFiles.length > 0) {
                                  const existing = Array.isArray(uploadedFiles['attached_images']) ? (uploadedFiles['attached_images'] as File[]) : [];
                                  const newFiles = [...existing];
                                  for (let i = 0; i < selectedFiles.length && newFiles.length < 20; i++) {
                                    const f = selectedFiles[i];
                                    if (f.size / 1024 / 1024 > 10) { setError(`Image ${f.name} exceeds 10 MB limit.`); continue; }
                                    newFiles.push(f);
                                  }
                                  setUploadedFiles({ ...uploadedFiles, attached_images: newFiles });
                                  setExecutionInputs({ ...executionInputs, attached_image_urls: [] });
                                }
                              }}
                            />
                            <label htmlFor="attached-images-upload">
                              <Button variant="outlined" component="span">
                                Add Images ({(Array.isArray(uploadedFiles['attached_images']) ? (uploadedFiles['attached_images'] as File[]).length : 0)}/20)
                              </Button>
                            </label>

                            {Array.isArray(uploadedFiles['attached_images']) && (uploadedFiles['attached_images'] as File[]).length > 0 && (
                              <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center' }}>
                                {(uploadedFiles['attached_images'] as File[]).map((file, idx) => (
                                  <Box key={idx} sx={{ position: 'relative', width: 72, height: 72, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                                    <img src={URL.createObjectURL(file)} alt={file.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const kept = (uploadedFiles['attached_images'] as File[]).filter((_, i) => i !== idx);
                                        setUploadedFiles({ ...uploadedFiles, attached_images: kept });
                                      }}
                                      sx={{ position: 'absolute', top: 1, right: 1, bgcolor: 'rgba(0,0,0,0.55)', color: 'white', p: 0.2, '&:hover': { bgcolor: 'error.main' } }}
                                    >
                                      <CloseIcon sx={{ fontSize: 13 }} />
                                    </IconButton>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                )}

                {/* Video Generation Settings */}
                {nodes.some(node => node.data?.allowed_models && hasVideoModel(node.data.allowed_models)) && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 167, 38, 0.1)', borderRadius: 1, border: '1px solid rgba(255, 167, 38, 0.3)' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      🎥 Video Generation Settings
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        select
                        label="Duration"
                        value={videoDuration}
                        onChange={(e) => setVideoDuration(Number(e.target.value))}
                        size="small"
                        sx={{ minWidth: 120 }}
                      >
                        <MenuItem value={4}>4 seconds</MenuItem>
                        <MenuItem value={5}>5 seconds</MenuItem>
                        <MenuItem value={6}>6 seconds</MenuItem>
                        <MenuItem value={7}>7 seconds</MenuItem>
                        <MenuItem value={8}>8 seconds</MenuItem>
                      </TextField>
                      <TextField
                        select
                        label="Aspect Ratio"
                        value={videoAspectRatio}
                        onChange={(e) => setVideoAspectRatio(e.target.value)}
                        size="small"
                        sx={{ minWidth: 150 }}
                      >
                        <MenuItem value="16:9">16:9 (Landscape)</MenuItem>
                        <MenuItem value="9:16">9:16 (Portrait)</MenuItem>
                        <MenuItem value="1:1">1:1 (Square)</MenuItem>
                      </TextField>
                    </Box>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      Veo supports 4-8 second videos. Generation takes 1-5 minutes.
                    </Typography>
                  </Box>
                )}

                {/* JSON / Additional data — collapsible */}
                {(hasJSON || inputTypes.size === 0) && (
                  <Accordion disableGutters elevation={0} sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', '&:before': { display: 'none' }, overflow: 'hidden' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                      <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>
                        {(() => {
                          const chainNameLower = (selectedChain?.name || '').toLowerCase();
                          const isMealPlan = chainNameLower.includes('meal plan');
                          if (isMealPlan) return 'Optional JSON (advanced)';
                          return inputTypes.size === 0 ? 'Input Data (JSON)' : 'Additional Data (JSON)';
                        })()}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={executionInput}
                        onChange={(e) => setExecutionInput(e.target.value)}
                        placeholder={(() => {
                          const chainNameLower = (selectedChain?.name || '').toLowerCase();
                          const isMealPlan = chainNameLower.includes('meal plan');
                          if (!isMealPlan) return '{"key": "value"}';
                          return 'Optional: paste JSON to override/augment your text input above.\nExample: {"days":7,"meals_per_day":4,"macro_targets":{"calories":1900,"protein_g":150,"carbs_g":180,"fat_g":60}}';
                        })()}
                        sx={{ fontFamily: 'monospace' }}
                      />
                    </AccordionDetails>
                  </Accordion>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        
        {/* Upload Progress Indicator */}
        {loading && uploadProgress && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Alert severity="info" sx={{ mb: 0 }}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Uploading images to cloud storage ({uploadProgress.current}/{uploadProgress.total})
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {uploadProgress.fileName}
                </Typography>
                <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1, height: 8 }}>
                  <Box 
                    sx={{ 
                      width: `${(uploadProgress.current / uploadProgress.total) * 100}%`, 
                      bgcolor: 'primary.main', 
                      borderRadius: 1, 
                      height: 8,
                      transition: 'width 0.3s ease'
                    }} 
                  />
                </Box>
              </Box>
            </Alert>
          </Box>
        )}
        
        <DialogActions>
          <Button onClick={() => setExecuteDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          {(() => {
            const runtimeInputsCfg2 = (selectedChain as any)?.config?.runtime_inputs;
            const needsRefImgs2 = !!(selectedChain && (
              ((selectedChain as any).config?.ui_type === 'video') ||
              (Array.isArray(runtimeInputsCfg2) && runtimeInputsCfg2.some((f: any) =>
                ['attached_image_urls', 'manual_image_urls', 'reference_images'].includes(f.key) && f.required
              ))
            ));
            const isVPC = !needsRefImgs2 &&
              Array.isArray(runtimeInputsCfg2) &&
              runtimeInputsCfg2.some((f: any) => f.key === 'video_url');
            const videoMissing = isVPC && !executionInputs.video_url?.trim();
            return (
              <Tooltip title={videoMissing ? 'Please upload a video file or paste a video URL before executing' : ''} arrow>
                <span>
                  <Button onClick={executeChain} variant="contained" disabled={loading || videoMissing}>
                    {loading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={20} />
                        <span>
                          {uploadProgress 
                            ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...`
                            : Object.keys(uploadedFiles).length > 0 
                              ? 'Preparing uploads...' 
                              : 'Starting...'}
                        </span>
                      </Box>
                    ) : 'Execute'}
                  </Button>
                </span>
              </Tooltip>
            );
          })()}
        </DialogActions>
      </Dialog>

      {/* Templates Dialog */}
      <Dialog open={templatesDialogOpen} onClose={() => setTemplatesDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Choose a Template</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Start with a pre-built workflow template. Templates will be loaded as a copy that you can customize and save.
          </Typography>
          {templates.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                No templates available. Run database migrations to add public chain templates.
              </Typography>
            </Box>
          ) : (
            <List>
              {templates.map((template) => {
                const nodeCount = template.graph_data?.nodes?.length || 0;
                const hasPlugins = template.graph_data?.nodes?.some((n: any) => n.functionId?.startsWith('plugin:'));
                // Detect video chains - these generate multiple videos and use significant credits
                const isVideoChain = (template as any).config?.ui_type === 'video' ||
                  template.name?.toLowerCase().includes('video') ||
                  template.description?.toLowerCase().includes('video') ||
                  template.description?.toLowerCase().includes('reference images');
                
                return (
                  <Box key={template.id}>
                    <Tooltip
                      title={template.description && template.description.length > 100 ? (
                        <Box sx={{ p: 1, maxWidth: 400 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {template.name}
                          </Typography>
                          <Typography variant="caption" sx={{ lineHeight: 1.4 }}>
                            {template.description}
                          </Typography>
                        </Box>
                      ) : ''}
                      arrow
                      placement="right"
                      enterDelay={300}
                    >
                      <ListItemButton
                        onClick={() => loadTemplate(template)}
                        sx={{
                          border: '1px solid',
                          borderColor: isVideoChain ? 'warning.main' : 'divider',
                          borderRadius: 1,
                          mb: isVideoChain ? 0 : 1,
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              {template.name}
                              <Chip label={`${nodeCount} nodes`} size="small" variant="outlined" />
                              {hasPlugins && <Chip label="🔌 Plugins" size="small" color="secondary" variant="outlined" />}
                              {isVideoChain && <Chip label="🎬 Video" size="small" color="warning" />}
                            </Box>
                          }
                          secondary={
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {template.description}
                            </Typography>
                          }
                          primaryTypographyProps={{ fontWeight: 'bold' }}
                        />
                      </ListItemButton>
                    </Tooltip>
                    {isVideoChain && (
                      <Box sx={{ 
                        bgcolor: 'warning.light', 
                        color: 'warning.contrastText',
                        px: 2, 
                        py: 1, 
                        mb: 1, 
                        borderRadius: '0 0 4px 4px',
                        borderLeft: '1px solid',
                        borderRight: '1px solid',
                        borderBottom: '1px solid',
                        borderColor: 'warning.main',
                        mt: '-1px'
                      }}>
                        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          ⚠️ <strong>Credit Usage Warning:</strong> Video generation chains create multiple video clips and can use <strong>5,000+ AI credits</strong> per run depending on video length and number of scenes.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplatesDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Node Configuration Dialog */}
      <Dialog open={nodeConfigOpen} onClose={() => setNodeConfigOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Node Configuration
          {selectedNodeId && (
            <IconButton
              onClick={() => {
                setNodes((nds) => nds.filter((n) => n.id !== selectedNodeId));
                setEdges((eds) => eds.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId));
                setNodeConfigOpen(false);
                setSelectedNodeId(null);
              }}
              sx={{ position: 'absolute', right: 48, top: 8 }}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedNodeId && nodes.find(n => n.id === selectedNodeId) && (() => {
            const node = nodes.find(n => n.id === selectedNodeId)!;
            const func = functions.find(f => f.id === node.data.functionId);
            
            return (
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {node.data.functionName}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {node.data.functionDescription}
                </Typography>

                {!node.data.isPlugin && (
                  <>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Instructions
                      {loadingNodeInstructions && <CircularProgress size={14} />}
                    </Typography>
                    {nodeInstructions ? (
                      <Box sx={{
                        bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 2,
                        maxHeight: 200, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap',
                        userSelect: 'text', cursor: 'text',
                      }}>
                        {nodeInstructions}
                      </Box>
                    ) : !loadingNodeInstructions ? (
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                        No instructions available
                      </Typography>
                    ) : null}
                  </>
                )}
                
                <Divider sx={{ my: 2 }} />

                {(node.data.inputTypes || []).length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      Accepts
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      {(node.data.inputTypes || []).map((type: string) => (
                        <Chip key={type} label={type} size="small" />
                      ))}
                    </Box>
                  </>
                )}

                {/* Plugin nodes: show structured usage info (inputs, output, example) */}
                {node.data.isPlugin && func && (
                  <Box sx={{ mb: 2 }}>
                    <PluginUsageInfo plugin={func as any} variant="full" maxExampleHeight={260} />
                  </Box>
                )}

                {/* Input Schema (for AI functions with explicit schema) */}
                {!node.data.isPlugin && func && (func as any).input_schema && (
                  <Box sx={{ mb: 2 }}>
                    <SchemaView
                      schema={(func as any).input_schema}
                      title="📥 Input Schema"
                      name="input"
                      views={['structure', 'example', 'schema']}
                      maxHeight={250}
                    />
                  </Box>
                )}
                
                {!node.data.isPlugin && (() => {
                  const format = node.data.responseFormat;
                  const isList = format?.type === 'array' || !!format?.is_list;
                  const obj = format?.object_id
                    ? customObjects.find(o => o.id === format.object_id)
                    : format?.object_name
                    ? customObjects.find(o => o.name === format.object_name)
                    : undefined;
                  const objectId: string | undefined = format?.object_id || obj?.id;

                  if (objectId) {
                    const label = obj?.display_name || obj?.name || format?.object_name;
                    return (
                      <Box sx={{ mb: 2 }}>
                        <ObjectSchemaView
                          objectId={objectId}
                          fallbackSchema={obj?.fields?.length ? objectToJsonSchema(obj, customObjects) : null}
                          isList={isList}
                          name={obj?.name || format?.object_name}
                          title={`📤 Output${label ? `: ${isList ? 'List of ' : ''}${label}` : ''}`}
                          maxHeight={320}
                        />
                      </Box>
                    );
                  }

                  const isPluginFunction = typeof node.data.functionId === 'string' && node.data.functionId.startsWith('plugin:');
                  const functionExamples = (func as any)?.examples;
                  const outputType = format?.output_type || (func as any)?.output_type;
                  const simpleExamples: Record<string, any> = {
                    'text': 'Example text output',
                    'string': 'Example string',
                    'json': { key: 'value' },
                    'integer': 42,
                    'number': 3.14,
                    'boolean': true
                  };
                  let example: any;
                  if (Array.isArray(functionExamples) && functionExamples.length > 0) {
                    example = functionExamples[0].output;
                  } else if (format?.type && !format.object_name) {
                    const value = simpleExamples[format.type] ?? format.type;
                    example = format.is_list ? [value] : value;
                  }

                  return (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        📤 Output Schema
                      </Typography>
                      <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1, maxHeight: 280, overflow: 'auto' }}>
                        {format?.object_name ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {loadingNodeSchema && <CircularProgress size={12} />}
                            <Typography variant="caption" color="text.secondary">
                              {loadingNodeSchema
                                ? `Loading ${format.object_name} schema...`
                                : `Could not load the ${format.object_name} object. It may not exist or may be private.`}
                            </Typography>
                          </Box>
                        ) : format?.type ? (
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            Output type: <strong>{format.type}</strong>
                            {format.is_list && ' (array)'}
                          </Typography>
                        ) : outputType ? (
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            Output type: {outputType}
                          </Typography>
                        ) : format ? (
                          <Typography variant="caption" component="pre" sx={{ fontSize: '0.7rem', whiteSpace: 'pre-wrap' }}>
                            {JSON.stringify(format, null, 2)}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No output schema defined
                          </Typography>
                        )}
                      </Box>

                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                        Example Output
                      </Typography>
                      <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
                        {isPluginFunction ? (
                          <Typography variant="caption" color="text.secondary">
                            Plugin output varies by execution context.
                          </Typography>
                        ) : example !== undefined ? (
                          <Typography variant="caption" component="pre" sx={{ fontSize: '0.7rem', whiteSpace: 'pre-wrap' }}>
                            {JSON.stringify(example, null, 2)}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Example not available.
                          </Typography>
                        )}
                      </Box>
                    </>
                  );
                })()}

                
                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <StepInputFields
                    value={node.data.config?.inputTemplate ?? node.data.inputTemplate}
                    showJsonEditor={false}
                    fields={
                      node.data.isPlugin && func
                        ? ((func as any).usage_info?.inputs
                          || [
                            ...(((func as any).required_params || []).map((n: string) => ({ name: n, required: true }))),
                            ...(((func as any).optional_params || []).map((n: string) => ({ name: n, required: false }))),
                          ])
                        : undefined
                    }
                    hideKeys={isLlmBatchPlugin(node.data.functionId) ? ['model', 'max_tokens', 'reasoning_effort'] : []}
                    onChange={(next) => {
                      const tpl = Object.keys(next).length ? next : null;
                      setNodes((nds) =>
                        nds.map((n) =>
                          n.id === selectedNodeId
                            ? {
                                ...n,
                                data: {
                                  ...n.data,
                                  inputTemplate: tpl,
                                  config: { ...n.data.config, inputTemplate: tpl },
                                },
                              }
                            : n
                        )
                      );
                    }}
                  />
                </Box>
                
                {/* Advanced: Custom Input Template - collapsed by default */}
                <Accordion 
                  defaultExpanded={!!node.data.inputTemplate}
                  sx={{ 
                    mb: 2, 
                    '&:before': { display: 'none' },
                    boxShadow: 1,
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box>
                      <Typography variant="subtitle2">
                        📝 Advanced: Custom Input Template
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Override how data flows into this node (optional)
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        When to use this:
                      </Typography>
                      <Typography variant="caption" component="div">
                        • Combine outputs from multiple previous nodes into one input
                      </Typography>
                      <Typography variant="caption" component="div">
                        • Extract specific fields from complex outputs
                      </Typography>
                      <Typography variant="caption" component="div">
                        • Restructure data for a specific function format
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1.5, mb: 0.5 }}>
                        Template syntax:
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                        • <code>{'{{input.field}}'}</code> - Get field from chain's initial input
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                        • <code>{'{{node-id.field}}'}</code> - Get field from another node's output
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                        • <code>{'{{node-id}}'}</code> - Get entire output from another node
                      </Typography>
                    </Alert>
                    
                    <TextField
                      fullWidth
                      multiline
                      rows={8}
                      label="Input Template (JSON)"
                      placeholder={`Example: Combine transcript and summary into one input

{
  "transcript": {{node-transcribe.text}},
  "summary": {{node-summary.summary}},
  "original_url": {{input.url}}
}`}
                      value={typeof node.data.inputTemplate === 'string' 
                        ? node.data.inputTemplate 
                        : node.data.inputTemplate 
                          ? JSON.stringify(node.data.inputTemplate, null, 2) 
                          : ''}
                      onChange={(e) => {
                        setNodes((nds) =>
                          nds.map((n) =>
                            n.id === selectedNodeId
                              ? { ...n, data: { ...n.data, inputTemplate: e.target.value } }
                              : n
                          )
                        );
                      }}
                      sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                      helperText="⚠️ Do NOT put quotes around {{placeholders}} - they're auto-quoted. Leave empty to use default data flow."
                    />
                  </AccordionDetails>
                </Accordion>

                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle2" gutterBottom>
                  Execution Options
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={node.data.config?.mapOverArray || false}
                      onChange={(e) => {
                        setNodes((nds) =>
                          nds.map((n) =>
                            n.id === selectedNodeId
                              ? { ...n, data: { ...n.data, config: { ...n.data.config, mapOverArray: e.target.checked } } }
                              : n
                          )
                        );
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Map Over Array</Typography>
                      <Typography variant="caption" color="text.secondary">
                        If previous node outputs an array, run this function once for each item
                      </Typography>
                    </Box>
                  }
                />

                {node.data.config?.mapOverArray && (
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField
                      label="Max Items"
                      type="number"
                      size="small"
                      value={node.data.config?.maxMappedItems ?? ''}
                      onChange={(e) => {
                        const val = e.target.value ? parseInt(e.target.value, 10) : undefined;
                        setNodes((nds) =>
                          nds.map((n) =>
                            n.id === selectedNodeId
                              ? { ...n, data: { ...n.data, config: { ...n.data.config, maxMappedItems: val } } }
                              : n
                          )
                        );
                      }}
                      placeholder="Default: 10 for image/video"
                      helperText="Limit how many items are processed. Prevents runaway costs for expensive models."
                      InputProps={{ inputProps: { min: 1, max: 100 } }}
                      sx={{ maxWidth: 260 }}
                    />
                    <Tooltip title="Only process array items where this field has a value. Items without it are passed through unchanged, keeping the full array intact." arrow placement="top-start">
                      <TextField
                        label="Skip items where field is empty"
                        size="small"
                        value={node.data.config?.skipItemWhenEmpty ?? ''}
                        onChange={(e) => {
                          const val = e.target.value || undefined;
                          setNodes((nds) =>
                            nds.map((n) =>
                              n.id === selectedNodeId
                                ? { ...n, data: { ...n.data, config: { ...n.data.config, skipItemWhenEmpty: val } } }
                                : n
                            )
                          );
                        }}
                        placeholder="e.g. image_prompt"
                        helperText="Skip items missing this field (saves credits on selective processing)"
                        sx={{ maxWidth: 260 }}
                      />
                    </Tooltip>
                  </Box>
                )}

                {/* Google Search grounding — only for Gemini models */}
                {func?.allowed_models?.some((m: string) => m.startsWith('gemini')) && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={node.data.config?.enableGoogleSearch || false}
                        onChange={(e) => {
                          setNodes((nds) =>
                            nds.map((n) =>
                              n.id === selectedNodeId
                                ? { ...n, data: { ...n.data, config: { ...n.data.config, enableGoogleSearch: e.target.checked } } }
                                : n
                            )
                          );
                        }}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Enable Google Search</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Let the AI search the web for current information. Billed per search query.
                        </Typography>
                      </Box>
                    }
                  />
                )}

                {/* Video Generation Settings - only show for video generation models */}
                {func?.allowed_models && hasVideoModel(func.allowed_models) && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      Video Generation Settings
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                      <TextField
                        select
                        label="Duration"
                        value={node.data.config?.videoDuration || 6}
                        onChange={(e) => {
                          setNodes((nds) =>
                            nds.map((n) =>
                              n.id === selectedNodeId
                                ? { ...n, data: { ...n.data, config: { ...n.data.config, videoDuration: Number(e.target.value) } } }
                                : n
                            )
                          );
                        }}
                        size="small"
                        sx={{ minWidth: 140 }}
                        helperText="4-8 seconds"
                      >
                        <MenuItem value={4}>4 seconds</MenuItem>
                        <MenuItem value={5}>5 seconds</MenuItem>
                        <MenuItem value={6}>6 seconds</MenuItem>
                        <MenuItem value={7}>7 seconds</MenuItem>
                        <MenuItem value={8}>8 seconds</MenuItem>
                      </TextField>
                      <TextField
                        select
                        label="Aspect Ratio"
                        value={node.data.config?.videoAspectRatio || '16:9'}
                        onChange={(e) => {
                          setNodes((nds) =>
                            nds.map((n) =>
                              n.id === selectedNodeId
                                ? { ...n, data: { ...n.data, config: { ...n.data.config, videoAspectRatio: e.target.value } } }
                                : n
                            )
                          );
                        }}
                        size="small"
                        sx={{ minWidth: 180 }}
                      >
                        <MenuItem value="16:9">16:9 (Landscape)</MenuItem>
                        <MenuItem value="9:16">9:16 (Portrait)</MenuItem>
                        <MenuItem value="1:1">1:1 (Square)</MenuItem>
                      </TextField>
                    </Box>
                  </>
                )}
                
                {isLlmBatchPlugin(node.data.functionId) && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    {(() => {
                      const tpl = parseInputTemplate(node.data.config?.inputTemplate ?? node.data.inputTemplate);
                      return (
                    <BatchJobSettings
                      model={node.data.config?.modelOverride || tpl.model || 'gpt-5.4-mini'}
                      maxTokens={tpl.max_tokens ?? 8192}
                      reasoningEffort={tpl.reasoning_effort ?? 'low'}
                      onChange={(next) => {
                        const nextTpl = { ...tpl };
                        nextTpl.model = next.model;
                        nextTpl.max_tokens = next.maxTokens;
                        if (next.reasoningEffort) nextTpl.reasoning_effort = next.reasoningEffort;
                        else delete nextTpl.reasoning_effort;
                        setNodes((nds) =>
                          nds.map((n) =>
                            n.id === selectedNodeId
                              ? {
                                  ...n,
                                  data: {
                                    ...n.data,
                                    inputTemplate: nextTpl,
                                    config: { ...n.data.config, modelOverride: next.model, inputTemplate: nextTpl },
                                  },
                                }
                              : n
                          )
                        );
                      }}
                    />
                      );
                    })()}
                  </>
                )}

                {/* AI Model & Generation Settings - for non-plugin AI functions */}
                {!node.data.isPlugin && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      🤖 AI Model Settings
                    </Typography>
                    
                    {/* Model Selection */}
                    <Box sx={{ mb: 2 }}>
                      <ModelSelector
                        value={node.data.config?.modelOverride || func?.allowed_models?.[0] || 'gpt-4o'}
                        onChange={(val) => {
                          setNodes((nds) =>
                            nds.map((n) =>
                              n.id === selectedNodeId
                                ? { ...n, data: { ...n.data, config: { ...n.data.config, modelOverride: (val as string) || null } } }
                                : n
                            )
                          );
                        }}
                        size="small"
                        label="Model"
                        helperText="Override the AI model for this node (default from function settings)"
                      />
                    </Box>
                    
                    <TemperatureSlider
                      value={node.data.config?.temperature}
                      model={node.data.config?.modelOverride || func?.allowed_models?.[0]}
                      onChange={(value) => {
                        setNodes((nds) =>
                          nds.map((n) =>
                            n.id === selectedNodeId
                              ? { ...n, data: { ...n.data, config: { ...n.data.config, temperature: value } } }
                              : n
                          )
                        );
                      }}
                    />

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      🛡️ Reliability & limits
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <StepReliability
                        functionId={node.data.functionId}
                        functionName={node.data.functionName}
                        chainDesc={chainReliability}
                      />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        label="Per-attempt limit (seconds)"
                        type="number"
                        value={node.data.config?.timeoutSeconds ?? ''}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const val = raw === '' ? undefined : Number(raw);
                          setNodes((nds) =>
                            nds.map((n) =>
                              n.id === selectedNodeId
                                ? { ...n, data: { ...n.data, config: { ...n.data.config, timeoutSeconds: val } } }
                                : n
                            )
                          );
                        }}
                        size="small"
                        fullWidth
                        inputProps={{ min: 10, max: 180 }}
                        placeholder="Default: 180"
                        helperText="Longest a single model try in this step may take before it counts as failed and the reliability rules above (retry or backup model) take over. 10–180s, blank = 180s. The overall time limit is set under Reliability."
                      />
                    </Box>

                    {/* Knowledge Base Categories */}
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      🔍 Knowledge Base (RAG)
                    </Typography>
                    <NodeKnowledgeSettings
                      config={node.data.config}
                      categories={availableCategories}
                      onChange={(patch) => setNodes((nds) =>
                        nds.map((n) =>
                          n.id === selectedNodeId
                            ? { ...n, data: { ...n.data, config: { ...n.data.config, ...patch } } }
                            : n
                        )
                      )}
                    />
                  </>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNodeConfigOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Rerun Adjustment Notes Dialog */}
      <Dialog open={rerunDialogOpen} onClose={() => setRerunDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.50', borderBottom: '2px solid', borderColor: 'primary.main', pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReplayIcon color="primary" sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 700 }}>Rerun from Step</Typography>
              {rerunNodeId && (
                <Typography variant="caption" color="text.secondary">
                  Starting from: <strong>{(() => {
                    const gd = (() => { try { return typeof selectedChain?.graph_data === 'string' ? JSON.parse(selectedChain.graph_data) : selectedChain?.graph_data; } catch { return null; } })();
                    const node = gd?.nodes?.find((n: any) => n.id === rerunNodeId);
                    return node?.data?.functionName || node?.data?.label || rerunNodeId;
                  })()}</strong>
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A new execution will copy all prior step results and re-execute from the selected step onwards.
          </Typography>

          {/* Include previous output toggle */}
          {rerunNodeId && execution?.node_results?.[rerunNodeId]?.output && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={rerunIncludeOutput}
                  onChange={(e) => setRerunIncludeOutput(e.target.checked)}
                />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  Include <strong>{(() => {
                    const gd = (() => { try { return typeof selectedChain?.graph_data === 'string' ? JSON.parse(selectedChain.graph_data) : selectedChain?.graph_data; } catch { return null; } })();
                    const node = gd?.nodes?.find((n: any) => n.id === rerunNodeId);
                    return node?.data?.functionName || node?.data?.label || rerunNodeId;
                  })()}</strong>'s output from this run in the note — so you can describe edits to what it already produced
                </Typography>
              }
              sx={{ mb: 1.5, ml: 0 }}
            />
          )}
          {rerunIncludeOutput && rerunNodeId && execution?.node_results?.[rerunNodeId]?.output && (
            <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }} variant="outlined">
              <Typography variant="caption">
                The step's previous output ({(() => {
                  const raw = JSON.stringify(execution.node_results[rerunNodeId].output);
                  return raw.length > 100 ? `${(raw.length / 1024).toFixed(1)}KB` : `${raw.length} chars`;
                })()}) will be prepended to your notes so the AI sees what it produced last time and can apply your edits.
              </Typography>
            </Alert>
          )}

          <Box sx={{
            border: '2px solid', borderColor: rerunNotes ? 'info.main' : 'divider',
            borderRadius: 2, p: 0.5, bgcolor: rerunNotes ? 'info.50' : 'transparent',
            transition: 'all 0.2s ease',
          }}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={10}
              label="Adjustment Notes for AI"
              placeholder="e.g. Use a warmer tone, pick different images, focus on budget options, change the color scheme..."
              value={rerunNotes}
              onChange={(e) => setRerunNotes(e.target.value)}
              helperText="These notes are injected into the AI prompt as high-priority instructions for this run."
              sx={{
                '& .MuiOutlinedInput-root': { bgcolor: 'white' },
                '& .MuiFormHelperText-root': { fontSize: '0.7rem', mt: 0.5 },
              }}
            />
          </Box>

          {/* Model override for this step */}
          {rerunNodeId && (() => {
            const gd = (() => { try { return typeof selectedChain?.graph_data === 'string' ? JSON.parse(selectedChain.graph_data) : selectedChain?.graph_data; } catch { return null; } })();
            const graphNode = gd?.nodes?.find((n: any) => n.id === rerunNodeId);
            const funcId = graphNode?.functionId || graphNode?.data?.functionId || '';
            const isPlugin = graphNode?.isPlugin || graphNode?.data?.isPlugin || (typeof funcId === 'string' && funcId.startsWith('plugin:'));
            if (isPlugin && !isLlmBatchPlugin(funcId)) return null;
            const nodeResult = execution?.node_results?.[rerunNodeId];
            const actualModelUsed = nodeResult?.modelUsed;
            const cfg = graphNode?.config || graphNode?.data?.config || {};
            const currentModel = actualModelUsed || cfg?.modelOverride || graphNode?.allowed_models?.[0] || graphNode?.data?.allowed_models?.[0] || '';
            return (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Model override for this step (optional)
                </Typography>
                <ModelSelector
                  value={rerunModelOverride || currentModel || 'gpt-4o'}
                  onChange={(v) => setRerunModelOverride((v as string) || '')}
                  label="Model"
                  size="small"
                />
                {rerunModelOverride && rerunModelOverride !== currentModel && (
                  <Typography variant="caption" sx={{ color: 'warning.main', mt: 0.5, display: 'block' }}>
                    Overriding from <strong>{currentModel || 'default'}</strong> to <strong>{rerunModelOverride}</strong> — this does not save to the chain.
                  </Typography>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRerunDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            size="large"
            startIcon={<ReplayIcon />}
            onClick={async () => {
              if (!execution?.id || !rerunNodeId) return;
              try {
                let finalNotes = rerunNotes || '';
                if (rerunIncludeOutput && rerunNodeId && execution?.node_results?.[rerunNodeId]?.output) {
                  const prevOutput = JSON.stringify(execution.node_results[rerunNodeId].output, null, 2);
                  const truncated = prevOutput.length > 12000 ? prevOutput.slice(0, 12000) + '\n... (truncated)' : prevOutput;
                  finalNotes = `PREVIOUS OUTPUT FROM THIS STEP (use as reference, apply the edits described below):\n${truncated}\n\nUSER EDITS:\n${finalNotes}`;
                }
                const effectiveModel = rerunModelOverride || (() => {
                  const gd = (() => { try { return typeof selectedChain?.graph_data === 'string' ? JSON.parse(selectedChain.graph_data) : selectedChain?.graph_data; } catch { return null; } })();
                  const gn = gd?.nodes?.find((n: any) => n.id === rerunNodeId);
                  const nr = execution?.node_results?.[rerunNodeId];
                  return nr?.modelUsed || (gn?.config || gn?.data?.config || {})?.modelOverride || '';
                })();
                const nodeOverrides: Record<string, any> | undefined = effectiveModel ? { [rerunNodeId]: { modelOverride: effectiveModel } } : undefined;
                const token = await user?.getIdToken();
                const res = await fetch(`/api/v2/chains/executions/${execution.id}/rerun-from/${rerunNodeId}`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ adjustment_notes: finalNotes || null, node_overrides: nodeOverrides }),
                });
                if (!res.ok) { const err = await res.json().catch(() => ({})); alert(err.detail || 'Rerun failed'); return; }
                const newExec = await res.json();
                setExecution(newExec);
                setRerunDialogOpen(false);
                setRerunNotes('');
                setRerunIncludeOutput(false);
                setRerunModelOverride('');
                setSelectedResultNode(null);
                pollExecution(newExec.id);
              } catch (e) { console.error('Rerun failed:', e); }
            }}
            sx={{ fontWeight: 700, px: 3 }}
          >
            Start Rerun
          </Button>
        </DialogActions>
      </Dialog>

      {/* Execution Results Dialog — split-panel layout */}
      <Dialog
        open={execution !== null}
        onClose={() => !executionPolling && setExecution(null)}
        maxWidth={false}
        PaperProps={{ sx: { width: '95vw', maxWidth: 1200, height: '85vh', maxHeight: 900 } }}
      >
        <DialogTitle sx={{ pb: 0.5, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {execution?.status === 'running' || execution?.status === 'pending' ? (
              <CircularProgress size={22} />
            ) : execution?.status === 'paused' ? (
              <PauseIcon color="warning" />
            ) : execution?.status === 'completed' ? (
              <CheckIcon color="success" />
            ) : (
              <CloseIcon color="error" />
            )}
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              {execution?.status === 'running' ? 'Running...' : execution?.status === 'pending' ? 'Starting...' : execution?.status === 'paused' ? 'Paused' : execution?.status === 'cancelled' ? 'Cancelled' : 'Results'}
            </Typography>
            {execution?.status !== 'running' && execution?.status !== 'pending' && execution?.execution_time_ms && (
              <Chip label={formatDuration(execution.execution_time_ms)} size="small" variant="outlined" />
            )}
            {(() => {
              const totalCredits = Object.values(execution?.node_results || {}).reduce((sum: number, nr: any) => {
                const v = Number(nr?.aiCreditsUsed ?? 0);
                return sum + (Number.isFinite(v) ? v : 0);
              }, 0);
              if (totalCredits <= 0) return null;
              return <Chip label={`${totalCredits.toFixed(2)} credits`} size="small" variant="outlined" />;
            })()}
            {selectedChain && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{selectedChain.name}</Typography>
            )}
          </Box>
          {execution?.status === 'cancelled' && (
            <Alert severity="warning" sx={{ mt: 1, py: 0 }} variant="outlined">Execution was cancelled.</Alert>
          )}
          {execution?.error && execution?.status !== 'cancelled' && (
            <Alert severity="error" sx={{ mt: 1, py: 0 }} variant="outlined">{execution.error}</Alert>
          )}
          {execution?.source_execution_id && (
            <Alert severity="info" icon={<ReplayIcon fontSize="small" />} sx={{ mt: 1, py: 0 }} variant="outlined">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Rerun from: {(() => {
                    const gd = (() => { try { return typeof selectedChain?.graph_data === 'string' ? JSON.parse(selectedChain.graph_data) : selectedChain?.graph_data; } catch { return null; } })();
                    const node = gd?.nodes?.find((n: any) => n.id === execution.resume_from_node);
                    return node?.data?.functionName || node?.data?.label || execution.resume_from_node || 'unknown step';
                  })()}
                </Typography>
                {execution.adjustment_notes && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary', fontStyle: 'italic',
                      maxHeight: 40, overflow: 'hidden', textOverflow: 'ellipsis',
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    }}
                    title={execution.adjustment_notes}
                  >
                    Note: {execution.adjustment_notes}
                  </Typography>
                )}
              </Box>
            </Alert>
          )}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'row', p: 0, overflow: 'hidden', flex: 1 }}>
          {execution && (() => {
            // Shared data: topological order, node names
            const graphData = (() => {
              try {
                return typeof selectedChain?.graph_data === 'string'
                  ? JSON.parse(selectedChain.graph_data)
                  : selectedChain?.graph_data;
              } catch { return null; }
            })();

            const topoOrder = (() => {
              if (!graphData?.nodes || !graphData?.edges) return null;
              const ids = graphData.nodes.map((n: any) => n.id).filter(Boolean);
              const indeg: Record<string, number> = {};
              const adj: Record<string, string[]> = {};
              ids.forEach((id: string) => { indeg[id] = 0; adj[id] = []; });
              for (const e of graphData.edges) {
                if (!e?.source || !e?.target || !(e.source in adj) || !(e.target in indeg)) continue;
                adj[e.source].push(e.target);
                indeg[e.target] = (indeg[e.target] || 0) + 1;
              }
              const q: string[] = ids.filter((id: string) => (indeg[id] || 0) === 0);
              const out: string[] = [];
              while (q.length) { const id = q.shift()!; out.push(id); for (const nxt of (adj[id] || [])) { indeg[nxt] -= 1; if (indeg[nxt] === 0) q.push(nxt); } }
              return out.length ? out : ids;
            })();

            const graphNodeNameMap: Record<string, string> = {};
            if (graphData?.nodes) {
              for (const gn of graphData.nodes) {
                const fid = gn.functionId || gn.data?.functionId;
                const nameFromData = gn.data?.functionName || gn.data?.label;
                if (nameFromData) graphNodeNameMap[gn.id] = nameFromData;
                else if (fid) {
                  const fn = functions.find((f: any) => f.id === fid);
                  if (fn) graphNodeNameMap[gn.id] = fn.name || (fn as any).display_name || fid;
                  else if (fid.startsWith('plugin:')) graphNodeNameMap[gn.id] = fid.replace('plugin:', '').replace(/_/g, ' ');
                }
              }
            }

            const executedNodeIds = Object.keys(execution.node_results || {});
            const chainGraphNodeIds: string[] = (graphData?.nodes || []).map((n: any) => n.id);
            const allNodeIds = Array.from(new Set([...chainGraphNodeIds, ...executedNodeIds, ...nodes.map(n => n.id)]));
            const orderIndex: Record<string, number> = {};
            if (topoOrder) topoOrder.forEach((id: string, i: number) => { orderIndex[id] = i; });
            const sortedSteps = allNodeIds.sort((a, b) => {
              const ia = orderIndex[a]; const ib = orderIndex[b];
              if (typeof ia === 'number' && typeof ib === 'number') return ia - ib;
              if (typeof ia === 'number') return -1;
              if (typeof ib === 'number') return 1;
              return a.localeCompare(b);
            });

            const searchingSteps = isRunSearchActive(inRunSearch);
            const matchingStepIds = new Set(
              searchingSteps
                ? sortedSteps.filter((id) => {
                    const name = (graphNodeNameMap[id] || nodes.find(n => n.id === id)?.data?.functionName || id || '').toLowerCase();
                    return name.includes(inRunSearch.trim().toLowerCase())
                      || nodeMatchesQuery(execution.node_results?.[id], inRunSearch);
                  })
                : []
            );
            const inputMatchesSearch = searchingSteps && valueContains(execution.input_data, inRunSearch);
            const visibleSteps = searchingSteps && showMatchingStepsOnly
              ? sortedSteps.filter((id) => matchingStepIds.has(id))
              : sortedSteps;

            const executionDone = ['completed', 'failed', 'cancelled'].includes(execution.status);
            const isRunningOrPending = execution.status === 'running' || execution.status === 'pending';
            const isPaused = execution.status === 'paused';
            const pauseSet = new Set(execution.pause_before_nodes || []);

            // Determine selected node for right panel
            const activeNodeId = selectedResultNode && (execution.node_results?.[selectedResultNode] || sortedSteps.includes(selectedResultNode))
              ? selectedResultNode
              : sortedSteps.find(id => execution.node_results?.[id]?.status === 'running') || sortedSteps.find(id => execution.node_results?.[id]) || null;

            const activeResult = activeNodeId ? execution.node_results?.[activeNodeId] : null;
            const activeOutput = activeResult?.output;
            const activeNodeName = activeNodeId ? (graphNodeNameMap[activeNodeId] || nodes.find(n => n.id === activeNodeId)?.data?.functionName || activeNodeId) : '';

            return (
              <>
                {/* ── LEFT PANEL: Steps ── */}
                <Box sx={{
                  width: leftPanelWidth, minWidth: 200, maxWidth: 450, flexShrink: 0,
                  display: 'flex', flexDirection: 'column',
                  borderRight: '1px solid', borderColor: 'divider',
                  bgcolor: 'grey.50',
                  overflow: 'hidden',
                }}>
                  <Box sx={{ px: 1, pt: 1, pb: 0.5, flexShrink: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Find in this run…"
                      value={inRunSearch}
                      onChange={(e) => setInRunSearch(e.target.value)}
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 0.5, color: 'text.secondary', fontSize: 16 }} />,
                        endAdornment: inRunSearch ? (
                          <IconButton size="small" onClick={() => { setInRunSearch(''); setShowMatchingStepsOnly(false); }} sx={{ p: 0.25 }}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        ) : null,
                      }}
                      sx={{ '& .MuiInputBase-root': { fontSize: '0.75rem', bgcolor: 'background.paper' } }}
                    />
                    {searchingSteps && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          {matchingStepIds.size} step{matchingStepIds.size === 1 ? '' : 's'} match
                          {inputMatchesSearch ? ' · input matches' : ''}
                        </Typography>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={showMatchingStepsOnly}
                              onChange={(_, v) => setShowMatchingStepsOnly(v)}
                              sx={{ py: 0, pr: 0.5 }}
                            />
                          }
                          label={<Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Matches only</Typography>}
                          sx={{ mr: 0, ml: 0 }}
                        />
                      </Box>
                    )}
                  </Box>
                  <Box ref={progressScrollRef} sx={{ flex: 1, overflow: 'auto', py: 1, pt: 0.5 }}>
                    {visibleSteps.length === 0 ? (
                      <Box sx={{ px: 1.5, py: 2 }}>
                        <Typography variant="caption" color="text.secondary">No steps match this search.</Typography>
                      </Box>
                    ) : visibleSteps.map((nodeId) => {
                      const idx = sortedSteps.indexOf(nodeId);
                      const isMatch = matchingStepIds.has(nodeId);
                      const nodeResult = execution.node_results?.[nodeId];
                      let stepStatus = nodeResult?.status || 'pending';
                      if (executionDone && stepStatus === 'running') stepStatus = 'failed';
                      const isSelected = activeNodeId === nodeId;
                      const isPausedHere = execution.paused_at_node === nodeId;
                      const hasPauseSet = pauseSet.has(nodeId);
                      const stepName = graphNodeNameMap[nodeId] || nodes.find(n => n.id === nodeId)?.data?.functionName || nodeId;
                      const isResumePoint = execution.resume_from_node === nodeId;

                      return (
                        <Box
                          key={nodeId}
                          ref={(el: HTMLElement | null) => { stepRowRefs.current[nodeId] = el; }}
                          onClick={() => setSelectedResultNode(nodeId)}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 0.75,
                            px: 1.5, py: 0.75, mx: 0.5, mb: 0.25, borderRadius: 1.5,
                            cursor: 'pointer',
                            opacity: searchingSteps && !isMatch && !isSelected ? 0.55 : 1,
                            bgcolor: isPausedHere
                              ? 'warning.100'
                              : isSelected
                                ? 'primary.100'
                                : isMatch
                                  ? 'warning.50'
                                : stepStatus === 'running'
                                  ? 'primary.50'
                                  : 'transparent',
                            border: '2px solid',
                            borderColor: isSelected ? 'primary.main' : isMatch ? 'warning.light' : 'transparent',
                            boxShadow: isSelected ? '0 0 0 1px rgba(25,118,210,0.2)' : 'none',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              bgcolor: isPausedHere ? 'warning.100' : isSelected ? 'primary.100' : 'grey.100',
                              borderColor: isSelected ? 'primary.main' : 'grey.300',
                              opacity: 1,
                            },
                          }}
                        >
                          {stepStatus === 'running' && <CircularProgress size={16} />}
                          {stepStatus === 'completed' && <Chip label="✓" size="small" color="success" sx={{ width: 20, height: 20, '& .MuiChip-label': { px: 0 } }} />}
                          {stepStatus === 'failed' && <Chip label="✗" size="small" color="error" sx={{ width: 20, height: 20, '& .MuiChip-label': { px: 0 } }} />}
                          {isPausedHere && <PauseIcon fontSize="small" color="warning" />}
                          {stepStatus === 'pending' && !isPausedHere && <Chip label="⋯" size="small" color="default" sx={{ width: 20, height: 20, '& .MuiChip-label': { px: 0 } }} />}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" sx={{
                              fontWeight: isSelected ? 700 : stepStatus === 'running' ? 700 : 500,
                              color: isSelected ? 'primary.dark' : 'text.primary',
                              display: 'block', lineHeight: 1.3,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>{idx + 1}.</Typography>
                              {stepName}
                              {isMatch && (
                                <Chip label="match" size="small" color="warning" sx={{ height: 16, ml: 0.5, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.5 } }} />
                              )}
                              {isResumePoint && (
                                <Tooltip title={execution.adjustment_notes ? `Rerun point — Note: ${execution.adjustment_notes}` : 'Rerun started from this step'} arrow>
                                  <ReplayIcon sx={{ fontSize: 12, ml: 0.5, verticalAlign: 'middle', color: 'info.main' }} />
                                </Tooltip>
                              )}
                            </Typography>
                            {nodeResult?.executionTime && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                {formatDuration(nodeResult.executionTime)}
                                {typeof nodeResult?.aiCreditsUsed === 'number' && ` • ${nodeResult.aiCreditsUsed.toFixed(2)} cr`}
                              </Typography>
                            )}
                            {(() => {
                              const modelInfo = describeRunModel(nodeResult?.modelUsed, nodeResult?.modelRequested, nodeResult?.fallbackUsed);
                              if (!modelInfo.label) return null;
                              return (
                                <Tooltip title={modelInfo.tooltip || modelInfo.label} arrow>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {modelInfo.label}
                                  </Typography>
                                </Tooltip>
                              );
                            })()}
                            {isResumePoint && execution.adjustment_notes && (
                              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'info.main', display: 'block', lineHeight: 1.2, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                Note: {execution.adjustment_notes}
                              </Typography>
                            )}
                          </Box>
                          {/* Action buttons */}
                          <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                            {executionDone && (
                              <Tooltip title="Rerun from this step" arrow>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  startIcon={<ReplayIcon sx={{ fontSize: 14 }} />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setRerunNodeId(nodeId);
                                    setRerunNotes(isResumePoint && execution.adjustment_notes ? execution.adjustment_notes : '');
                                    setRerunIncludeOutput(false);
                                    setRerunModelOverride('');
                                    setRerunDialogOpen(true);
                                  }}
                                  sx={{
                                    minWidth: 0, px: 0.75, py: 0.25,
                                    fontSize: '0.65rem', textTransform: 'none',
                                    lineHeight: 1.2,
                                    '& .MuiButton-startIcon': { mr: 0.25 },
                                  }}
                                >
                                  Rerun
                                </Button>
                              </Tooltip>
                            )}
                            {isRunningOrPending && stepStatus === 'pending' && !isPausedHere && (
                              <Tooltip title={hasPauseSet ? 'Remove pause' : 'Pause before this step'} arrow>
                                <IconButton size="small" sx={{ p: 0.25 }} onClick={async (e) => {
                                  e.stopPropagation();
                                  const token = await user?.getIdToken();
                                  const method = hasPauseSet ? 'DELETE' : 'POST';
                                  await fetch(`/api/v2/chains/executions/${execution.id}/pause-at/${nodeId}`, {
                                    method, headers: { Authorization: `Bearer ${token}` },
                                  });
                                }}>
                                  <PauseIcon sx={{ fontSize: 16, color: hasPauseSet ? 'warning.main' : 'action.active' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Paused state: prominent continue panel */}
                  {isPaused && (
                    <Box sx={{
                      borderTop: '3px solid', borderColor: 'warning.main',
                      bgcolor: 'warning.50',
                      p: 0,
                      animation: 'pulse-border 2s ease-in-out infinite',
                      '@keyframes pulse-border': {
                        '0%, 100%': { borderTopColor: 'warning.main' },
                        '50%': { borderTopColor: 'warning.light' },
                      },
                    }}>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 2, py: 1.5,
                        bgcolor: 'warning.main', color: 'warning.contrastText',
                      }}>
                        <PauseIcon fontSize="small" />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.2 }}>
                            Execution Paused
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>
                            Before: {graphNodeNameMap[execution.paused_at_node || ''] || execution.paused_at_node}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ px: 2, py: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary', fontSize: '0.8rem' }}>
                          Review the results so far, then optionally add notes before continuing.
                        </Typography>
                        <TextField
                          fullWidth size="small" multiline minRows={2} maxRows={5}
                          label="Adjustment notes"
                          placeholder="e.g. Use a different image, remove the word 'example', change the tone to be more formal..."
                          value={continueNotes}
                          onChange={(e) => setContinueNotes(e.target.value)}
                          sx={{
                            mb: 1.5, bgcolor: 'white', borderRadius: 1,
                            '& .MuiOutlinedInput-root': { borderRadius: 1 },
                          }}
                        />
                        <Button
                          fullWidth variant="contained" color="success" size="large"
                          startIcon={<PlayArrowIcon />}
                          onClick={async () => {
                            const token = await user?.getIdToken();
                            await fetch(`/api/v2/chains/executions/${execution.id}/continue`, {
                              method: 'POST',
                              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify({ adjustment_notes: continueNotes || null }),
                            });
                            setContinueNotes('');
                          }}
                          sx={{ py: 1.25, fontWeight: 700, fontSize: '0.95rem' }}
                        >
                          Continue Execution
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* ── DRAG HANDLE ── */}
                <Box
                  sx={{ width: 4, cursor: 'col-resize', bgcolor: 'divider', '&:hover': { bgcolor: 'primary.main' }, flexShrink: 0, transition: 'background-color 0.15s' }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    isDraggingRef.current = true;
                    const startX = e.clientX;
                    const startW = leftPanelWidth;
                    const onMove = (ev: MouseEvent) => { if (isDraggingRef.current) setLeftPanelWidth(Math.max(200, Math.min(450, startW + ev.clientX - startX))); };
                    const onUp = () => { isDraggingRef.current = false; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                    document.addEventListener('mousemove', onMove);
                    document.addEventListener('mouseup', onUp);
                  }}
                />

                {/* ── RIGHT PANEL: Selected node details ── */}
                <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
                  {/* Reference images */}
                  {execution.input_data?.reference_images && execution.input_data.reference_images.length > 0 && !activeNodeId && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Reference Images ({execution.input_data.reference_images.length})</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {execution.input_data.reference_images.map((imgUrl: string, index: number) => (
                          <Box key={index} sx={{ width: 80, height: 60, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider', cursor: 'pointer' }} onClick={() => window.open(imgUrl, '_blank')}>
                            <img src={imgUrl} alt={`Ref ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {activeNodeId && activeResult ? (() => {
                    const output = activeOutput;
                    const result = activeResult;
                    const status = result?.status || 'unknown';
                    const isMealPlanChain = (selectedChain?.name || '').toLowerCase().includes('meal plan');
                    const hasPDF = output && (output.pdf_base64 || output.pdf_data || output.pdf_url);
                    const hasVideo = output && (typeof output.video_url === 'string' && output.video_url.startsWith('http'));
                    const videoUrl = hasVideo ? output.video_url : null;
                    const downloadUrl = output?.download_url || videoUrl;
                    const mediaItems = output ? extractMediaItems(output).map(m => ({ ...m, stepName: m.stepName || activeNodeName })) : [];
                    const htmlResultUrl = (() => {
                      if (!output) return null;
                      const candidate = output.html_url || output.url;
                      if (typeof candidate === 'string' && candidate.startsWith('http') && candidate.toLowerCase().includes('.html')) return candidate;
                      return null;
                    })();
                    const canDownloadJSON = isMealPlanChain && activeNodeId === 'node-assemble' && output && typeof output === 'object';

                    return (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700 }}>{activeNodeName}</Typography>
                          <Chip label={status} size="small" color={status === 'completed' ? 'success' : status === 'failed' ? 'error' : status === 'running' ? 'primary' : 'default'} />
                          {result.executionTime && (
                            <Typography variant="caption" color="text.secondary">{formatDuration(result.executionTime)}</Typography>
                          )}
                          {typeof result?.aiCreditsUsed === 'number' && (
                            <Chip label={`${result.aiCreditsUsed.toFixed(2)} credits`} size="small" variant="outlined" />
                          )}
                          {(() => {
                            const modelInfo = describeRunModel(result?.modelUsed, result?.modelRequested, result?.fallbackUsed);
                            if (!modelInfo.label) return null;
                            return (
                              <Tooltip title={modelInfo.tooltip || modelInfo.label} arrow>
                                <Chip label={modelInfo.label} size="small" variant="outlined" />
                              </Tooltip>
                            );
                          })()}
                        </Box>

                        {execution.resume_from_node === activeNodeId && execution.adjustment_notes && (() => {
                          const noteLen = execution.adjustment_notes.length;
                          const isLong = noteLen > 300;
                          return (
                            <Alert severity="info" icon={<NoteAddIcon fontSize="small" />} sx={{ mb: 2, py: 0.5 }} variant="outlined">
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
                                Adjustment Note (from rerun){isLong ? ` · ${(noteLen / 1024).toFixed(1)}KB` : ''}
                              </Typography>
                              <Box sx={{ maxHeight: 120, overflow: 'auto', fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {execution.adjustment_notes}
                              </Box>
                            </Alert>
                          );
                        })()}
                        {execution.resume_from_node === activeNodeId && !execution.adjustment_notes && (
                          <Alert severity="info" sx={{ mb: 2, py: 0 }} variant="outlined">
                            <Typography variant="caption">This step was the starting point of a rerun (no adjustment note provided).</Typography>
                          </Alert>
                        )}

                        {result.error && <Alert severity="error" sx={{ mb: 2 }}>{result.error}</Alert>}

                        {status === 'running' && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2" color="text.secondary">Processing...</Typography>
                          </Box>
                        )}

                        {hasPDF && (
                          <Button variant="contained" size="small" sx={{ mb: 2 }} onClick={() => {
                            if (output.pdf_url && typeof output.pdf_url === 'string' && output.pdf_url.startsWith('http')) { window.open(output.pdf_url, '_blank'); return; }
                            downloadPDF({ [activeNodeId]: result });
                          }}>Download PDF</Button>
                        )}

                        {canDownloadJSON && (
                          <Button variant="outlined" size="small" sx={{ mb: 2, ml: hasPDF ? 1 : 0 }} onClick={() => {
                            const blob = new Blob([JSON.stringify(output, null, 2)], { type: 'application/json' });
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a'); a.href = url; a.download = 'meal_plan.json'; document.body.appendChild(a); a.click();
                            window.URL.revokeObjectURL(url); document.body.removeChild(a);
                          }}>Download JSON</Button>
                        )}

                        {htmlResultUrl && (
                          <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', borderColor: 'primary.main', borderWidth: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: 'primary.50', borderBottom: '1px solid', borderColor: 'primary.100' }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, flexGrow: 1, color: 'primary.main' }}>Generated Result</Typography>
                              <Button size="small" variant="contained" onClick={() => window.open(htmlResultUrl, '_blank')} sx={{ textTransform: 'none' }}>Open in New Tab</Button>
                              <Button size="small" variant="contained" color="secondary" onClick={() => {
                                const htmlContent = output?.html_content || output?.html || result?.input?.html_content || result?.input?.html;
                                if (htmlContent && typeof htmlContent === 'string' && htmlContent.length > 50) {
                                  localStorage.setItem('grapesjs-html-content', htmlContent);
                                } else {
                                  localStorage.setItem('grapesjs-html-content', '');
                                }
                                window.open('/dashboard/html-editor', '_blank');
                              }} sx={{ textTransform: 'none' }}>Edit in Editor</Button>
                              <Button size="small" variant="outlined" onClick={() => navigator.clipboard.writeText(htmlResultUrl)} sx={{ textTransform: 'none' }}>Copy URL</Button>
                            </Box>
                            <Box sx={{ width: '100%', height: 420, bgcolor: 'grey.50' }}>
                              <iframe title="HTML Result Preview" src={htmlResultUrl} style={{ width: '100%', height: '100%', border: 0 }} />
                            </Box>
                          </Paper>
                        )}

                        {(() => {
                          let filtered = mediaItems;
                          if (hasVideo) filtered = filtered.filter((m: MediaItem) => m.kind !== 'video');
                          if (htmlResultUrl) filtered = filtered.filter((m: MediaItem) => m.url !== htmlResultUrl);
                          if (isBatchStatusOutput(output)) {
                            filtered = filtered.filter((m: MediaItem) =>
                              !friendlyDownloadPath(m.label || '') && !friendlyDownloadPath(m.url || ''));
                          }
                          if (filtered.length === 0) return null;
                          return (
                            <MediaGallery
                              items={filtered}
                              getToken={getToken}
                              zipName={selectedChain?.name?.replace(/\s+/g, '_') || 'chain'}
                            />
                          );
                        })()}

                        {hasVideo && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              Final Video
                              {output.duration_seconds && <Chip label={`${Math.round(output.duration_seconds)}s`} size="small" />}
                            </Typography>
                            <Box sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: 'black', mb: 2 }}>
                              <video controls style={{ width: '100%', maxHeight: 400 }} src={videoUrl}>Your browser does not support the video tag.</video>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Button variant="contained" size="small" href={downloadUrl} target="_blank" rel="noopener noreferrer">Download Video</Button>
                              <Button variant="outlined" size="small" onClick={() => { navigator.clipboard.writeText(videoUrl); }}>Copy URL</Button>
                            </Box>
                          </Box>
                        )}

                        {isBatchStatusOutput(output) && (
                          <BatchStatusPanel
                            key={activeNodeId}
                            output={output}
                            nodeInput={result?.input}
                            getToken={getToken}
                            executionId={execution?.id}
                            nodeId={activeNodeId}
                            onUpdate={(fresh) => setExecution((prev: any) => {
                              if (!prev?.node_results?.[activeNodeId]) return prev;
                              const node = { ...prev.node_results[activeNodeId], output: fresh };
                              if (fresh?.token_usage) node.tokenUsage = fresh.token_usage;
                              if (fresh?.model_used || fresh?.model) node.modelUsed = fresh.model_used || fresh.model;
                              if (typeof fresh?.ai_credits_used === 'number') node.aiCreditsUsed = fresh.ai_credits_used;
                              return { ...prev, node_results: { ...prev.node_results, [activeNodeId]: node } };
                            })}
                          />
                        )}

                        {output && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                              {isBatchStatusOutput(output) ? 'Raw output data:' : 'Output Data:'}
                            </Typography>
                            <CopyableCodeBlock content={output} highlight={inRunSearch} />
                          </Box>
                        )}

                        {result?.input && (
                          <Accordion sx={{ mt: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="caption" fontWeight="bold">Input Data</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <CopyableCodeBlock content={result.input} highlight={inRunSearch} />
                            </AccordionDetails>
                          </Accordion>
                        )}

                        <GroundingMetadataPanel metadata={result?.metadata} sx={{ mt: 2 }} />
                      </Box>
                    );
                  })() : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                      <Typography variant="body2">Select a step to view its details</Typography>
                    </Box>
                  )}
                </Box>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', flexShrink: 0 }}>
          {(() => {
            if (!execution?.node_results || execution.status === 'running' || execution.status === 'pending') return <Box />;
            const allMedia: MediaItem[] = [];
            const allMediaSeen = new Set<string>();
            const _gd = (() => { try { return typeof selectedChain?.graph_data === 'string' ? JSON.parse(selectedChain.graph_data) : selectedChain?.graph_data; } catch { return null; } })();
            const _nameMap: Record<string, string> = {};
            if (_gd?.nodes) for (const gn of _gd.nodes) {
              const nm = gn.data?.functionName || gn.data?.label;
              if (nm) _nameMap[gn.id] = nm;
            }
            for (const [nodeId, nr] of Object.entries(execution.node_results) as [string, any][]) {
              if (nr?.output) {
                const sName = _nameMap[nodeId] || nodeId;
                for (const item of extractMediaItems(nr.output)) {
                  const key = (item.url || item.gcsUrl || '').replace(/[?#].*$/, '');
                  if (key && !allMediaSeen.has(key)) { allMediaSeen.add(key); allMedia.push({ ...item, stepName: sName }); }
                }
              }
            }
            const downloadable = allMedia.filter(i => i.url);
            if (downloadable.length === 0) return <Box />;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Button
                  variant="contained" size="small" disabled={dialogZipping}
                  startIcon={dialogZipping ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <DownloadIcon />}
                  onClick={async () => {
                    setDialogZipping(true);
                    await downloadAllMediaAsZip(allMedia, selectedChain?.name?.replace(/\s+/g, '_') || 'chain', getToken).finally(() => setDialogZipping(false));
                  }}
                >
                  {dialogZipping ? `Downloading...` : `Download All (${downloadable.length})`}
                </Button>
              </Box>
            );
          })()}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {(execution?.status === 'running' || execution?.status === 'pending') && (
              <Button variant="outlined" color="warning" size="small" sx={{ textTransform: 'none' }}
                onClick={async () => {
                  if (!execution?.id) return;
                  try {
                    const token = await user?.getIdToken();
                    await fetch(`/api/v2/chains/executions/${execution.id}/cancel`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
                    setExecution((prev: any) => prev ? { ...prev, status: 'cancelled', error: 'Cancelled by user' } : prev);
                    setExecutionPolling(false);
                    pollingExecutionIdRef.current = null;
                    if (pollTimeoutRef.current) { window.clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
                  } catch (e) { console.error('Failed to cancel:', e); }
                }}
              >Cancel Execution</Button>
            )}
            {execution && execution.status !== 'running' && execution.status !== 'pending' && execution.input_data && (
              <>
                <Button variant="outlined" size="small" onClick={() => {
                  const inp = execution.input_data || {};
                  const preloaded: Record<string, any> = {};
                  for (const [k, v] of Object.entries(inp)) { preloaded[k] = v; }
                  setExecutionInputs(preloaded);
                  setUploadedFiles({});
                  if (Array.isArray(inp.attached_image_urls) && inp.attached_image_urls.length > 0) {
                    setShowAttachImages(true);
                    setInputMethods((prev: any) => ({ ...prev, attached_images: 'url' }));
                  }
                  if (Array.isArray(inp.reference_images) && inp.reference_images.length > 0) {
                    setInputMethods((prev: any) => ({ ...prev, reference_images: 'url' }));
                  }
                  setExecution(null);
                  setExecuteDialogOpen(true);
                }}>Re-run with this input</Button>
                {execution.source_execution_id && execution.resume_from_node && (
                  <Tooltip title={execution.adjustment_notes ? `Retries from "${(() => {
                    const gd = (() => { try { return typeof selectedChain?.graph_data === 'string' ? JSON.parse(selectedChain.graph_data) : selectedChain?.graph_data; } catch { return null; } })();
                    const node = gd?.nodes?.find((n: any) => n.id === execution.resume_from_node);
                    return node?.data?.functionName || node?.data?.label || execution.resume_from_node;
                  })()}" with same adjustment note` : 'Retry from same step with same settings'} arrow>
                    <Button
                      variant="outlined" size="small" color="info"
                      startIcon={<ReplayIcon sx={{ fontSize: 14 }} />}
                      onClick={async () => {
                        if (!execution?.source_execution_id || !execution?.resume_from_node) return;
                        try {
                          const token = await user?.getIdToken();
                          const res = await fetch(`/api/v2/chains/executions/${execution.source_execution_id}/rerun-from/${execution.resume_from_node}`, {
                            method: 'POST',
                            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify({ adjustment_notes: execution.adjustment_notes || null }),
                          });
                          if (!res.ok) {
                            const err = await res.json().catch(() => ({}));
                            alert(err.detail || 'Retry failed');
                            return;
                          }
                          const newExec = await res.json();
                          setExecution(newExec);
                          setSelectedResultNode(null);
                          pollExecution(newExec.id);
                        } catch (e) { console.error('Retry failed:', e); }
                      }}
                    >
                      Retry same rerun{execution.adjustment_notes ? ' + note' : ''}
                    </Button>
                  </Tooltip>
                )}
              </>
            )}
            <Button onClick={() => setExecution(null)} disabled={executionPolling}>Close</Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Code Examples Dialog */}
      <Dialog
        open={codeExamplesOpen}
        onClose={() => setCodeExamplesOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CodeIcon />
              <Typography variant="h6">API Integration</Typography>
            </Box>
            <IconButton onClick={() => setCodeExamplesOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {codeCopyAlert && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCodeCopyAlert(false)}>
              Code copied to clipboard!
            </Alert>
          )}
          
          {selectedChain && (() => {
            const baseUrl = window.location.origin;
            const chainId = selectedChain.id;

            // Determine input types from entry nodes
            const entryNodes = nodes.filter(node => !edges.some(edge => edge.target === node.id));
            const inputTypes = new Set<string>();
            entryNodes.forEach(node => {
              (node.data.inputTypes || []).forEach((type: string) => inputTypes.add(type));
            });

            const hasVideo = inputTypes.has('video') || inputTypes.has('mp4');
            const hasAudio = inputTypes.has('audio') || inputTypes.has('mp3');
            const hasImage = inputTypes.has('image');

            // Pull the runtime input schema for this chain (if any). When defined,
            // this drives the dynamic input form preview and the example payload.
            const runtimeInputs: any[] = (selectedChain as any)?.config?.runtime_inputs || [];
            const hasRuntimeInputs = Array.isArray(runtimeInputs) && runtimeInputs.length > 0;

            // Build the example input payload. Prefer values the user has typed
            // into the form preview; fall back to defaults / placeholders so the
            // copied code is always a working starting point.
            const buildPayloadValue = (field: any): any => {
              const k = field?.key;
              const v = codeExampleInputs[k];
              if (v !== undefined && v !== '' && v !== null) {
                if (field.type === 'number') {
                  const n = Number(v);
                  return Number.isFinite(n) ? n : v;
                }
                if (field.type === 'boolean') {
                  return v === true || v === 'true';
                }
                return v;
              }
              if (field.default !== undefined && field.default !== null) return field.default;
              if (field.type === 'boolean') return false;
              if (field.type === 'number') return field.placeholder ? `<${field.placeholder}>` : 0;
              if (field.type === 'select' && Array.isArray(field.options) && field.options.length) {
                return field.options[0]?.value;
              }
              return field.placeholder ? `<${field.placeholder}>` : `<${field.label || field.key}>`;
            };

            let exampleInput: any = {};
            if (hasRuntimeInputs) {
              for (const f of runtimeInputs) {
                if (!f || !f.key) continue;
                exampleInput[f.key] = buildPayloadValue(f);
              }
            } else {
              // Fall back to type-based heuristics for chains without a schema
              if (hasVideo) {
                exampleInput.video_url = "https://example.com/video.mp4";
                exampleInput.video_title = "My Video Title";
              } else if (hasAudio) {
                exampleInput.audio_url = "https://example.com/audio.mp3";
              } else if (hasImage) {
                exampleInput.image_url = "https://example.com/image.jpg";
              } else {
                exampleInput.text = "Your input text here";
              }

              const chainName = selectedChain?.name || '';
              if (chainName.toLowerCase().includes('meal plan')) {
                exampleInput = {
                  days: 7,
                  meals_per_day: 4,
                  goal: "fat loss",
                  age: 34,
                  sex: "female",
                  height_cm: 165,
                  weight_kg: 72,
                  activity_level: "moderately active",
                  diet_style: "high-protein mediterranean",
                  likes: ["salmon", "berries", "greek yogurt"],
                  dislikes: ["mushrooms"],
                  allergies: ["peanuts"],
                  budget: "medium",
                  health_conditions: ["prediabetes"],
                  time_constraints: "30 min max cooking on weekdays",
                };
              }
              if (chainName.includes('Email') || chainName.includes('Document')) {
                exampleInput.creator_name = "Your Name";
                exampleInput.creator_email = "you@example.com";
              }
              if (chainName.includes('Email')) {
                exampleInput.additional_recipients = "recipient1@example.com, recipient2@example.com";
              }
            }

            const uploadFields = hasRuntimeInputs
              ? runtimeInputs.filter((f: any) => f && f.type === 'upload')
              : [];
            const hasFileUpload = uploadFields.length > 0;
            if (hasFileUpload) {
              for (const f of uploadFields) {
                const fileKey = f.file_key || (String(f.key || '').endsWith('_url') ? f.key : null);
                if (fileKey && exampleInput[fileKey] === undefined) {
                  exampleInput[fileKey] = '<https URL from POST /api/v2/temp/upload>';
                }
              }
            }

            const exampleInputStr = JSON.stringify(exampleInput, null, 2);

            // Resolve which API key to embed in code samples. If the user has at
            // least one key, we inline its plaintext value so the snippet is
            // ready to run; otherwise we fall back to the YOUR_API_KEY placeholder.
            const selectedKey = codeExampleApiKeys.find(k => k.id === selectedCodeApiKeyId) || null;
            const inlineApiKey = selectedKey?.api_key || 'YOUR_API_KEY';
            const apiKeyMaskedDisplay = selectedKey
              ? `${selectedKey.api_key.slice(0, 8)}…${selectedKey.api_key.slice(-4)}`
              : 'YOUR_API_KEY';

            const copyToClipboard = (text: string) => {
              navigator.clipboard.writeText(text);
              setCodeCopyAlert(true);
              setTimeout(() => setCodeCopyAlert(false), 3000);
            };
            
            // Comprehensive curl example with all options
            const curlExample = `# =============================================================================
# Chain: ${selectedChain?.name || 'Your Chain'}
# =============================================================================
# This example shows ALL available options. Remove what you don't need.
# =============================================================================

curl -X POST "${baseUrl}/api/v2/chains/${chainId}/execute" \\
  -H "Authorization: Bearer ${inlineApiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
  "input_data": ${exampleInputStr.split('\n').map((line, i) => i === 0 ? line : '  ' + line).join('\n')},
  
  "node_overrides": {
    "node-id-here": {
      "modelOverride": "gpt-4o",
      "temperature": 0.7,
      "maxTokens": 4096,
      "knowledgeCategories": ["category-uuid-1", "category-uuid-2"],
      "knowledgeChunks": 3
    }
  },
  
  "metadata": {
    "source": "api",
    "custom_field": "any custom data you want to track"
  }
}'

# =============================================================================
# INPUT FIELDS EXPLAINED:
# =============================================================================
# input_data:           Main input for the chain (text, URLs, etc.)
#                       Pasteable text (csv/json/txt) goes in the text field as a string.
#                       PDF / image / Excel: POST /api/v2/temp/upload (multipart "file"),
#                       then put public_url in input_data.<file_key> (often source_file_url).
#                       Do not base64 the file into execute.
# node_overrides:       Per-node AI settings (keyed by node ID from chain graph)
#                       - modelOverride: Use a different model for this node
#                       - temperature: Control creativity (0-2, default varies)
#                       - maxTokens: Limit response length
#                       - knowledgeCategories: RAG category IDs for context
#                       - knowledgeChunks: Number of chunks to retrieve (1-10)
# metadata:             Your custom tracking data (not sent to AI)
# =============================================================================
${hasFileUpload ? `
# FILE UPLOAD (do this first for PDF / image / Excel):
# curl -X POST "${baseUrl}/api/v2/temp/upload" \\
#   -H "Authorization: Bearer ${inlineApiKey}" \\
#   -F "file=@/path/to/file.png"
# Put the response public_url into input_data["${uploadFields[0]?.file_key || 'source_file_url'}"].
# Pasteable text (csv/json/txt) can skip this and go in the text field as a string.
` : ''}`;

            const pythonExample = `"""
Chain: ${selectedChain?.name || 'Your Chain'}
${selectedChain?.description || ''}

This example shows ALL available options. Remove what you don't need.
"""
import requests
import time

API_KEY = "${inlineApiKey}"
BASE_URL = "${baseUrl}"
CHAIN_ID = "${chainId}"

# =============================================================================
# INPUT DATA - Your main input for the chain
# =============================================================================
input_data = ${exampleInputStr}

# =============================================================================
# NODE OVERRIDES (OPTIONAL)
# Override AI settings for specific nodes in the chain
# Get node IDs from the chain graph or UI
# =============================================================================
node_overrides = {
    # "node-id-from-chain": {
    #     "modelOverride": "gpt-4o",       # Use different model
    #     "temperature": 0.7,              # Creativity (0-2)
    #     "maxTokens": 4096,               # Response length limit
    #     "knowledgeCategories": ["id1"],  # RAG category IDs
    #     "knowledgeChunks": 3,            # Chunks to retrieve (1-10)
    # }
}

# =============================================================================
# METADATA (OPTIONAL) - Your custom tracking data (not sent to AI)
# =============================================================================
metadata = {
    "source": "python_script",
    "user_id": "your-user-id",
    "any_custom_field": "any value"
}

${hasFileUpload ? `# =============================================================================
# FILE INPUTS (PDF / image / Excel) — optional
# Pasteable text (csv, json, txt) can skip this and live in input_data as a string.
# =============================================================================
# up = requests.post(
#     f"{BASE_URL}/api/v2/temp/upload",
#     headers={"Authorization": f"Bearer {API_KEY}"},
#     files={"file": open("/path/to/file.png", "rb")},
# )
# up.raise_for_status()
# input_data["${uploadFields[0]?.file_key || 'source_file_url'}"] = up.json()["public_url"]
` : ''}# Execute the chain
response = requests.post(
    f"{BASE_URL}/api/v2/chains/{CHAIN_ID}/execute",
    headers={
        "Authorization": f"Bearer {API_KEY}",
        "Content-Type": "application/json"
    },
    json={
        "input_data": input_data,
        "node_overrides": node_overrides,  # Remove if not needed
        "metadata": metadata
    }
)

execution = response.json()
execution_id = execution["id"]
print(f"✓ Execution started: {execution_id}")

# Poll for results
while True:
    status_response = requests.get(
        f"{BASE_URL}/api/v2/chains/executions/{execution_id}",
        headers={"Authorization": f"Bearer {API_KEY}"}
    )
    result = status_response.json()
    
    if result["status"] in ["completed", "failed"]:
        print(f"\\n{'✓' if result['status'] == 'completed' else '✗'} Status: {result['status']}")
        
        # Print outputs from each node
        if result.get("node_results"):
            for node_id, node_result in result["node_results"].items():
                print(f"\\n--- {node_id} ---")
                if node_result.get("output"):
                    print(node_result["output"])
        break
    
    print(f"⏳ Status: {result['status']}...")
    time.sleep(2)`;

            const jsExample = `/**
 * Chain: ${selectedChain?.name || 'Your Chain'}
 * ${selectedChain?.description || ''}
 * 
 * This example shows ALL available options. Remove what you don't need.
 */

const API_KEY = "${inlineApiKey}";
const BASE_URL = "${baseUrl}";
const CHAIN_ID = "${chainId}";

// =============================================================================
// INPUT DATA - Your main input for the chain
// =============================================================================
const inputData = ${exampleInputStr};

// =============================================================================
// NODE OVERRIDES (OPTIONAL)
// Override AI settings for specific nodes in the chain
// Get node IDs from the chain graph or UI
// =============================================================================
const nodeOverrides = {
  // "node-id-from-chain": {
  //   modelOverride: "gpt-4o",       // Use different model
  //   temperature: 0.7,              // Creativity (0-2)
  //   maxTokens: 4096,               // Response length limit  
  //   knowledgeCategories: ["id1"],  // RAG category IDs
  //   knowledgeChunks: 3,            // Chunks to retrieve (1-10)
  // }
};

// =============================================================================
// METADATA (OPTIONAL) - Your custom tracking data (not sent to AI)
// =============================================================================
const metadata = {
  source: "javascript",
  customField: "any value you want to track"
};

${hasFileUpload ? `// FILE INPUTS (PDF / image / Excel): upload first, then pass the URL.
// Pasteable text (csv/json/txt) can skip this and go in inputData as a string.
// const form = new FormData();
// form.append("file", fileBlob, "label.png");
// const up = await fetch(\`\${BASE_URL}/api/v2/temp/upload\`, {
//   method: "POST",
//   headers: { Authorization: \`Bearer \${API_KEY}\` },
//   body: form,
// });
// const uploaded = await up.json();
// inputData["${uploadFields[0]?.file_key || 'source_file_url'}"] = uploaded.public_url;
` : ''}// Execute the chain
const response = await fetch(\`\${BASE_URL}/api/v2/chains/\${CHAIN_ID}/execute\`, {
  method: "POST",
  headers: {
    "Authorization": \`Bearer \${API_KEY}\`,
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    input_data: inputData,
    node_overrides: nodeOverrides,  // Remove if not needed
    metadata: metadata
  })
});

const execution = await response.json();
console.log("✓ Execution started:", execution.id);

// Poll for results
const pollExecution = async (executionId) => {
  while (true) {
    const statusResponse = await fetch(
      \`\${BASE_URL}/api/v2/chains/executions/\${executionId}\`,
      { headers: { "Authorization": \`Bearer \${API_KEY}\` } }
    );
    const result = await statusResponse.json();
    
    if (result.status === "completed" || result.status === "failed") {
      console.log(result.status === "completed" ? "✓" : "✗", "Status:", result.status);
      
      // Print outputs from each node
      if (result.node_results) {
        for (const [nodeId, nodeResult] of Object.entries(result.node_results)) {
          console.log(\`\\n--- \${nodeId} ---\`);
          console.log(nodeResult.output);
        }
      }
      return result;
    }
    
    console.log("⏳ Status:", result.status, "...");
    await new Promise(resolve => setTimeout(resolve, 2000));
  }
};

const result = await pollExecution(execution.id);`;

            const nodeExample = `/**
 * Chain: ${selectedChain?.name || 'Your Chain'}
 * ${selectedChain?.description || ''}
 * 
 * This example shows ALL available options. Remove what you don't need.
 */
const axios = require('axios');

const API_KEY = '${inlineApiKey}';
const BASE_URL = '${baseUrl}';
const CHAIN_ID = '${chainId}';

// =============================================================================
// INPUT DATA - Your main input for the chain
// =============================================================================
const inputData = ${exampleInputStr};

// =============================================================================
// NODE OVERRIDES (OPTIONAL)
// Override AI settings for specific nodes in the chain
// Get node IDs from the chain graph or UI
// =============================================================================
const nodeOverrides = {
  // 'node-id-from-chain': {
  //   modelOverride: 'gpt-4o',       // Use different model
  //   temperature: 0.7,              // Creativity (0-2)
  //   maxTokens: 4096,               // Response length limit
  //   knowledgeCategories: ['id1'],  // RAG category IDs
  //   knowledgeChunks: 3,            // Chunks to retrieve (1-10)
  // }
};

// =============================================================================
// METADATA (OPTIONAL) - Your custom tracking data (not sent to AI)
// =============================================================================
const metadata = {
  source: 'nodejs',
  customField: 'any value you want to track'
};

${hasFileUpload ? `// FILE INPUTS (PDF / image / Excel):
// const FormData = require('form-data');
// const fs = require('fs');
// const form = new FormData();
// form.append('file', fs.createReadStream('/path/to/file.png'));
// const up = await axios.post(\`\${BASE_URL}/api/v2/temp/upload\`, form, {
//   headers: { Authorization: \`Bearer \${API_KEY}\`, ...form.getHeaders() },
// });
// inputData['${uploadFields[0]?.file_key || 'source_file_url'}'] = up.data.public_url;
` : ''}async function executeChain() {
  // Start chain execution
  const { data: execution } = await axios.post(
    \`\${BASE_URL}/api/v2/chains/\${CHAIN_ID}/execute\`,
    {
      input_data: inputData,
      node_overrides: nodeOverrides,  // Remove if not needed
      metadata: metadata
    },
    {
      headers: {
        'Authorization': \`Bearer \${API_KEY}\`,
        'Content-Type': 'application/json'
      }
    }
  );
  
  console.log('✓ Execution started:', execution.id);
  
  // Poll for results
  while (true) {
    const { data: result } = await axios.get(
      \`\${BASE_URL}/api/v2/chains/executions/\${execution.id}\`,
      { headers: { 'Authorization': \`Bearer \${API_KEY}\` } }
    );
    
    if (result.status === 'completed' || result.status === 'failed') {
      console.log(result.status === 'completed' ? '✓' : '✗', 'Status:', result.status);
      
      // Print outputs from each node
      if (result.node_results) {
        for (const [nodeId, nodeResult] of Object.entries(result.node_results)) {
          console.log(\`\\n--- \${nodeId} ---\`);
          console.log(JSON.stringify(nodeResult.output, null, 2));
        }
      }
      return result;
    }
    
    console.log('⏳ Status:', result.status, '...');
    await new Promise(r => setTimeout(r, 2000));
  }
}

executeChain().catch(console.error);`;

            const codeExamples = [
              { label: 'cURL', code: curlExample, language: 'bash' },
              { label: 'Python', code: pythonExample, language: 'python' },
              { label: 'JavaScript', code: jsExample, language: 'javascript' },
              { label: 'Node.js', code: nodeExample, language: 'javascript' }
            ];

            // Standalone "track an existing execution" snippets — handy for
            // resuming polling from another process / dashboard / cron job.
            const trackExecId = (codeExampleTrackId && codeExampleTrackId.trim())
              || 'EXECUTION_ID_FROM_LAUNCH_RESPONSE';

            const trackCurl = `# Get the latest status + node_results for an execution
curl -X GET "${baseUrl}/api/v2/chains/executions/${trackExecId}" \\
  -H "Authorization: Bearer ${inlineApiKey}"

# (Optional) Cancel a running execution
curl -X POST "${baseUrl}/api/v2/chains/executions/${trackExecId}/cancel" \\
  -H "Authorization: Bearer ${inlineApiKey}"`;

            const trackPython = `import requests, time

API_KEY = "${inlineApiKey}"
BASE_URL = "${baseUrl}"
EXECUTION_ID = "${trackExecId}"

def get_status():
    r = requests.get(
        f"{BASE_URL}/api/v2/chains/executions/{EXECUTION_ID}",
        headers={"Authorization": f"Bearer {API_KEY}"},
    )
    r.raise_for_status()
    return r.json()

# One-shot status check
print(get_status()["status"])

# Or poll until done. Status values: pending | running | completed | failed | cancelled
while True:
    result = get_status()
    completed_nodes = sum(
        1 for n in (result.get("node_results") or {}).values()
        if n.get("status") in ("completed", "failed", "skipped")
    )
    total_nodes = len(result.get("node_results") or {})
    print(f"status={result['status']} progress={completed_nodes}/{total_nodes}")
    if result["status"] in ("completed", "failed", "cancelled"):
        break
    time.sleep(2)

print("final status:", result["status"])
for node_id, node in (result.get("node_results") or {}).items():
    print(node_id, "->", node.get("status"))`;

            const trackJs = `const API_KEY = '${inlineApiKey}';
const BASE_URL = '${baseUrl}';
const EXECUTION_ID = '${trackExecId}';

async function getStatus() {
  const res = await fetch(\`\${BASE_URL}/api/v2/chains/executions/\${EXECUTION_ID}\`, {
    headers: { Authorization: \`Bearer \${API_KEY}\` },
  });
  if (!res.ok) throw new Error(\`Status \${res.status}\`);
  return res.json();
}

async function pollUntilDone() {
  // Status values: pending | running | completed | failed | cancelled
  while (true) {
    const result = await getStatus();
    const nodes = result.node_results || {};
    const done = Object.values(nodes).filter(
      (n) => ['completed', 'failed', 'skipped'].includes(n.status)
    ).length;
    console.log(\`status=\${result.status} progress=\${done}/\${Object.keys(nodes).length}\`);
    if (['completed', 'failed', 'cancelled'].includes(result.status)) return result;
    await new Promise(r => setTimeout(r, 2000));
  }
}

// One-shot status check
getStatus().then(s => console.log('status:', s.status));

// ...or poll until the chain finishes
pollUntilDone().then(final => {
  console.log('final status:', final.status);
  for (const [id, node] of Object.entries(final.node_results || {})) {
    console.log(id, '->', node.status);
  }
});`;

            const statusExamples = [
              { label: 'cURL', code: trackCurl, language: 'bash' },
              { label: 'Python', code: trackPython, language: 'python' },
              { label: 'JavaScript', code: trackJs, language: 'javascript' },
            ];
            
            const renderCodeBlock = (code: string, maxHeight: number = 400) => (
              <Box sx={{ position: 'relative' }}>
                <IconButton
                  onClick={() => copyToClipboard(code)}
                  sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1, color: '#d4d4d4' }}
                  size="small"
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: '#1e1e1e',
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight,
                  }}
                >
                  <Typography
                    component="pre"
                    sx={{
                      color: '#d4d4d4',
                      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      fontSize: '0.8rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      m: 0,
                    }}
                  >
                    {code}
                  </Typography>
                </Box>
              </Box>
            );

            return (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    Chain: {selectedChain.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="caption">Chain ID:</Typography>
                    <code style={{ fontSize: '0.75rem' }}>{chainId}</code>
                    <Tooltip title="Copy chain ID">
                      <IconButton size="small" onClick={() => copyToClipboard(String(chainId))} sx={{ p: 0.25 }}>
                        <CopyIcon sx={{ fontSize: '0.85rem' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography variant="caption" display="block" sx={{ mt: 1, borderTop: '1px solid rgba(0,0,0,0.1)', pt: 1 }}>
                    <strong>Available Request Fields:</strong>
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ pl: 1 }}>
                    • <strong>input_data</strong>: Your main input (text, URLs, etc.)<br/>
                    • <strong>node_overrides</strong>: Per-node model / temperature / RAG overrides<br/>
                    • <strong>additional_messages</strong>: Extra context added to AI prompts<br/>
                    &nbsp;&nbsp;↳ Same as the "Additional Input" field in the card UI<br/>
                    • <strong>metadata</strong>: Your custom tracking data (not sent to AI)
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    Expected input types: {Array.from(inputTypes).join(', ') || 'json/text'}
                  </Typography>
                </Alert>

                {/* API Key selector — auto-load the user's keys and inject the
                    selected one directly into the code samples below. */}
                <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2">🔑 API Key</Typography>
                    {codeExampleApiKeysLoading && <CircularProgress size={14} />}
                  </Box>
                  {codeExampleApiKeysError && (
                    <Alert severity="error" sx={{ mb: 1 }}>{codeExampleApiKeysError}</Alert>
                  )}
                  {!codeExampleApiKeysLoading && codeExampleApiKeys.length === 0 && (
                    <Alert severity="warning" icon={false} sx={{ py: 0.5 }}>
                      <Typography variant="caption">
                        No API keys yet. Samples below use <code>YOUR_API_KEY</code> as a placeholder.&nbsp;
                        <Link href="/dashboard/api-keys" target="_blank" rel="noopener">
                          Create one in Settings → API Keys
                        </Link>.
                      </Typography>
                    </Alert>
                  )}
                  {codeExampleApiKeys.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <FormControl size="small" sx={{ minWidth: 240, flex: 1 }}>
                        <InputLabel id="code-api-key-label">Use this key in samples</InputLabel>
                        <Select
                          labelId="code-api-key-label"
                          label="Use this key in samples"
                          value={selectedCodeApiKeyId ?? ''}
                          onChange={(e) => setSelectedCodeApiKeyId(Number(e.target.value))}
                        >
                          {codeExampleApiKeys.map((k) => (
                            <MenuItem key={k.id} value={k.id}>
                              {(k.name || `Key #${k.id}`) + (k.active === false ? ' (inactive)' : '')}
                              &nbsp;—&nbsp;
                              <Typography component="span" variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                                {k.api_key.slice(0, 8)}…{k.api_key.slice(-4)}
                              </Typography>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        <Typography variant="caption" color="text.secondary">In samples:</Typography>
                        <code>{revealCodeApiKey ? inlineApiKey : apiKeyMaskedDisplay}</code>
                        <Tooltip title={revealCodeApiKey ? 'Hide key' : 'Reveal key'}>
                          <IconButton size="small" onClick={() => setRevealCodeApiKey(v => !v)}>
                            <CodeIcon sx={{ fontSize: '0.95rem' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy key">
                          <IconButton size="small" onClick={() => copyToClipboard(inlineApiKey)}>
                            <CopyIcon sx={{ fontSize: '0.95rem' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    The selected key is inlined into every snippet below so you can copy-paste and run immediately.
                  </Typography>
                </Box>

                {/* Runtime Inputs form preview — surfaces the same dynamic fields
                    the run dialog shows, so the code sample reflects them. */}
                {hasRuntimeInputs && (
                  <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      📝 Inputs preview
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        — values flow into the <code>input_data</code> JSON in every sample
                      </Typography>
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 1.25 }}>
                      {runtimeInputs.map((field: any) => {
                        if (!field || !field.key) return null;
                        const fieldType = field.type || 'text';
                        const isRequired = field.required === true;
                        const label = `${field.label || field.key}${isRequired ? ' *' : ''}`;
                        const value = codeExampleInputs[field.key];

                        if (fieldType === 'boolean') {
                          const checked = value === true || value === 'true';
                          return (
                            <FormControlLabel
                              key={field.key}
                              control={
                                <Switch
                                  size="small"
                                  checked={checked}
                                  onChange={(e) => setCodeExampleInputs(prev => ({ ...prev, [field.key]: e.target.checked }))}
                                />
                              }
                              label={<Typography variant="body2">{label}</Typography>}
                            />
                          );
                        }

                        if (fieldType === 'select' && Array.isArray(field.options)) {
                          return (
                            <FormControl key={field.key} size="small" fullWidth>
                              <InputLabel>{label}</InputLabel>
                              <Select
                                label={label}
                                value={value ?? (field.default || field.options[0]?.value || '')}
                                onChange={(e) => setCodeExampleInputs(prev => ({ ...prev, [field.key]: e.target.value }))}
                              >
                                {field.options.map((opt: any) => (
                                  <MenuItem key={opt.value} value={opt.value}>{opt.label || opt.value}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          );
                        }

                        if (fieldType === 'textarea') {
                          return (
                            <TextField
                              key={field.key}
                              size="small"
                              fullWidth
                              multiline
                              minRows={2}
                              maxRows={4}
                              label={label}
                              placeholder={field.placeholder || ''}
                              value={value || ''}
                              onChange={(e) => setCodeExampleInputs(prev => ({ ...prev, [field.key]: e.target.value }))}
                            />
                          );
                        }

                        return (
                          <TextField
                            key={field.key}
                            size="small"
                            fullWidth
                            type={fieldType === 'number' ? 'number' : 'text'}
                            label={label}
                            placeholder={field.placeholder || ''}
                            value={value ?? ''}
                            onChange={(e) => setCodeExampleInputs(prev => ({ ...prev, [field.key]: e.target.value }))}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  📡 API Endpoint
                </Typography>
                <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1, mb: 2, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  <code>POST {baseUrl}/api/v2/chains/{chainId}/execute</code>
                </Box>

                <Tabs
                  value={codeTabValue}
                  onChange={(e, v) => setCodeTabValue(v)}
                  sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                >
                  {codeExamples.map((ex) => (
                    <Tab key={ex.label} label={ex.label} />
                  ))}
                </Tabs>

                {codeExamples.map((example, index) => (
                  <Box key={example.label} hidden={codeTabValue !== index}>
                    {renderCodeBlock(example.code)}
                  </Box>
                ))}

                <Divider sx={{ my: 3 }} />

                {/* Status Tracking section — explicit, always-visible block of
                    snippets that show how to query an execution that's already
                    been launched. Useful for resumable workers / dashboards. */}
                <Typography variant="subtitle2" gutterBottom>
                  ⏱️ Track an Execution (Status & Polling)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Launching a chain returns immediately with an <code>id</code> and{' '}
                  <code>status: "pending"</code>. Use the snippets below to fetch the execution
                  status, watch progress per node, and read the final outputs.
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    label="Execution ID"
                    placeholder="Paste execution.id from launch response"
                    value={codeExampleTrackId}
                    onChange={(e) => setCodeExampleTrackId(e.target.value)}
                    sx={{ flex: 1, minWidth: 280 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Optional — leave blank to keep <code>EXECUTION_ID_FROM_LAUNCH_RESPONSE</code> in samples.
                  </Typography>
                </Box>

                <Box sx={{ p: 1.25, bgcolor: 'grey.100', borderRadius: 1, mb: 1.5, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  <code>GET {baseUrl}/api/v2/chains/executions/{trackExecId}</code>
                </Box>

                <Tabs
                  value={codeStatusTabValue}
                  onChange={(e, v) => setCodeStatusTabValue(v)}
                  sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                >
                  {statusExamples.map((ex) => (
                    <Tab key={ex.label} label={ex.label} />
                  ))}
                </Tabs>

                {statusExamples.map((example, index) => (
                  <Box key={example.label} hidden={codeStatusTabValue !== index}>
                    {renderCodeBlock(example.code, 320)}
                  </Box>
                ))}

                <Box sx={{ mt: 1.5, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="caption" component="div">
                    <strong>Status values:</strong>{' '}
                    <Chip size="small" label="pending" sx={{ mr: 0.5 }} />
                    <Chip size="small" label="running" color="info" sx={{ mr: 0.5 }} />
                    <Chip size="small" label="completed" color="success" sx={{ mr: 0.5 }} />
                    <Chip size="small" label="failed" color="error" sx={{ mr: 0.5 }} />
                    <Chip size="small" label="cancelled" />
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Poll roughly every 2–5 seconds. Each <code>node_results[node-id]</code> entry has
                    its own <code>status</code> and <code>output</code> so you can render live progress.
                  </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle2" gutterBottom>
                  📋 Response Format
                </Typography>
                <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
{`{
  "id": "execution-uuid",
  "chain_id": "${chainId}",
  "status": "completed",  // pending | running | completed | failed | cancelled
  "node_results": {
    "node-1": {
      "status": "completed",  // pending | running | completed | failed | skipped
      "output": { ... },
      "executionTime": 1234
    },
    "node-2": { ... }
  },
  "execution_time_ms": 5432,
  "total_tokens": 1500,
  "total_cost": 0.0045,
  "started_at": "2024-01-01T12:00:00Z",
  "completed_at": "2024-01-01T12:00:05Z"
}`}
                  </pre>
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    Snippets above use the <strong>{selectedKey ? (selectedKey.name || `Key #${selectedKey.id}`) : 'YOUR_API_KEY placeholder'}</strong>.
                    Switch keys at the top of this dialog to regenerate every sample with a different key.
                  </Typography>
                </Alert>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCodeExamplesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {selectedChain && (
        <ChainVersionHistory
          open={versionHistoryOpen}
          chainId={selectedChain.id}
          onClose={() => setVersionHistoryOpen(false)}
          onRestored={async () => {
            try {
              await reloadChainFromServer(selectedChain.id);
            } catch (e: any) {
              setError(e.message);
            }
          }}
        />
      )}

      {selectedChain && reliabilityOpen && (
        <ReliabilityDialog
          open
          onClose={() => setReliabilityOpen(false)}
          targetType="chain"
          targetId={selectedChain.id}
          targetName={selectedChain.name}
          onSaved={setChainReliability}
        />
      )}

      {/* AI Chain Builder Wizard */}
      <ChainWizard
        open={chainWizardOpen}
        onClose={() => { setChainWizardOpen(false); setWizardEditPrompt(undefined); setWizardEditTemplateSlug(undefined); setWizardEditChainId(undefined); setWizardEditChainName(undefined); setWizardEditChainDesc(undefined); setWizardEditPlan(undefined); setWizardSeedPrompt(undefined); }}
        initialPrompt={wizardEditPrompt}
        seedPrompt={wizardSeedPrompt}
        templateSlug={wizardEditTemplateSlug}
        existingChainId={wizardEditChainId}
        existingChainName={wizardEditChainName}
        existingChainDescription={wizardEditChainDesc}
        existingPlan={wizardEditPlan}
        onChainCreated={async (chainId) => {
          setChainWizardOpen(false); setWizardEditPrompt(undefined); setWizardEditTemplateSlug(undefined); setWizardEditChainId(undefined); setWizardEditChainName(undefined); setWizardEditChainDesc(undefined); setWizardEditPlan(undefined); setWizardSeedPrompt(undefined);
          try {
            const token = await user?.getIdToken();
            const authHeaders = { Authorization: `Bearer ${token}` };

            const [chainRes, funcsRes, pluginsRes] = await Promise.all([
              fetch(`/api/v2/chains/${chainId}`, { headers: authHeaders }),
              fetch('/api/v2/chains/functions/schemas', { headers: authHeaders }),
              fetch('/api/v2/plugins', { headers: authHeaders }),
            ]);

            loadFunctions();
            loadChains();

            if (chainRes.ok) {
              const chain = await chainRes.json();
              let freshFuncs = funcsRes.ok ? await funcsRes.json() : [];
              if (pluginsRes.ok) {
                const pluginsData = await pluginsRes.json();
                const pluginFuncs = (pluginsData.plugins || []).map((p: any) => ({
                  ...p, id: `plugin:${p.name}`, name: p.name,
                  display_name: p.display_name || p.name,
                  description: p.description, is_plugin: true,
                }));
                freshFuncs = [...freshFuncs, ...pluginFuncs];
              }

              // Detect any chain node functionIds missing from the schemas list
              // (can happen due to DB read-replica lag or dedup in the schemas endpoint)
              const graphData = typeof chain.graph_data === 'string'
                ? JSON.parse(chain.graph_data) : chain.graph_data;
              const knownIds = new Set(freshFuncs.map((f: any) => f.id));
              const missingIds = (graphData?.nodes || [])
                .map((n: any) => n.functionId)
                .filter((id: string) => id && !id.startsWith('plugin:') && !knownIds.has(id));

              if (missingIds.length > 0) {
                const uniqueMissing = Array.from(new Set<string>(missingIds));
                const fetched = await Promise.all(
                  uniqueMissing.map(async (fid: string) => {
                    try {
                      const r = await fetch(`/api/v2/ai-functions/${fid}`, { headers: authHeaders });
                      if (r.ok) {
                        const d = await r.json();
                        const fn = d.data || d;
                        return { id: String(fn.id), name: fn.name, display_name: fn.display_name || fn.name,
                          description: fn.description, input_types: fn.input_types, response_format: fn.response_format,
                          allowed_models: fn.allowed_models || [], customer_id: fn.customer_id,
                          is_public: fn.customer_id === 'public', version: fn.version };
                      }
                    } catch { /* skip */ }
                    return null;
                  })
                );
                const resolved = fetched.filter(Boolean);
                if (resolved.length > 0) freshFuncs = [...freshFuncs, ...resolved];
              }

              loadChain(chain, freshFuncs);
            }
          } catch (e) {
            console.error('Failed to load new chain:', e);
          }
        }}
      />
    </Box>
  );
};

export default FunctionChaining;
